import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { seriesUpdatedSinceLastWatch } from '../utils/continueWatching';
import { apiClient, AuthUser, ChannelStreamProfile } from '../lib/apiClient';
import { getIDBItem, setIDBItem } from '../utils/indexedDB';
import {
  Channel,
  Movie,
  Series,
  Episode,
  EPGProgram,
  UserProfile,
  PlaylistSource,
  ParentalControlConfig,
  AppSettings,
  ContentType,
  CategoryConfig,
  AllCategoriesConfig,
  ServerDNS,
  ChannelResolution,
  ChannelQualityVariant,
  ActiveScreen,
} from '../types/iptv';
import { groupChannelsByQuality, extractChannelQualityInfo, expandChannelsToRaw, RESOLUTION_HIERARCHY } from '../utils/channelQualityUtils';
import {
  INITIAL_CHANNELS,
  INITIAL_MOVIES,
  INITIAL_SERIES,
  INITIAL_PROFILES,
  INITIAL_PLAYLISTS,
  INITIAL_PARENTAL_CONFIG,
  INITIAL_SETTINGS,
  SYSTEM_DEFAULT_CHANNELS,
  generateLiveEPG,
  DEFAULT_CANONICAL_LIVE_CATEGORIES,
  DEFAULT_CANONICAL_MOVIE_CATEGORIES,
  DEFAULT_CANONICAL_SERIES_CATEGORIES,
} from '../data/mockChannels';
import { isContentRestricted } from '../utils/security';
import { parseM3U, testAndFetchXtream, testAndFetchXtreamWithFallback, fetchXtreamSeriesInfo } from '../utils/m3uParser';
import { getListServer, getServerOfList, setCurrentListServer, getListAccount, ListAccount } from '../utils/managerDns';
import { computeCatalogDiff, findFreshMovieForExisting, type CatalogDiff } from '../utils/catalogDiff';
import { resolveCorrectChannelLogo } from '../utils/channelLogos';
import { formatSeconds, formatDurationHuman } from '../utils/timeFormat';
import { applyProfileTheme } from '../utils/themeColors';
import { fetchChannelEPG, fetchBatchEPG } from '../utils/epgService';
import { enableScreenWakeLock, disableScreenWakeLock } from '../utils/screenWakeLock';

interface Toast {
  id: string;
  message: string;
  type: 'info' | 'success' | 'warning' | 'error';
}

interface IPTVContextType {
  // Navigation & View
  activeTab: 'home' | 'live' | 'movies' | 'series' | 'epg' | 'favorites' | 'multi' | 'settings';
  setActiveTab: (tab: 'home' | 'live' | 'movies' | 'series' | 'epg' | 'favorites' | 'multi' | 'settings') => void;
  searchQuery: string;
  setSearchQuery: (query: string) => void;

  // Active Category per section (null = Show Categories Hub / Grid, string = Active Category items)
  activeChannelCategory: string | null;
  setActiveChannelCategory: (cat: string | null) => void;
  activeMovieCategory: string | null;
  setActiveMovieCategory: (cat: string | null) => void;
  activeSeriesCategory: string | null;
  setActiveSeriesCategory: (cat: string | null) => void;
  activeSeriesDetail: Series | null;
  setActiveSeriesDetail: (series: Series | null) => void;

  // Active Playback
  currentChannel: Channel | null;
  setCurrentChannel: (channel: Channel | null) => void;
  playChannel: (channel: Channel) => void;
  switchChannelQuality: (quality: ChannelResolution, targetStreamUrl?: string) => void;
  currentMovie: Movie | null;
  setCurrentMovie: (movie: Movie | null, startFromBeginning?: boolean) => void;
  playMovie: (movie: Movie, startFromBeginning?: boolean) => void;
  currentSeries: Series | null;
  currentEpisode: Episode | null;
  playEpisode: (series: Series, episode: Episode, startFromBeginning?: boolean) => void;
  playSeriesEpisode: (series: Series, episode: Episode, startFromBeginning?: boolean) => void;
  initialSeekSeconds: number | null;
  setInitialSeekSeconds: (seconds: number | null) => void;
  getContentProgress: (contentId: string) => {
    watchedSeconds: number;
    totalSeconds: number;
    percentage: number;
    isCompleted: boolean;
    updatedAt: string;
    formattedProgress: string;
    formattedTotal: string;
    formattedRemaining: string;
  } | null;
  getSeriesProgress: (series: Series) => {
    lastWatchedEpisode: Episode | null;
    watchedSeconds: number;
    totalSeconds: number;
    percentage: number;
    totalWatchedEpisodes: number;
    isCompleted: boolean;
    updatedAt: string;
    formattedProgress: string;
  } | null;
  clearContentProgress: (contentId: string, seriesTitle?: string, episodeIds?: string[]) => void;
  isContentWatched: (contentId: string) => boolean;
  toggleWatched: (contentId: string | string[], isSeries?: boolean, seriesEpisodes?: Episode[], forceState?: boolean) => void;
  stopPlayback: () => void;
  isPlaying: boolean;
  setIsPlaying: (playing: boolean) => void;

  // Content Data
  channels: Channel[];
  rawChannels: Channel[];
  systemChannels: Channel[];
  toggleMergeChannel: (baseNameOrKey: string) => void;
  setMergeQualitiesGlobal: (enabled: boolean) => void;
  excludeQualityFromMerge: (baseName: string, resolution: ChannelResolution) => void;
  restoreQualityToMerge: (baseName: string, resolution: ChannelResolution) => void;
  excludeChannelFromMerge: (channelId: string, channelName?: string) => void;
  restoreChannelToMerge: (channelId: string) => void;
  deleteChannelMerge: (baseKeyOrName: string) => void;
  restoreDeletedMerge: (baseKeyOrName: string) => void;
  mergeSelectedChannelsManually: (channelIds: string[], customName?: string) => void;
  addSystemChannel: (channel: Omit<Channel, 'id' | 'num'>) => Channel;
  removeSystemChannel: (id: string) => void;
  clearSystemChannels: () => void;
  movies: Movie[];
  series: Series[];
  loadSeriesEpisodes: (seriesId: string) => Promise<Series | null>;
  epgData: Record<string, EPGProgram[]>;
  loadChannelEPG: (channel: Channel, forceRefresh?: boolean) => Promise<EPGProgram[]>;
  loadBatchEPG: (channels: Channel[], forceRefresh?: boolean) => Promise<void>;
  isEpgLoading: boolean;
  channelCategories: string[];
  movieCategories: string[];
  seriesCategories: string[];
  rawChannelCategories: string[];
  rawMovieCategories: string[];
  rawSeriesCategories: string[];

  // Category Configuration & Editor
  categoryConfig: AllCategoriesConfig;
  toggleHideCategory: (type: 'live' | 'movies' | 'series', categoryName: string) => void;
  renameCategory: (type: 'live' | 'movies' | 'series', oldName: string, newName: string) => void;
  reorderCategory: (type: 'live' | 'movies' | 'series', categoryName: string, direction: 'up' | 'down') => void;
  setCategoryPosition: (type: 'live' | 'movies' | 'series', categoryName: string, targetIndex: number) => void;
  moveCategoryToTop: (type: 'live' | 'movies' | 'series', categoryName: string) => void;
  moveCategoryToBottom: (type: 'live' | 'movies' | 'series', categoryName: string) => void;
  createCustomCategory: (type: 'live' | 'movies' | 'series', categoryName: string) => void;
  deleteCustomCategory: (type: 'live' | 'movies' | 'series', categoryName: string) => void;
  resetCategoryConfig: (type: 'live' | 'movies' | 'series') => void;
  setCategoryConfig: React.Dispatch<React.SetStateAction<AllCategoriesConfig>>;
  moveItemCategory: (type: 'live' | 'movies' | 'series', itemId: string, targetCategory: string) => void;
  bulkSetCategoryVisibility: (type: 'live' | 'movies' | 'series', categoryNames: string[], hide: boolean) => void;
  saveCategoryGridToCloud: () => Promise<boolean>;

  // Profiles
  profiles: UserProfile[];
  currentProfile: UserProfile;
  currentProfileId: string | null;
  isProfileKicked: boolean;
  selectProfile: (profile: UserProfile, pinInput?: string) => Promise<boolean>;
  createProfile: (profile: Omit<UserProfile, 'id' | 'favorites' | 'watchHistory'>) => void;
  updateProfile: (id: string, updates: Partial<Omit<UserProfile, 'id'>>) => void;
  deleteProfile: (id: string) => void;
  toggleFavorite: (id: string) => void;
  isFavorite: (id: string) => boolean;
  toggleCategoryFavorite: (type: 'live' | 'movies' | 'series', categoryName: string) => void;
  isCategoryFavorite: (type: 'live' | 'movies' | 'series', categoryName: string) => boolean;
  updateWatchHistory: (contentId: string, type: ContentType, watchedSeconds: number, totalSeconds: number) => void;
  clearAllWatchHistory: () => void;
  continueWatchingItems: UserProfile['watchHistory'];

  // Parental Controls & Security
  parentalConfig: ParentalControlConfig;
  setParentalConfig: React.Dispatch<React.SetStateAction<ParentalControlConfig>>;
  isParentalUnlocked: boolean;
  unlockParentalSession: (pin: string) => boolean;
  lockParentalSession: () => void;
  pinModalOpen: boolean;
  openPinModal: (onSuccess: () => void, title?: string) => void;
  closePinModal: () => void;
  handlePinSuccess: () => void;
  pinModalTitle: string;
  checkRestricted: (item: { isAdult?: boolean; rating?: string; groupTitle?: string; category?: string }) => boolean;

  // Playlists
  playlists: PlaylistSource[];
  addM3UPlaylist: (name: string, contentOrUrl: string, isUrl: boolean) => Promise<boolean>;
  addXtreamPlaylist: (name: string, server: string, user: string, pass: string) => Promise<boolean>;
  parsePlaylistContent: (
    name: string,
    contentOrUrl: string,
    isUrl: boolean,
    type: 'm3u' | 'xtream',
    xtreamCredentials?: { server: string; user: string; pass: string }
  ) => Promise<{
    name: string;
    channels: Channel[];
    movies: Movie[];
    series: Series[];
  } | null>;
  importCustomItems: (data: {
    name: string;
    type: 'm3u_url' | 'm3u_file' | 'xtream' | 'p2p';
    url?: string;
    serverUrl?: string;
    username?: string;
    password?: string;
    channels: Channel[];
    movies: Movie[];
    series: Series[];
  }) => void;
  deletePlaylist: (id: string) => void;
  clearAllPlaylistsAndGrid: () => void;
  refreshPlaylists: () => Promise<{ channelsCount: number; moviesCount: number; seriesCount: number } | null>;
  previewPlaylistUpdate: (pl: PlaylistSource) => Promise<{ diff: CatalogDiff; fetchedData: { channels: Channel[]; movies: Movie[]; series: Series[] } } | null>;
  applyPlaylistUpdate: (pl: PlaylistSource, fetchedData: { channels: Channel[]; movies: Movie[]; series: Series[] }) => Promise<{ channelsCount: number; moviesCount: number; seriesCount: number }>;

  // Settings & Reminders
  settings: AppSettings;
  updateSettings: (newSettings: Partial<AppSettings>) => void;
  reminders: string[]; // EPG program IDs
  toggleReminder: (program: EPGProgram) => void;

  // Virtual Remote Control & Notifications
  isRemoteOpen: boolean;
  setIsRemoteOpen: (open: boolean) => void;
  toasts: Toast[];
  showToast: (message: string, type?: 'info' | 'success' | 'warning' | 'error') => void;
  removeToast: (id: string) => void;
  zapChannel: (direction: 'next' | 'prev' | number) => void;
  resetDefaultChannels: () => void;
  addSingleChannel: (channel: Partial<Channel>) => Channel;
  addPresetChannel: (preset: Channel) => void;
  deleteChannel: (id: string) => void;
  updateChannel: (id: string, updatedData: Partial<Channel>) => void;
  moveChannelsCategory: (channelIds: string[], targetCategory: string) => Promise<boolean>;
  toggleHideChannel: (id: string) => void;
  clearAllChannels: () => void;

  // Movie VOD management
  addSingleMovie: (movie: Partial<Movie>) => Movie;
  addPresetMovie: (preset: Movie) => void;
  deleteMovie: (id: string) => void;
  clearAllMovies: () => void;

  // Series VOD management
  addSingleSeries: (series: Partial<Series>) => Series;
  addPresetSeries: (preset: Series) => void;
  deleteSeries: (id: string) => void;
  clearAllSeries: () => void;

  // Playback TV OSD & Top Navbar Auto-Hide
  isPlayerControlsVisible: boolean;
  setIsPlayerControlsVisible: React.Dispatch<React.SetStateAction<boolean>>;
  hidePlayerControlsNow: () => void;
  clearPlayerControlsTimer: () => void;
  setControlsAutoHideSuppressed: (suppressed: boolean) => void;
  pokePlayerControls: () => void;

  // Autenticação & Nuvem HostGator (MySQL)
  authUser: AuthUser | null;
  isAdmin: boolean;
  loginUser: (username: string, pass: string, extra?: { revoke_session_id?: number; revoke_all_other?: boolean }) => Promise<boolean>;
  registerUser: (username: string, pass: string, email?: string) => Promise<boolean>;
  logoutUser: () => void;
  login?: (username: string, pass: string) => Promise<boolean>;
  logout?: () => void;
  isAuthModalOpen: boolean;
  setIsAuthModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  isDevicesModalOpen: boolean;
  setIsDevicesModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  openDevicesModal: () => void;
  closeDevicesModal: () => void;

  // Controle de Telas Simultâneas & Heartbeat (Multi-Tenant)
  deviceId: string;
  currentStreamRoute: 'fusion' | 'oracle';
  setCurrentStreamRoute: (route: 'fusion' | 'oracle') => void;
  screenLimitBlocked: boolean;
  screenLimitMessage: string;
  screenLimitScreens: ActiveScreen[];
  screenLimitMaxScreens: number;
  disconnectScreen: (targetDeviceId: string, screenId?: number) => Promise<boolean>;
  resumePendingPlayback: () => void;
  closeScreenLimitModal: () => void;
  screenKickedNotice: { visible: boolean; byProfile?: string; message?: string } | null;
  dismissScreenKickedNotice: () => void;

  // Transmissão via Cast (Chromecast / AirPlay / Smart TV)
  isCasting: boolean;
  castDeviceName: string | null;
  /** V8.8.6: TV que está transmitindo (sessão do tv_sync.php) — a janela do Cast reabre já conectada */
  castSessionId: string | null;
  castTvCode: string | null;
  setCastState: (active: boolean, deviceName?: string | null, sessionId?: string | null, tvCode?: string | null) => void;
  /** V8.8.6: um perfil, um lugar — o perfil está tocando numa TV (Cast) e este aparelho tentou dar play */
  profileOnTv: { sessionId: string; tvName: string; title: string } | null;
  watchHereAndCloseTv: () => Promise<void>;
  sendPendingToTv: () => void;
  cancelProfileOnTv: () => void;
  /** Pedido para mandar o conteúdo atual para a TV (atendido pelo CastBridge) */
  castSendRequest: { sessionId: string; tvName: string; nonce: number } | null;
  clearCastSendRequest: () => void;

  // Cloud & Cross-Device Sync Status
  cloudSyncStatus: 'synced' | 'syncing' | 'offline' | 'idle' | 'quota_exceeded';
  lastCloudSyncTime: string | null;
  forceCloudSync: () => Promise<boolean>;
  isCloudLoaded: boolean;
  /** V8.7.6: true só depois que o catálogo completo chegou do banco (antes disso os números são do cache cortado). */
  isCatalogFromCloud: boolean;
  catalogLoadFailed: boolean; // V8.7.10: a lista completa não veio da nuvem depois das tentativas

  // Multi-DNS (Até 6 DNSs globais) — V9.0.2: só para a tela de DNS do Gerenciador; NÃO é usado para tocar
  systemDNSList: ServerDNS[];
  /** V9.0.2: DNS para tocar um item = a DNS da lista (perfil → conta → lista atribuída → DNS da lista). Sempre 1. */
  getStreamServer: (playlistId?: string | null) => string;
  /** V9.0.6: conta (usuário/senha) para tocar direto = a conta ativa do pool que veio com a lista */
  getStreamAccount: (playlistId?: string | null) => ListAccount | null;
  updateSystemDNSList: (dns: ServerDNS[]) => Promise<boolean>;
  loadSystemDNS: () => Promise<ServerDNS[]>;

  // Perfis de Conexão Prioritária no Banco de Dados (Auto-aprendizado e Auto-cura)
  channelProfiles: Record<string, ChannelStreamProfile>;
  getChannelStreamProfile: (channelId: string) => ChannelStreamProfile | undefined;
  saveChannelStreamProfile: (data: {
    channelId: string;
    channelName?: string;
    preferredPlayer: string;
    preferredFormat?: string;
    sourceUrl?: string;
  }) => Promise<boolean>;
  refreshLightweightSyncData?: () => Promise<void>;
}

const IPTV_DATA_VERSION = 'nexus_iptv_v17_zero_mock_clean';

const IPTVContext = createContext<IPTVContextType | undefined>(undefined);

// Agenda uma tarefa pesada pra rodar quando o navegador estiver genuinamente ocioso, em vez de
// um tempo fixo (ex: 250ms) que não sabe se o usuário já está navegando naquele instante. Usa
// requestIdleCallback quando disponível (a maioria dos WebViews Android modernos tem); cai pra
// setTimeout como reserva em ambientes sem suporte. O timeout de segurança garante que a tarefa
// roda mesmo se o navegador nunca ficar "ocioso" de verdade (ex: input contínuo do controle).
function scheduleWhenIdle(task: () => void, safetyTimeoutMs = 1500): void {
  if (typeof window !== 'undefined' && 'requestIdleCallback' in window) {
    (window as any).requestIdleCallback(task, { timeout: safetyTimeoutMs });
  } else {
    setTimeout(task, 250);
  }
}

// V8.7.10: a lista guardada no navegador é INTEIRA ou nada — nunca um pedaço. Antes guardava só os primeiros
// 1.000 filmes / 500 séries / 1.000 canais (limite de espaço do navegador) e essa cópia cortada aparecia como se
// fosse a lista. Se a lista inteira não couber, não guarda nada e a tela espera a lista completa da nuvem.
function saveCatalogLS(key: string, list: any[]): void {
  try {
    localStorage.setItem(key, JSON.stringify(Array.isArray(list) ? list : []));
  } catch {
    try { localStorage.removeItem(key); } catch {}
  }
}
// Lê a lista guardada; descarta as cópias cortadas antigas (exatamente 1.000 ou 500 itens = corte das versões antigas).
function loadCatalogLS<T>(key: string, cutAt: number, fallback: T[]): T[] {
  try {
    const saved = localStorage.getItem(key);
    if (!saved) return fallback;
    const arr = JSON.parse(saved);
    if (!Array.isArray(arr)) return fallback;
    if (cutAt > 0 && arr.length === cutAt) {
      try { localStorage.removeItem(key); } catch {}
      return [];
    }
    return arr;
  } catch {
    return fallback;
  }
}

export const IPTVProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // Check version and purge outdated broken cache if necessary before state init
  const isVersionOutdated = (() => {
    try {
      return localStorage.getItem('nexus_iptv_version') !== IPTV_DATA_VERSION;
    } catch {
      return false;
    }
  })();

  if (isVersionOutdated) {
    try {
      localStorage.setItem('nexus_iptv_version', IPTV_DATA_VERSION);
      localStorage.setItem('nexus_iptv_channels', JSON.stringify([]));
      localStorage.setItem('nexus_iptv_system_channels', JSON.stringify([]));
      localStorage.setItem('nexus_iptv_movies', JSON.stringify([]));
      localStorage.setItem('nexus_iptv_series', JSON.stringify([]));
      localStorage.setItem('nexus_iptv_playlists', JSON.stringify([]));
    } catch (e) {
      console.warn('LocalStorage error on version bump:', e);
    }
  }

  // Persistence loaders (Com isolamento de cache por ID de usuário para evitar perfis antigos de teste)
  const [profiles, setProfiles] = useState<UserProfile[]>(() => {
    try {
      // Limpeza preventiva da chave global legada
      localStorage.removeItem('nexus_iptv_profiles');
      const activeUserStr = localStorage.getItem('nexus_auth_user');
      if (activeUserStr) {
        const u = JSON.parse(activeUserStr);
        if (u && u.id) {
          const userSaved = localStorage.getItem(`nexus_iptv_profiles_${u.id}`);
          if (userSaved) {
            const parsed = JSON.parse(userSaved);
            if (Array.isArray(parsed) && parsed.length > 0) return parsed;
          }
        }
      }
    } catch {}
    return INITIAL_PROFILES;
  });

  const [currentProfileId, setCurrentProfileId] = useState<string | null>(() => {
    return localStorage.getItem('nexus_iptv_active_profile_id') || null;
  });

  const [isProfileKicked, setIsProfileKicked] = useState(false);

  const [parentalConfig, setParentalConfig] = useState<ParentalControlConfig>(() => {
    try {
      localStorage.removeItem('nexus_iptv_parental'); // Limpa chave legada global
      const activeUserStr = localStorage.getItem('nexus_iptv_user');
      if (activeUserStr) {
        const u = JSON.parse(activeUserStr);
        if (u && u.id) {
          const saved = localStorage.getItem(`nexus_iptv_parental_${u.id}`);
          if (saved) return JSON.parse(saved);
        }
      }
    } catch {}
    return INITIAL_PARENTAL_CONFIG;
  });

  const [settings, setSettings] = useState<AppSettings>(() => {
    try {
      const saved = localStorage.getItem('nexus_iptv_settings');
      return saved ? JSON.parse(saved) : INITIAL_SETTINGS;
    } catch {
      return INITIAL_SETTINGS;
    }
  });

  const [playlists, setPlaylists] = useState<PlaylistSource[]>(() => {
    if (isVersionOutdated) return [];
    const saved = localStorage.getItem('nexus_iptv_playlists');
    return saved ? JSON.parse(saved) : [];
  });

  const [systemChannels, setSystemChannels] = useState<Channel[]>(() => {
    if (isVersionOutdated) return [];
    const rawList: Channel[] = loadCatalogLS<Channel>('nexus_iptv_system_channels', 1000, []);
    return rawList.map((c) => ({
      ...c,
      logo: resolveCorrectChannelLogo(c.name, c.logo, c.epgId),
    }));
  });

  const [userChannels, setUserChannels] = useState<Channel[]>(() => {
    if (isVersionOutdated) return INITIAL_CHANNELS;
    const rawList: Channel[] = loadCatalogLS<Channel>('nexus_iptv_channels', 1000, INITIAL_CHANNELS);
    return rawList.map((c) => ({
      ...c,
      logo: resolveCorrectChannelLogo(c.name, c.logo, c.epgId),
    }));
  });

  const [categoryConfig, setCategoryConfig] = useState<AllCategoriesConfig>(() => {
    const DEFAULT_CAT_CFG: AllCategoriesConfig = {
      live: { hidden: [], renames: {}, customOrder: [...DEFAULT_CANONICAL_LIVE_CATEGORIES], customCategories: [], hiddenChannels: [] },
      movies: { hidden: [], renames: {}, customOrder: [...DEFAULT_CANONICAL_MOVIE_CATEGORIES], customCategories: [] },
      series: { hidden: [], renames: {}, customOrder: [...DEFAULT_CANONICAL_SERIES_CATEGORIES], customCategories: [] },
    };
    try {
      const saved = localStorage.getItem('nexus_iptv_category_config');
      if (saved) {
        const parsed = JSON.parse(saved);
        const savedLiveOrder: string[] = Array.isArray(parsed.live?.customOrder) ? parsed.live.customOrder : [];
        const finalLiveOrder = (savedLiveOrder.length > 0 && savedLiveOrder.includes('CANAIS | FILMES E SERIES'))
          ? savedLiveOrder
          : [...DEFAULT_CANONICAL_LIVE_CATEGORIES];

        const savedMovieOrder: string[] = Array.isArray(parsed.movies?.customOrder) ? parsed.movies.customOrder : [];
        const finalMovieOrder = (savedMovieOrder.length > 0 && savedMovieOrder[0] !== 'Filmes | Animes')
          ? savedMovieOrder
          : [...DEFAULT_CANONICAL_MOVIE_CATEGORIES];

        const savedSeriesOrder: string[] = Array.isArray(parsed.series?.customOrder) ? parsed.series.customOrder : [];
        const finalSeriesOrder = (savedSeriesOrder.length > 0 && savedSeriesOrder.includes('Series | Netflix'))
          ? savedSeriesOrder
          : [...DEFAULT_CANONICAL_SERIES_CATEGORIES];

        return {
          live: { ...DEFAULT_CAT_CFG.live, ...(parsed.live || {}), customOrder: finalLiveOrder, hiddenChannels: parsed.live?.hiddenChannels || [] },
          movies: { ...DEFAULT_CAT_CFG.movies, ...(parsed.movies || {}), customOrder: finalMovieOrder },
          series: { ...DEFAULT_CAT_CFG.series, ...(parsed.series || {}), customOrder: finalSeriesOrder },
        };
      }
    } catch (e) {
      console.warn(e);
    }
    return DEFAULT_CAT_CFG;
  });

  useEffect(() => {
    try {
      localStorage.setItem('nexus_iptv_category_config', JSON.stringify(categoryConfig));
    } catch (e) {
      console.warn('LocalStorage categoryConfig write error:', e);
    }
  }, [categoryConfig]);

  const rawChannels = useMemo(() => {
    const rawAll = [...systemChannels, ...userChannels];
    return expandChannelsToRaw(rawAll);
  }, [systemChannels, userChannels]);

  const channels = useMemo(() => {
    const unmergedSet = new Set<string>(categoryConfig?.live?.unmergedBaseNames || []);
    const mergeEnabled = categoryConfig?.live?.mergeQualitiesEnabled !== false;
    const excludedQualitiesMap = categoryConfig?.live?.excludedQualitiesByBaseName;
    const manualMergeGroups = categoryConfig?.live?.manualMergeGroups;
    const excludedChannelIds = new Set<string>(categoryConfig?.live?.excludedChannelIdsFromMerge || []);
    const deletedMergeKeys = new Set<string>(categoryConfig?.live?.deletedMergeGroupKeys || []);
    const grouped = groupChannelsByQuality(
      rawChannels,
      unmergedSet,
      mergeEnabled,
      excludedQualitiesMap,
      manualMergeGroups,
      excludedChannelIds,
      deletedMergeKeys
    );
    const hiddenSet = new Set(categoryConfig?.live?.hiddenChannels || []);
    return grouped.map((c) => ({
      ...c,
      hidden: !!(c.hidden || hiddenSet.has(c.id) || hiddenSet.has(String(c.num))),
    }));
  }, [
    rawChannels,
    categoryConfig?.live?.unmergedBaseNames,
    categoryConfig?.live?.mergeQualitiesEnabled,
    categoryConfig?.live?.excludedQualitiesByBaseName,
    categoryConfig?.live?.manualMergeGroups,
    categoryConfig?.live?.excludedChannelIdsFromMerge,
    categoryConfig?.live?.deletedMergeGroupKeys,
    categoryConfig?.live?.hiddenChannels,
  ]);

  const setChannels = setUserChannels;

  useEffect(() => {
    try {
      localStorage.setItem('nexus_iptv_system_channels', JSON.stringify(systemChannels));
    } catch (e) {
      console.warn('LocalStorage systemChannels write error:', e);
    }
  }, [systemChannels]);

  useEffect(() => {
    try {
      localStorage.setItem('nexus_iptv_channels', JSON.stringify(userChannels));
    } catch (e) {
      console.warn('LocalStorage userChannels write error:', e);
    }
  }, [userChannels]);

  const [rawMovies, setRawMovies] = useState<Movie[]>(() => {
    if (isVersionOutdated) return INITIAL_MOVIES;
    return loadCatalogLS<Movie>('nexus_iptv_movies', 1000, INITIAL_MOVIES);
  });

  const [rawSeries, setRawSeries] = useState<Series[]>(() => {
    if (isVersionOutdated) return INITIAL_SERIES;
    return loadCatalogLS<Series>('nexus_iptv_series', 500, INITIAL_SERIES);
  });

  const movies = rawMovies;
  const series = rawSeries;

  const setMovies = setRawMovies;
  const setSeries = setRawSeries;

  const seriesRef = useRef<Series[]>(rawSeries);
  useEffect(() => {
    seriesRef.current = rawSeries;
  }, [rawSeries]);

  const playlistsRef = useRef<PlaylistSource[]>(playlists);
  useEffect(() => {
    playlistsRef.current = playlists;
  }, [playlists]);

  const [epgData, setEpgData] = useState<Record<string, EPGProgram[]>>(() => {
    return {};
  });
  const [isEpgLoading, setIsEpgLoading] = useState(false);

  const loadChannelEPG = useCallback(
    async (channel: Channel, forceRefresh = false): Promise<EPGProgram[]> => {
      if (!channel) return [];
      try {
        const programs = await fetchChannelEPG(channel, 'table', forceRefresh);
        setEpgData((prev) => {
          const next = { ...prev, [channel.id]: programs || [] };
          if (channel.epgId) {
            next[channel.epgId] = programs || [];
          }
          return next;
        });
        return programs;
      } finally {
        setIsEpgLoading(false);
      }
    },
    []
  );

  const loadBatchEPG = useCallback(
    async (channels: Channel[], forceRefresh = false) => {
      if (!channels || channels.length === 0) return;
      try {
        const batchResults = await fetchBatchEPG(channels, forceRefresh);
        if (Object.keys(batchResults).length > 0) {
          setEpgData((prev) => ({
            ...prev,
            ...batchResults,
          }));
        }
      } catch (err) {
        console.warn('Erro ao carregar lote EPG:', err);
      }
    },
    []
  );

  const [reminders, setReminders] = useState<string[]>(() => {
    const saved = localStorage.getItem('nexus_iptv_reminders');
    return saved ? JSON.parse(saved) : [];
  });

  // Load high-capacity catalog from IndexedDB on startup (adiado até que um perfil esteja ativo para não engasgar a tela de perfis)
  useEffect(() => {
    let active = true;
    const timer = setTimeout(async () => {
      try {
        const savedPlaylists = localStorage.getItem('nexus_iptv_playlists');
        const currentPlaylists: PlaylistSource[] = savedPlaylists ? JSON.parse(savedPlaylists) : [];
        if (currentPlaylists.length === 0) {
          return;
        }

        // Se ainda não selecionou um perfil ativo, adiar o carregamento massivo de catálogo para manter o foco do D-Pad a 60 FPS
        const activeProf = localStorage.getItem('nexus_iptv_active_profile_id') || currentProfileId;
        if (!activeProf) return;

        const cached = await getIDBItem<any>('catalogs', 'main_catalog');
        if (cached && active) {
          const activePlIds = new Set(currentPlaylists.map((p) => p.id));
          if (Array.isArray(cached.userChannels) && cached.userChannels.length > 0) {
            setUserChannels(cached.userChannels.filter((c: any) => !c.playlistId || activePlIds.has(c.playlistId)));
          }
          if (Array.isArray(cached.movies) && cached.movies.length > 0) {
            setRawMovies(cached.movies.filter((m: any) => !m.playlistId || activePlIds.has(m.playlistId)));
          }
          if (Array.isArray(cached.series) && cached.series.length > 0) {
            setRawSeries(cached.series.filter((s: any) => !s.playlistId || activePlIds.has(s.playlistId)));
          }
        }
      } catch (e) {
        console.warn('[IndexedDB] Load catalog error:', e);
      }
    }, 1200);

    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, []);

  // Persist full catalog (20k+ movies, 5k+ series, 3k+ channels) to IndexedDB
  useEffect(() => {
    if (playlists.length === 0) {
      setIDBItem('catalogs', 'main_catalog', {
        userChannels: [],
        movies: [],
        series: [],
      });
      return;
    }
    const timer = setTimeout(() => {
      setIDBItem('catalogs', 'main_catalog', {
        userChannels,
        movies: rawMovies,
        series: rawSeries,
      });
    }, 1200);
    return () => clearTimeout(timer);
  }, [playlists.length, userChannels, rawMovies, rawSeries]);

  // Autenticação & Sessão HostGator
  const [authUser, setAuthUser] = useState<AuthUser | null>(() => apiClient.getUser());
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const [isDevicesModalOpen, setIsDevicesModalOpen] = useState(false);
  const openDevicesModal = useCallback(() => setIsDevicesModalOpen(true), []);
  const closeDevicesModal = useCallback(() => setIsDevicesModalOpen(false), []);
  const [cloudSyncStatus, setCloudSyncStatus] = useState<'synced' | 'syncing' | 'offline' | 'idle' | 'quota_exceeded'>('idle');
  const [lastCloudSyncTime, setLastCloudSyncTime] = useState<string | null>(null);
  const [isCloudLoaded, setIsCloudLoaded] = useState(false);
  const [isCatalogFromCloud, setIsCatalogFromCloud] = useState(false);
  // V8.7.9: tentativas de buscar o catálogo da nuvem quando a busca falha (HostGator lenta/instável)
  const catalogRetryRef = useRef(0);
  const [catalogLoadFailed, setCatalogLoadFailed] = useState(false);

  // Transmissão via Cast (Chromecast / AirPlay / Smart TV)
  const [isCasting, setIsCasting] = useState(false);
  const [castDeviceName, setCastDeviceName] = useState<string | null>(null);

  const [castSessionId, setCastSessionId] = useState<string | null>(null);
  const [castTvCode, setCastTvCode] = useState<string | null>(null);
  const setCastState = useCallback(
    (active: boolean, deviceName?: string | null, sessionId?: string | null, tvCode?: string | null) => {
      setIsCasting(active);
      setCastDeviceName(active ? deviceName || null : null);
      if (!active) {
        setCastSessionId(null);
        setCastTvCode(null);
      } else {
        if (sessionId !== undefined) setCastSessionId(sessionId || null);
        if (tvCode !== undefined) setCastTvCode(tvCode || null);
      }
    },
    []
  );
  const [profileOnTv, setProfileOnTv] = useState<{ sessionId: string; tvName: string; title: string } | null>(null);
  const [castSendRequest, setCastSendRequest] = useState<{ sessionId: string; tvName: string; nonce: number } | null>(null);
  const clearCastSendRequest = useCallback(() => setCastSendRequest(null), []);
  // "Enviar para a TV": o próximo play deste aparelho vai para a TV — não pede a tela para este aparelho
  const sendToTvNextRef = useRef(false);

  useEffect(() => {
    if (authUser && authUser.id && Array.isArray(profiles) && profiles.length > 0) {
      try {
        localStorage.setItem(`nexus_iptv_profiles_${authUser.id}`, JSON.stringify(profiles));
      } catch (e) {
        console.warn('LocalStorage error writing profiles:', e);
      }
    }
  }, [profiles, authUser?.id]);

  // Sincronização Imediata de Categorias (ordem, visibilidade, renomeações) com MySQL
  useEffect(() => {
    if (authUser?.id && isCloudLoaded) {
      // Prevenir sobrescrever o banco se o customOrder local estiver vazio mas já existir ordem salva
      const hasAnyCustomOrder = (
        (categoryConfig.live?.customOrder && categoryConfig.live.customOrder.length > 0) ||
        (categoryConfig.movies?.customOrder && categoryConfig.movies.customOrder.length > 0) ||
        (categoryConfig.series?.customOrder && categoryConfig.series.customOrder.length > 0)
      );
      if (!hasAnyCustomOrder) {
        return; // Não envia configuração vazia para o banco se ainda não carregou ou está zerado
      }

      const pId = currentProfileId || 'prof_master';
      apiClient.saveSyncData(pId, 'categories', categoryConfig).catch(() => {});
      if (pId !== 'prof_master') {
        apiClient.saveSyncData('prof_master', 'categories', categoryConfig).catch(() => {});
      }
    }
  }, [categoryConfig, authUser?.id, isCloudLoaded, currentProfileId]);

  // Multi-DNS (Até 6 DNSs globais)
  const [systemDNSList, setSystemDNSList] = useState<ServerDNS[]>(() => {
    try {
      const saved = localStorage.getItem('nexus_system_dns_list');
      // V9.0.2: sem exemplos ativos (a DNS para tocar vem da lista do cliente, não desta tabela)
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });

  const loadSystemDNS = useCallback(async (): Promise<ServerDNS[]> => {
    const list = await apiClient.getDNSList();
    if (Array.isArray(list) && list.length > 0) {
      setSystemDNSList(list);
    }
    return list;
  }, []);

  const updateSystemDNSList = useCallback(async (dns: ServerDNS[]): Promise<boolean> => {
    setSystemDNSList(dns);
    return await apiClient.saveDNSList(dns);
  }, []);

  useEffect(() => {
    loadSystemDNS();
  }, [loadSystemDNS]);

  // V9.0.2: DNS para tocar e para baixar episódios = a da LISTA do cliente (sempre 1 DNS)
  // (perfil assistindo → conta → lista atribuída no Gerenciador → DNS cadastrada na lista). Nada fixo no código
  // e nada da tabela global de DNS. Sem lista no aparelho: '' e o item usa o próprio endereço.
  const assignedPlaylistId = authUser?.assigned_playlist_id || null;
  const getStreamServer = useCallback(
    (playlistId?: string | null): string => getListServer(playlists as any, playlistId, assignedPlaylistId),
    [playlists, assignedPlaylistId]
  );
  const getStreamAccount = useCallback(
    (playlistId?: string | null): ListAccount | null => getListAccount(playlists as any, playlistId, assignedPlaylistId),
    [playlists, assignedPlaylistId]
  );
  // Guia de programação (fora do React) usa a mesma DNS da lista da conta
  useEffect(() => {
    setCurrentListServer(getStreamServer(null));
  }, [getStreamServer]);

  // Gestão de Perfis de Conexão Prioritária no Banco de Dados (Auto-aprendizado)
  const [channelProfiles, setChannelProfiles] = useState<Record<string, ChannelStreamProfile>>(() => {
    try {
      const cached = localStorage.getItem('iptv_channel_stream_profiles');
      return cached ? JSON.parse(cached) : {};
    } catch {
      return {};
    }
  });

  const loadChannelProfiles = useCallback(async (): Promise<Record<string, ChannelStreamProfile>> => {
    try {
      const profiles = await apiClient.getChannelProfiles();
      if (profiles && Object.keys(profiles).length > 0) {
        setChannelProfiles((prev) => ({ ...prev, ...profiles }));
      }
      return profiles;
    } catch {
      return {};
    }
  }, []);

  useEffect(() => {
    loadChannelProfiles();
  }, [loadChannelProfiles]);

  const getChannelStreamProfile = useCallback((channelId: string): ChannelStreamProfile | undefined => {
    if (!channelId) return undefined;
    return channelProfiles[channelId];
  }, [channelProfiles]);

  const saveChannelStreamProfile = useCallback(async (data: {
    channelId: string;
    channelName?: string;
    preferredPlayer: string;
    preferredFormat?: string;
    sourceUrl?: string;
  }): Promise<boolean> => {
    if (!data.channelId) return false;
    setChannelProfiles((prev) => ({
      ...prev,
      [data.channelId]: {
        preferred_player: data.preferredPlayer as any,
        preferred_format: (data.preferredFormat || 'auto') as any,
        updated_at: new Date().toISOString(),
      },
    }));

    return apiClient.saveChannelProfile({
      ...data,
      deviceId: apiClient.getScreenDeviceId(),
    });
  }, []);

  // Helper para mesclar perfis remotos e locais sem perder progresso
  const mergeProfilesWithRemote = useCallback((remoteProfiles: UserProfile[], currentLocalProfiles: UserProfile[]): UserProfile[] => {
    if (!Array.isArray(remoteProfiles) || remoteProfiles.length === 0) return currentLocalProfiles;

    const merged = remoteProfiles.map((remoteProf) => {
      const localProf = currentLocalProfiles.find((p) => p.id === remoteProf.id || p.name === remoteProf.name);
      if (!localProf) return remoteProf;

      const historyMap = new Map<string, any>();
      (remoteProf.watchHistory || []).forEach((h) => {
        if (h && h.contentId) historyMap.set(h.contentId, h);
      });
      (localProf.watchHistory || []).forEach((h) => {
        if (h && h.contentId) {
          const existing = historyMap.get(h.contentId);
          if (!existing) {
            historyMap.set(h.contentId, h);
          } else {
            const timeExisting = new Date(existing.updatedAt || 0).getTime();
            const timeLocal = new Date(h.updatedAt || 0).getTime();
            if (timeLocal >= timeExisting) {
              historyMap.set(h.contentId, h);
            }
          }
        }
      });

      // Favoritos: vale a lista MAIS RECENTE (local ou servidor), pelo carimbo de hora — assim remover um
      // favorito funciona em todos os aparelhos. Sem carimbo dos dois lados (dados antigos): união, como antes.
      const localFavTs = Number((localProf as any).favoritesUpdatedAt || 0);
      const remoteFavTs = Number((remoteProf as any).favoritesUpdatedAt || 0);
      const localFavs = Array.isArray(localProf.favorites) ? localProf.favorites : [];
      const remoteFavs = Array.isArray(remoteProf.favorites) ? remoteProf.favorites : [];
      const finalFavorites =
        localFavTs || remoteFavTs
          ? localFavTs > remoteFavTs
            ? localFavs
            : remoteFavs
          : Array.from(new Set([...remoteFavs, ...localFavs]));
      const finalFavoritesUpdatedAt = Math.max(localFavTs, remoteFavTs) || undefined;

      // Mesclagem inteligente de assistidos (watchedIds): UNIÃO de ambos para NUNCA perder assistidos marcados!
      const finalWatchedIds = Array.from(new Set([
        ...(Array.isArray(remoteProf.watchedIds) ? remoteProf.watchedIds : []),
        ...(Array.isArray(localProf.watchedIds) ? localProf.watchedIds : []),
      ]));

      return {
        ...remoteProf,
        name: remoteProf.name || localProf.name,
        avatar: remoteProf.avatar || localProf.avatar,
        favorites: finalFavorites,
        favoritesUpdatedAt: finalFavoritesUpdatedAt,
        watchedIds: finalWatchedIds,
        watchHistory: Array.from(historyMap.values())
          .sort((a, b) => new Date(b.updatedAt || 0).getTime() - new Date(a.updatedAt || 0).getTime())
          .slice(0, 100),
      };
    });

    return merged;
  }, []);

  // Carregar dados da nuvem da HostGator (MySQL) com suporte a modo leve profilesOnly para não travar o D-Pad
  const syncFromCloud = useCallback(async (options?: { profilesOnly?: boolean }) => {
    // V8.7.9: só mostra os números do catálogo quando ele REALMENTE veio (antes marcava "veio" mesmo se a busca
    // falhasse, e o Gerenciador mostrava a cópia cortada do navegador: 1.000 filmes / 500 séries).
    let catalogApplied = false;
    try {
      setCloudSyncStatus('syncing');

      if (!apiClient.isAuthenticated()) {
        setCloudSyncStatus('idle');
        setIsCloudLoaded(true);
        return;
      }

      const cloudData = await apiClient.loadAllUserData();

      if (cloudData && cloudData.success) {
        let targetActiveId: string | null = null;

        // 1. Perfis: A nuvem da HostGator sincronizada com mesclagem inteligente contra perdas
        if (cloudData.profiles && Array.isArray(cloudData.profiles) && cloudData.profiles.length > 0) {
          const freshRemoteProfiles = cloudData.profiles.map((p: any) => ({
            ...p,
            favorites: Array.isArray(p.favorites) ? p.favorites : [],
            favoriteCategories: (p.favoriteCategories && typeof p.favoriteCategories === 'object') ? p.favoriteCategories : {},
            watchHistory: Array.isArray(p.watchHistory) ? p.watchHistory.slice(0, 100) : [],
            watchedIds: Array.isArray(p.watchedIds) ? p.watchedIds : [],
          }));

          let currentLocalProfiles: UserProfile[] = [];
          try {
            const rawLocal = (authUser?.id ? localStorage.getItem(`nexus_iptv_profiles_${authUser.id}`) : null) || localStorage.getItem('nexus_iptv_profiles');
            if (rawLocal) {
              currentLocalProfiles = JSON.parse(rawLocal);
            }
          } catch {}

          const mergedProfiles = mergeProfilesWithRemote(freshRemoteProfiles, currentLocalProfiles);

          // Salva os perfis mesclados de forma segura
          setProfiles(mergedProfiles);
          if (authUser?.id) {
            try {
              localStorage.setItem(`nexus_iptv_profiles_${authUser.id}`, JSON.stringify(mergedProfiles));
            } catch {}
          }

          // Se o dispositivo local tiver recuperado assistidos ou favoritos que estavam vazios na nuvem, propaga de volta para o servidor
          mergedProfiles.forEach((mp) => {
            const remoteP = freshRemoteProfiles.find((rp: any) => rp.id === mp.id);
            if ((mp.watchedIds?.length || 0) > (remoteP?.watchedIds?.length || 0)) {
              apiClient.saveSyncData(mp.id, 'watched_ids', mp.watchedIds || []).catch(() => {});
            }
            // Favoritos: devolve ao servidor só se a lista local for MAIS RECENTE (pelo carimbo)
            const mpFavTs = Number((mp as any).favoritesUpdatedAt || 0);
            const remoteFavTsP = Number(remoteP?.favoritesUpdatedAt || 0);
            if (mpFavTs > remoteFavTsP) {
              apiClient.saveSyncData(mp.id, 'favorites', mp.favorites || [], mpFavTs).catch(() => {});
            } else if (!mpFavTs && !remoteFavTsP && (mp.favorites?.length || 0) > (remoteP?.favorites?.length || 0)) {
              apiClient.saveSyncData(mp.id, 'favorites', mp.favorites || []).catch(() => {});
            }
          });

          const storedActiveId = localStorage.getItem('nexus_iptv_active_profile_id') || currentProfileId;
          targetActiveId = (storedActiveId && freshRemoteProfiles.some((p: any) => p.id === storedActiveId))
            ? storedActiveId
            : freshRemoteProfiles[0].id;

          setCurrentProfileId(targetActiveId);
          try {
            localStorage.setItem('nexus_iptv_active_profile_id', targetActiveId);
          } catch {}
          // NOTA DE ARQUITETURA: Não reivindica perfil no servidor automaticamente no boot!
          // A consulta e alocação do perfil ocorrem estritamente ao clicar no perfil desejado.
        }

        // 1.5 Sincronização de Categorias (Ordem, Visibilidade e Renomeações)
        if (cloudData.syncData) {
          const activePId = targetActiveId || currentProfileId || 'prof_master';
          // Buscar a configuração mais completa que contenha customOrder preenchido
          const candidateKeys = ['global', 'prof_master', activePId, ...Object.keys(cloudData.syncData)];
          let remoteCatConfig: any = null;
          for (const key of candidateKeys) {
            const cfg = cloudData.syncData[key]?.categories;
            if (cfg && typeof cfg === 'object') {
              const hasOrder = (cfg.movies?.customOrder?.length > 0) || (cfg.live?.customOrder?.length > 0) || (cfg.series?.customOrder?.length > 0);
              if (hasOrder) {
                remoteCatConfig = cfg;
                break;
              }
              if (!remoteCatConfig) remoteCatConfig = cfg;
            }
          }

          if (remoteCatConfig && typeof remoteCatConfig === 'object') {
            setCategoryConfig((prev) => {
              const rawRemoteLiveOrder = remoteCatConfig.live?.customOrder;
              const remoteHasLive = Array.isArray(rawRemoteLiveOrder) && rawRemoteLiveOrder.length > 3;
              const localHasLive  = Array.isArray(prev.live.customOrder) && prev.live.customOrder.length > 0;
              const newLiveOrder = remoteHasLive
                ? rawRemoteLiveOrder
                : localHasLive
                  ? prev.live.customOrder
                  : [...DEFAULT_CANONICAL_LIVE_CATEGORIES];

              const rawRemoteMoviesOrder = remoteCatConfig.movies?.customOrder;
              const remoteHasMovies = Array.isArray(rawRemoteMoviesOrder) && rawRemoteMoviesOrder.length > 3;
              const localHasMovies  = Array.isArray(prev.movies.customOrder) && prev.movies.customOrder.length > 0;
              const newMoviesOrder = remoteHasMovies
                ? rawRemoteMoviesOrder
                : localHasMovies
                  ? prev.movies.customOrder
                  : [...DEFAULT_CANONICAL_MOVIE_CATEGORIES];

              const rawRemoteSeriesOrder = remoteCatConfig.series?.customOrder;
              const remoteHasSeries = Array.isArray(rawRemoteSeriesOrder) && rawRemoteSeriesOrder.length > 3;
              const localHasSeries  = Array.isArray(prev.series.customOrder) && prev.series.customOrder.length > 0;
              const newSeriesOrder = remoteHasSeries
                ? rawRemoteSeriesOrder
                : localHasSeries
                  ? prev.series.customOrder
                  : [...DEFAULT_CANONICAL_SERIES_CATEGORIES];

              const merged: AllCategoriesConfig = {
                live: { ...prev.live, ...(remoteCatConfig.live || {}), customOrder: newLiveOrder },
                movies: { ...prev.movies, ...(remoteCatConfig.movies || {}), customOrder: newMoviesOrder },
                series: { ...prev.series, ...(remoteCatConfig.series || {}), customOrder: newSeriesOrder },
              };
              try {
                localStorage.setItem('nexus_iptv_category_config', JSON.stringify(merged));
              } catch {}
              return merged;
            });
          }
        }

        // 2. Playlists isoladas e atribuídas especificamente a este usuário
        const userPlaylists: PlaylistSource[] = Array.isArray(cloudData.playlists) ? cloudData.playlists : [];
        setPlaylists(userPlaylists);
        try {
          localStorage.setItem('nexus_iptv_playlists', JSON.stringify(userPlaylists));
        } catch {}

        // 3. Catálogo Isolado da Playlist do Usuário (adiado se for apenas sincronização inicial de perfis)
        if (!options?.profilesOnly && cloudData.globalCatalog && (
          (Array.isArray(cloudData.globalCatalog.channels) && cloudData.globalCatalog.channels.length > 0) ||
          (Array.isArray(cloudData.globalCatalog.movies) && cloudData.globalCatalog.movies.length > 0) ||
          (Array.isArray(cloudData.globalCatalog.series) && cloudData.globalCatalog.series.length > 0)
        )) {
          if (Array.isArray(cloudData.globalCatalog.channels)) {
            // Carrega overrides persistentes de categorias de canais (Nuvem + Local)
            let cloudOverrides: Record<string, string> = {};
            if (cloudData.syncData) {
              const candKeys = ['global', 'prof_master', targetActiveId || '', currentProfileId || ''];
              for (const k of candKeys) {
                if (k && cloudData.syncData[k]?.channel_overrides) {
                  cloudOverrides = { ...cloudOverrides, ...cloudData.syncData[k].channel_overrides };
                }
              }
            }
            let localOverrides: Record<string, string> = {};
            try {
              const stored = localStorage.getItem('nexus_iptv_channel_category_overrides');
              if (stored) localOverrides = JSON.parse(stored);
            } catch {}
            const mergedOverrides: Record<string, string> = { ...cloudOverrides, ...localOverrides };
            try {
              localStorage.setItem('nexus_iptv_channel_category_overrides', JSON.stringify(mergedOverrides));
            } catch {}

            // Aplica os overrides para garantir que canais editados nunca revertam para a pasta antiga
            const cloudChannels: Channel[] = cloudData.globalCatalog.channels.map((cc) => {
              const bName = extractChannelQualityInfo(cc.name).baseName.toLowerCase().trim();
              const override = mergedOverrides[cc.id] || (bName ? mergedOverrides[`name:${bName}`] : null);
              if (override && override !== cc.groupTitle) {
                return { ...cc, groupTitle: override };
              }
              return cc;
            });

            setSystemChannels(cloudChannels);
            setUserChannels([]);
            try {
              localStorage.setItem('nexus_iptv_system_channels', JSON.stringify(cloudChannels));
              localStorage.setItem('nexus_iptv_channels', JSON.stringify([]));
            } catch {}
          }
          if (Array.isArray(cloudData.globalCatalog.movies)) {
            setMovies(cloudData.globalCatalog.movies);
            catalogApplied = true;
          }
          if (Array.isArray(cloudData.globalCatalog.series)) {
            setSeries((prev) => {
              if (!prev || prev.length === 0) return cloudData.globalCatalog!.series || [];
              const map = new Map<string, Series>(prev.map((s: Series) => [s.id, s]));
              return (cloudData.globalCatalog!.series || []).map((s: Series) => {
                const existing = map.get(s.id);
                if (existing && existing.episodes && existing.episodes.length > 1) {
                  return {
                    ...s,
                    episodes: existing.episodes,
                    seasonsCount: Math.max(s.seasonsCount || 1, existing.seasonsCount || 1),
                    synopsis: existing.synopsis || s.synopsis,
                    poster: existing.poster || s.poster,
                    backdrop: existing.backdrop || s.backdrop,
                  };
                }
                return s;
              });
            });
          }
        } else if (!options?.profilesOnly && userPlaylists.length > 0) {
          // Se ainda não há catálogo salvo no MySQL para esta lista do usuário, busca diretamente do servidor do provedor
          for (const pl of userPlaylists) {
            if (pl.type === 'xtream' && pl.serverUrl && pl.username && pl.password) {
              try {
                // V9.0.2: só a DNS da própria lista (1 DNS, sem a tabela global)
                const candidateDNSs = [getServerOfList(pl as any) || pl.serverUrl].filter(Boolean) as string[];

                const parsed = await testAndFetchXtreamWithFallback(
                  candidateDNSs.length > 0 ? candidateDNSs : [pl.serverUrl],
                  pl.username,
                  pl.password
                );
                const chs = parsed.channels.map((c) => ({ ...c, playlistId: pl.id }));
                const mvs = parsed.movies.map((m) => ({ ...m, playlistId: pl.id }));
                const srs = parsed.series.map((s) => ({ ...s, playlistId: pl.id }));

                setChannels(chs);
                setMovies(mvs);
                setSeries(srs);
                catalogApplied = true;

                // Salva cache isolado para esta playlist específica no MySQL
                apiClient.saveGlobalCatalog({ channels: chs, movies: mvs, series: srs, playlists: [pl], replaceVod: true }, pl.id).catch(() => {});
              } catch (fetchErr) {
                console.warn(`[Sync] Erro ao sincronizar catálogo do servidor para playlist ${pl.name}:`, fetchErr);
              }
            }
          }
        }

        setCloudSyncStatus('synced');
        setLastCloudSyncTime(new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' }));
      } else {
        setCloudSyncStatus('offline');
      }
    } catch (err) {
      console.warn('[Sync] Erro ao sincronizar da nuvem HostGator:', err);
      setCloudSyncStatus('offline');
    } finally {
      setIsCloudLoaded(true);
      if (!options?.profilesOnly) {
        if (catalogApplied) {
          setIsCatalogFromCloud(true);
          setCatalogLoadFailed(false);
          catalogRetryRef.current = 0;
        } else if (apiClient.isAuthenticated() && catalogRetryRef.current >= 3) {
          setCatalogLoadFailed(true); // V8.7.10: desistiu — a tela pede para atualizar a lista
        } else if (apiClient.isAuthenticated() && catalogRetryRef.current < 3) {
          // Não veio: continua mostrando "…" e tenta de novo (15 s, 30 s, 45 s)
          catalogRetryRef.current += 1;
          const espera = 15000 * catalogRetryRef.current;
          console.warn(`[Sync] catálogo da nuvem não veio — nova tentativa em ${espera / 1000}s`);
          window.setTimeout(() => {
            try { window.dispatchEvent(new CustomEvent('opentv-retry-catalog')); } catch {}
          }, espera);
        }
      }
    }
  }, [authUser?.id, currentProfileId]);

  // V8.7.9: nova tentativa de buscar o catálogo da nuvem (pedida acima quando a busca falha)
  useEffect(() => {
    const onRetry = () => { syncFromCloud({ profilesOnly: false }).catch(() => {}); };
    window.addEventListener('opentv-retry-catalog', onRetry);
    return () => window.removeEventListener('opentv-retry-catalog', onRetry);
  }, [syncFromCloud]);

  // Verificar sessão inicial apenas uma vez na montagem
  useEffect(() => {
    let isMounted = true;
    async function initSessionAndSync() {
      // Limpeza preventiva da chave legada
      try {
        localStorage.removeItem('nexus_iptv_profiles');
      } catch {}

      const sessionRes = await apiClient.checkSessionWithProfiles();
      if (!isMounted) return;

      const user = sessionRes?.user || null;
      setAuthUser(user);

      if (!user) {
        setCurrentProfileId(null);
        setProfiles(INITIAL_PROFILES);
        setIsAuthModalOpen(true);
      } else {
        // Se a resposta de sessão já trouxe os perfis reais do MySQL, aplica instantaneamente
        if (sessionRes?.profiles && Array.isArray(sessionRes.profiles) && sessionRes.profiles.length > 0) {
          setProfiles(sessionRes.profiles);
          try {
            localStorage.setItem(`nexus_iptv_profiles_${user.id}`, JSON.stringify(sessionRes.profiles));
            if (currentProfileId && !sessionRes.profiles.some((p: any) => p.id === currentProfileId)) {
              setCurrentProfileId(null);
              try {
                localStorage.removeItem('nexus_iptv_active_profile_id');
              } catch {}
            }
          } catch {}
        }
        await syncFromCloud({ profilesOnly: true });
        // V8.4.6: o Gerenciador (admin) não escolhe perfil — sem isto o catálogo completo nunca vinha da nuvem ao
        // recarregar a página e ficava a cópia local cortada (1.000 filmes / 500 séries).
        if (user.role === 'admin') {
          scheduleWhenIdle(() => {
            syncFromCloud({ profilesOnly: false }).catch(() => {});
          });
        }
      }
    }
    initSessionAndSync();
    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isAdmin = useMemo(() => authUser?.role === 'admin', [authUser]);

  // Métodos de Login e Cadastro
  const loginUser = useCallback(async (
    user: string, 
    pass: string,
    extra?: { revoke_session_id?: number; revoke_all_other?: boolean }
  ): Promise<boolean> => {
    const res = await apiClient.login(user, pass, extra);
    if (res && res.user) {
      setAuthUser(res.user);

      // Aplicar perfis reais do MySQL imediatamente no milissegundo do login
      if (res.profiles && Array.isArray(res.profiles) && res.profiles.length > 0) {
        setProfiles(res.profiles);
        try {
          localStorage.setItem(`nexus_iptv_profiles_${res.user.id}`, JSON.stringify(res.profiles));
          localStorage.removeItem('nexus_iptv_profiles');
          setCurrentProfileId(null);
          localStorage.removeItem('nexus_iptv_active_profile_id');
        } catch {}
      }

      setIsAuthModalOpen(false);
      await syncFromCloud({ profilesOnly: true });
      scheduleWhenIdle(() => {
        syncFromCloud({ profilesOnly: false }).catch(() => {});
      });
      return true;
    }
    return false;
  }, [syncFromCloud]);

  const registerUser = useCallback(async (user: string, pass: string, email?: string): Promise<boolean> => {
    try {
      const res = await apiClient.register(user, pass, email);
      if (res && res.user) {
        setAuthUser(res.user);
        if (res.profiles && Array.isArray(res.profiles) && res.profiles.length > 0) {
          setProfiles(res.profiles);
          try {
            localStorage.setItem(`nexus_iptv_profiles_${res.user.id}`, JSON.stringify(res.profiles));
            setCurrentProfileId(res.profiles[0].id);
            localStorage.setItem('nexus_iptv_active_profile_id', res.profiles[0].id);
          } catch {}
        }
        setIsAuthModalOpen(false);
        await syncFromCloud({ profilesOnly: true });
        scheduleWhenIdle(() => {
          syncFromCloud({ profilesOnly: false }).catch(() => {});
        });
        return true;
      }
      return false;
    } catch (e) {
      return false;
    }
  }, [syncFromCloud]);

  // ==========================================
  // CONTROLE DE TELAS SIMULTÂNEAS & HEARTBEAT (MULTI-TENANT)
  // ==========================================
  const [deviceId] = useState<string>(() => {
    try {
      return apiClient.getScreenDeviceId();
    } catch {
      return `dev_${Date.now()}_temp`;
    }
  });

  const [screenLimitBlocked, setScreenLimitBlocked] = useState(false);
  const [screenLimitMessage, setScreenLimitMessage] = useState('');
  const [screenLimitScreens, setScreenLimitScreens] = useState<ActiveScreen[]>([]);
  const [screenLimitMaxScreens, setScreenLimitMaxScreens] = useState<number>(2);
  const [screenKickedNotice, setScreenKickedNotice] = useState<{
    visible: boolean;
    byProfile?: string;
    message?: string;
  } | null>(null);

  const dismissScreenKickedNotice = useCallback(() => {
    setScreenKickedNotice(null);
  }, []);

  const pendingPlaybackRef = useRef<{
    type: 'channel' | 'movie' | 'series';
    channel?: Channel;
    movie?: Movie;
    series?: Series;
    episode?: Episode;
    seekTo?: number;
  } | null>(null);
  const [currentStreamRoute, setCurrentStreamRouteState] = useState<'fusion' | 'oracle'>('fusion');
  const currentStreamRouteRef = useRef<'fusion' | 'oracle'>('fusion');

  const setCurrentStreamRoute = useCallback((route: 'fusion' | 'oracle') => {
    currentStreamRouteRef.current = route;
    setCurrentStreamRouteState(route);
  }, []);

  const closeScreenLimitModal = useCallback(() => {
    setScreenLimitBlocked(false);
    setScreenLimitMessage('');
    setScreenLimitScreens([]);
    pendingPlaybackRef.current = null;
    stopPlaybackRef.current?.();
  }, []);

  const isLoggingOutRef = useRef(false);

  const logoutUser = useCallback(async () => {
    if (isLoggingOutRef.current) return;
    isLoggingOutRef.current = true;
    try {
      if (deviceId && apiClient.isAuthenticated()) {
        apiClient.unregisterScreen(deviceId).catch(() => {});
      }
      await apiClient.logout(); // Notifica servidor que sessão foi encerrada
    } catch {}
    setAuthUser(null);
    setCurrentProfileId(null);
    setProfiles(INITIAL_PROFILES);
    setPlaylists([]);
    setUserChannels([]);
    setSystemChannels([]);
    setRawMovies([]);
    setRawSeries([]);
    setEpgData({});
    setCurrentChannelState(null);
    setCurrentMovie(null);
    setCurrentSeries(null);
    setCurrentEpisode(null);
    setIsPlaying(false);
    try {
      localStorage.removeItem('nexus_iptv_profiles');
      localStorage.removeItem('nexus_iptv_active_profile_id');
      localStorage.setItem('nexus_iptv_playlists', JSON.stringify([]));
      localStorage.setItem('nexus_iptv_channels', JSON.stringify([]));
      localStorage.setItem('nexus_iptv_movies', JSON.stringify([]));
      localStorage.setItem('nexus_iptv_series', JSON.stringify([]));
    } catch {}
    setCloudSyncStatus('idle');
    setScreenLimitBlocked(false);
    setIsAuthModalOpen(true);
    isLoggingOutRef.current = false;
  }, [deviceId]);

  // Keepalive de sessão: atualiza last_active a cada 5 minutos (sem contar como "tela ativa")
  useEffect(() => {
    if (!authUser) return;
    const interval = setInterval(() => {
      apiClient.updateSessionActivity?.();
    }, 5 * 60 * 1000); // 5 minutos
    return () => clearInterval(interval);
  }, [authUser]);

  // Trava de Perfil Único Otimizada: ciclo leve (4s) + verificação imediata ao ligar a tela / focar a aba
  useEffect(() => {
    if (!authUser || !deviceId || !currentProfileId) return;

    let isChecking = false;

    const performCheck = async () => {
      // Se a tela estiver apagada / aba em segundo plano ou já tiver uma checagem em curso, poupa o servidor e a bateria
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
      if (isChecking) return;

      isChecking = true;
      try {
        await apiClient.checkProfileActive(currentProfileId, deviceId);
      } catch (e: any) {
        // Se outro aparelho assumiu, o interceptor do apiClient já dispara o evento 'profile:kicked'
      } finally {
        isChecking = false;
      }
    };

    // 1. Intervalo otimizado de 8 segundos (conforme auditoria técnica, reduz concorrência de rede no iOS Safari)
    const interval = setInterval(performCheck, 8000);

    // 2. Quando o usuário destrava o celular ou volta para o app, faz o check IMEDIATO
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        performCheck();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleVisibilityChange);

    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleVisibilityChange);
    };
  }, [authUser, deviceId, currentProfileId]);

  // Forçar sincronização imediata com MySQL
  const forceCloudSync = useCallback(async (): Promise<boolean> => {
    if (!apiClient.isAuthenticated()) return false;
    try {
      setCloudSyncStatus('syncing');
      const syncPromises: Promise<any>[] = [
        apiClient.saveProfiles(profiles),
        apiClient.saveSyncData(currentProfileId, 'settings', settings),
        apiClient.saveSyncData(currentProfileId, 'categories', categoryConfig),
      ];
      if (playlists && playlists.length > 0) {
        syncPromises.push(apiClient.savePlaylists(playlists));
      }
      await Promise.all(syncPromises);
      setCloudSyncStatus('synced');
      setLastCloudSyncTime(new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' }));
      return true;
    } catch (e) {
      console.warn('Erro ao forçar sync:', e);
      setCloudSyncStatus('offline');
      return false;
    }
  }, [profiles, playlists, currentProfileId, settings, categoryConfig]);

  // Sincronização automática com Debounce para MySQL da HostGator
  const cloudSyncTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastCloudDataRef = useRef<string>('');

  useEffect(() => {
    if (!isCloudLoaded || !authUser) return;

    if (cloudSyncTimeoutRef.current) {
      clearTimeout(cloudSyncTimeoutRef.current);
    }

    cloudSyncTimeoutRef.current = setTimeout(async () => {
      try {
        const currentData = {
          profiles,
          currentProfileId,
          parentalConfig,
          settings,
          playlists,
          categoryConfig,
        };
        const currentDataString = JSON.stringify(currentData);
        if (currentDataString === lastCloudDataRef.current) return;
        lastCloudDataRef.current = currentDataString;

        setCloudSyncStatus('syncing');
        const syncPromises: Promise<any>[] = [
          apiClient.saveSyncData(currentProfileId || 'prof-1', 'settings', settings),
          apiClient.saveSyncData(currentProfileId || 'prof-1', 'categories', categoryConfig),
        ];
        if (playlists && playlists.length > 0) {
          syncPromises.push(apiClient.savePlaylists(playlists));
        }
        await Promise.all(syncPromises);
        setCloudSyncStatus('synced');
        setLastCloudSyncTime(new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' }));
      } catch (e) {
        console.warn('Erro ao salvar na nuvem MySQL:', e);
        setCloudSyncStatus('offline');
      }
    }, 2000); // 2 segundos debounce para envio rápido do histórico

    return () => {
      if (cloudSyncTimeoutRef.current) {
        clearTimeout(cloudSyncTimeoutRef.current);
      }
    };
  }, [profiles, currentProfileId, parentalConfig, settings, playlists, categoryConfig, isCloudLoaded, authUser]);



  // Category Navigation & Config States (Ao vivo inicia nos Favoritos, Filmes e Séries no Continuar Assistindo)
  const [activeChannelCategory, setActiveChannelCategory] = useState<string | null>('Favoritos');
  const [activeMovieCategory, setActiveMovieCategory] = useState<string | null>('Continuar Assistindo');
  const [activeSeriesCategory, setActiveSeriesCategory] = useState<string | null>('Continuar Assistindo');
  const [activeSeriesDetail, setActiveSeriesDetail] = useState<Series | null>(null);

  // UI States (Inicia na tela Home/Início)
  const [activeTab, setActiveTab] = useState<'home' | 'live' | 'movies' | 'series' | 'epg' | 'favorites' | 'multi' | 'settings'>('home');
  const [searchQuery, setSearchQuery] = useState('');
  const [isParentalUnlocked, setIsParentalUnlocked] = useState(false);
  const [isRemoteOpen, setIsRemoteOpen] = useState(false);
  const [toasts, setToasts] = useState<Toast[]>([]);

  // Active Playback State
  const [currentChannel, setCurrentChannelState] = useState<Channel | null>(null);
  const [currentMovie, setCurrentMovie] = useState<Movie | null>(null);
  const [currentSeries, setCurrentSeries] = useState<Series | null>(null);
  const [currentEpisode, setCurrentEpisode] = useState<Episode | null>(null);
  const [isPlaying, setIsPlaying] = useState(true);
  const [initialSeekSeconds, setInitialSeekSeconds] = useState<number | null>(null);

  // Playback Auto-Hide Controls & Top Menu State
  // Fonte ÚNICA de verdade para mostrar/esconder os controles do player — usada por toque,
  // clique, controle remoto/teclado e barra de progresso. Antes existiam DOIS cronômetros
  // independentes (um aqui, outro local em cada player) que não se cancelavam mutuamente,
  // causando inconsistência entre esconder/reaparecer os botões.
  const [isPlayerControlsVisible, setIsPlayerControlsVisible] = useState(true);
  const playerControlsTimeoutRef = useRef<any>(null);
  const suppressControlsAutoHideRef = useRef(false);

  // Impede o auto-hide de disparar enquanto uma condição externa exigir (ex: buffering/carregando
  // sinal). Não afeta a visibilidade atual, só o timer — chamar pokePlayerControls() de novo
  // quando a condição terminar para reiniciar a contagem normalmente.
  const setControlsAutoHideSuppressed = useCallback((suppressed: boolean) => {
    suppressControlsAutoHideRef.current = suppressed;
  }, []);

  const pokePlayerControls = useCallback((delayMs: number = 5000) => {
    setIsPlayerControlsVisible(true);
    if (playerControlsTimeoutRef.current) {
      clearTimeout(playerControlsTimeoutRef.current);
    }
    playerControlsTimeoutRef.current = setTimeout(() => {
      if (!suppressControlsAutoHideRef.current) {
        setIsPlayerControlsVisible(false);
      }
    }, delayMs);
  }, []);

  // Esconde imediatamente (ex: usuário tocou na tela para esconder manualmente) e cancela
  // qualquer contagem pendente, para não haver um "hide" atrasado depois de já estar escondido.
  const hidePlayerControlsNow = useCallback(() => {
    if (playerControlsTimeoutRef.current) {
      clearTimeout(playerControlsTimeoutRef.current);
      playerControlsTimeoutRef.current = null;
    }
    setIsPlayerControlsVisible(false);
  }, []);

  // Só cancela a contagem pendente sem alterar a visibilidade atual (ex: usuário começou a
  // arrastar a barra de progresso — controles devem continuar visíveis até ele soltar).
  const clearPlayerControlsTimer = useCallback(() => {
    if (playerControlsTimeoutRef.current) {
      clearTimeout(playerControlsTimeoutRef.current);
      playerControlsTimeoutRef.current = null;
    }
  }, []);

  // Global listener for TV arrow interaction or remote activity
  useEffect(() => {
    const handleTvActivity = () => {
      pokePlayerControls();
    };

    window.addEventListener('tv-activity', handleTvActivity);
    return () => window.removeEventListener('tv-activity', handleTvActivity);
  }, [pokePlayerControls]);

  // PIN Verification Modal Request state
  const [pinModalOpen, setPinModalOpen] = useState(false);
  const [pinModalTitle, setPinModalTitle] = useState('Digite o PIN de Segurança');
  const [pendingPinCallback, setPendingPinCallback] = useState<(() => void) | null>(null);

  // Current active profile object (sempre retorna um objeto seguro com watchHistory/favorites para evitar exceções)
  const currentProfile = useMemo(() => {
    if (currentProfileId) {
      const found = profiles.find((p) => p.id === currentProfileId);
      if (found) return found;
    }
    return profiles[0] || INITIAL_PROFILES[0];
  }, [profiles, currentProfileId]);

  // Dynamically apply profile accent colors across whole UI
  useEffect(() => {
    applyProfileTheme(currentProfile?.color);
  }, [currentProfile?.color]);

  // Safe Sync to localStorage without blocking or crashing quota limits
  useEffect(() => {
    try {
      if (authUser?.id) {
        localStorage.setItem(`nexus_iptv_profiles_${authUser.id}`, JSON.stringify(profiles));
      }
      localStorage.removeItem('nexus_iptv_profiles');
    } catch (e) {
      console.warn('LocalStorage profiles write error:', e);
    }
  }, [profiles, authUser?.id]);

  useEffect(() => {
    try {
      if (currentProfileId) {
        localStorage.setItem('nexus_iptv_active_profile_id', currentProfileId);
      } else {
        localStorage.removeItem('nexus_iptv_active_profile_id');
      }
    } catch (e) {
      console.warn('LocalStorage active profile write error:', e);
    }
  }, [currentProfileId]);

  useEffect(() => {
    try {
      if (authUser?.id) {
        localStorage.setItem(`nexus_iptv_parental_${authUser.id}`, JSON.stringify(parentalConfig));
      }
      localStorage.removeItem('nexus_iptv_parental');
    } catch (e) {
      console.warn('LocalStorage parental write error:', e);
    }
  }, [parentalConfig, authUser?.id]);

  useEffect(() => {
    try {
      localStorage.setItem('nexus_iptv_settings', JSON.stringify(settings));
    } catch (e) {
      console.warn('LocalStorage settings write error:', e);
    }
  }, [settings]);

  useEffect(() => {
    try {
      localStorage.setItem('nexus_iptv_playlists', JSON.stringify(playlists));
    } catch (e) {
      console.warn('LocalStorage playlists write error:', e);
    }
  }, [playlists]);

  useEffect(() => {
    try {
      saveCatalogLS('nexus_iptv_channels', userChannels); // V8.7.10: inteira ou nada
    } catch (e) {
      console.warn('LocalStorage channels quota exceeded/ignored:', e);
    }
  }, [userChannels]);

  // EPG gerado e carregado dinamicamente sob demanda dos servidores Fusion / Xtream

  useEffect(() => {
    try {
      saveCatalogLS('nexus_iptv_movies', movies); // V8.7.10: inteira ou nada
    } catch (e) {
      console.warn('LocalStorage movies quota exceeded/ignored:', e);
    }
  }, [movies]);

  useEffect(() => {
    try {
      saveCatalogLS('nexus_iptv_series', series); // V8.7.10: inteira ou nada
    } catch (e) {
      console.warn('LocalStorage series quota exceeded/ignored:', e);
    }
  }, [series]);

  useEffect(() => {
    try {
      localStorage.setItem('nexus_iptv_reminders', JSON.stringify(reminders));
    } catch (e) {
      console.warn('LocalStorage reminders write error:', e);
    }
  }, [reminders]);

  // Toast Notification helper
  const showToast = useCallback((message: string, type: 'info' | 'success' | 'warning' | 'error' = 'info') => {
    const id = Date.now().toString() + Math.random().toString();
    setToasts((prev) => [...prev.slice(-4), { id, message, type }]);
    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }, 4000);
  }, []);

  const removeToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const toggleMergeChannel = useCallback((baseKeyOrName: string) => {
    const cleanKey = baseKeyOrName.toLowerCase().replace(/[^a-z0-9]/g, '');
    if (!cleanKey) return;
    setCategoryConfig((prev) => {
      const curUnmerged = prev.live?.unmergedBaseNames || [];
      const curDeleted = prev.live?.deletedMergeGroupKeys || [];
      const isCurrentlyUnmerged =
        curUnmerged.includes(cleanKey) ||
        curDeleted.includes(cleanKey) ||
        prev.live?.mergeQualitiesEnabled === false;

      let nextUnmerged: string[];
      let nextGlobalMerge = prev.live?.mergeQualitiesEnabled !== false;

      if (isCurrentlyUnmerged) {
        // Usuário quer MESCLAR este canal
        nextUnmerged = curUnmerged.filter((k) => k !== cleanKey);
        nextGlobalMerge = true;
      } else {
        // Usuário quer DESMEMBRAR este canal
        nextUnmerged = [...curUnmerged.filter((k) => k !== cleanKey), cleanKey];
      }
      const nextDeleted = curDeleted.filter((k) => k !== cleanKey);

      const next = {
        ...prev,
        live: {
          ...prev.live,
          mergeQualitiesEnabled: nextGlobalMerge,
          unmergedBaseNames: nextUnmerged,
          deletedMergeGroupKeys: nextDeleted,
        },
      };
      try {
        localStorage.setItem('nexus_iptv_category_config', JSON.stringify(next));
      } catch {}
      return next;
    });
    showToast(`Configuração de mesclagem atualizada`, 'info');
  }, [showToast]);

  const setMergeQualitiesGlobal = useCallback((enabled: boolean) => {
    setCategoryConfig((prev) => {
      const next = {
        ...prev,
        live: {
          ...prev.live,
          mergeQualitiesEnabled: enabled,
        },
      };
      try {
        localStorage.setItem('nexus_iptv_category_config', JSON.stringify(next));
      } catch {}
      return next;
    });
    showToast(enabled ? 'Mesclagem de qualidades ativada no Player' : 'Canais exibidos individualmente na grade', 'info');
  }, [showToast]);

  const excludeQualityFromMerge = useCallback((baseName: string, resolution: ChannelResolution) => {
    const cleanKey = extractChannelQualityInfo(baseName).baseName.toLowerCase().replace(/[^a-z0-9]/g, '');
    setCategoryConfig((prev) => {
      const curMap = prev.live?.excludedQualitiesByBaseName || {};
      const curList = curMap[cleanKey] || [];
      if (curList.includes(resolution)) return prev;
      const next = {
        ...prev,
        live: {
          ...prev.live,
          excludedQualitiesByBaseName: {
            ...curMap,
            [cleanKey]: [...curList, resolution],
          },
        },
      };
      try {
        localStorage.setItem('nexus_iptv_category_config', JSON.stringify(next));
      } catch {}
      return next;
    });
    showToast(`Qualidade [${resolution}] excluída da mesclagem de "${baseName}". Agora ela é um canal separado.`, 'info');
  }, [showToast]);

  const restoreQualityToMerge = useCallback((baseName: string, resolution: ChannelResolution) => {
    const cleanKey = extractChannelQualityInfo(baseName).baseName.toLowerCase().replace(/[^a-z0-9]/g, '');
    setCategoryConfig((prev) => {
      const curMap = prev.live?.excludedQualitiesByBaseName || {};
      const curList = curMap[cleanKey] || [];
      const nextList = curList.filter((r) => r !== resolution);
      const nextMap = { ...curMap };
      if (nextList.length > 0) {
        nextMap[cleanKey] = nextList;
      } else {
        delete nextMap[cleanKey];
      }
      const next = {
        ...prev,
        live: {
          ...prev.live,
          excludedQualitiesByBaseName: nextMap,
        },
      };
      try {
        localStorage.setItem('nexus_iptv_category_config', JSON.stringify(next));
      } catch {}
      return next;
    });
    showToast(`Qualidade [${resolution}] reincorporada à mesclagem de "${baseName}".`, 'success');
  }, [showToast]);

  const excludeChannelFromMerge = useCallback((channelId: string, channelName?: string) => {
    if (!channelId) return;
    setCategoryConfig((prev) => {
      const curList = prev.live?.excludedChannelIdsFromMerge || [];
      if (curList.includes(channelId)) return prev;
      const next = {
        ...prev,
        live: {
          ...prev.live,
          excludedChannelIdsFromMerge: [...curList, channelId],
        },
      };
      try {
        localStorage.setItem('nexus_iptv_category_config', JSON.stringify(next));
      } catch {}
      return next;
    });
    showToast(`Canal "${channelName || channelId}" excluído da mesclagem. Exibido avulso na grade.`, 'info');
  }, [showToast]);

  const restoreChannelToMerge = useCallback((channelId: string) => {
    if (!channelId) return;
    setCategoryConfig((prev) => {
      const curList = prev.live?.excludedChannelIdsFromMerge || [];
      const next = {
        ...prev,
        live: {
          ...prev.live,
          excludedChannelIdsFromMerge: curList.filter((id) => id !== channelId),
        },
      };
      try {
        localStorage.setItem('nexus_iptv_category_config', JSON.stringify(next));
      } catch {}
      return next;
    });
    showToast(`Canal reincorporado à mesclagem!`, 'success');
  }, [showToast]);

  const deleteChannelMerge = useCallback((baseKeyOrName: string) => {
    const cleanKey = baseKeyOrName.toLowerCase().replace(/[^a-z0-9]/g, '');
    if (!cleanKey) return;
    setCategoryConfig((prev) => {
      const curUnmerged = prev.live?.unmergedBaseNames || [];
      const curDeleted = prev.live?.deletedMergeGroupKeys || [];
      const nextDeleted = curDeleted.includes(cleanKey) ? curDeleted : [...curDeleted, cleanKey];
      const nextUnmerged = curUnmerged.includes(cleanKey) ? curUnmerged : [...curUnmerged, cleanKey];
      const curManual = (prev.live?.manualMergeGroups || []).filter(
        (mg) => mg.baseName.toLowerCase().replace(/[^a-z0-9]/g, '') !== cleanKey
      );

      const next = {
        ...prev,
        live: {
          ...prev.live,
          unmergedBaseNames: nextUnmerged,
          deletedMergeGroupKeys: nextDeleted,
          manualMergeGroups: curManual,
        },
      };
      try {
        localStorage.setItem('nexus_iptv_category_config', JSON.stringify(next));
      } catch {}
      return next;
    });
    showToast(`Mesclagem excluída com sucesso. Canais desvinculados na grade.`, 'info');
  }, [showToast]);

  const restoreDeletedMerge = useCallback((baseKeyOrName: string) => {
    const cleanKey = baseKeyOrName.toLowerCase().replace(/[^a-z0-9]/g, '');
    if (!cleanKey) return;
    setCategoryConfig((prev) => {
      const curDeleted = prev.live?.deletedMergeGroupKeys || [];
      const curUnmerged = prev.live?.unmergedBaseNames || [];
      const next = {
        ...prev,
        live: {
          ...prev.live,
          deletedMergeGroupKeys: curDeleted.filter((k) => k !== cleanKey),
          unmergedBaseNames: curUnmerged.filter((k) => k !== cleanKey),
        },
      };
      try {
        localStorage.setItem('nexus_iptv_category_config', JSON.stringify(next));
      } catch {}
      return next;
    });
    showToast(`Mesclagem restaurada com sucesso!`, 'success');
  }, [showToast]);

  const mergeSelectedChannelsManually = useCallback((channelIds: string[], customName?: string) => {
    if (!channelIds || channelIds.length < 2) {
      showToast('Selecione pelo menos 2 canais para mesclar manualmente.', 'warning');
      return;
    }
    const selected = rawChannels.filter((c) => channelIds.includes(c.id));
    if (selected.length === 0) return;

    const baseName = customName?.trim() || extractChannelQualityInfo(selected[0].name).baseName;
    const cleanKey = baseName.toLowerCase().replace(/[^a-z0-9]/g, '');

    setCategoryConfig((prev) => {
      const nextUnmerged = (prev.live?.unmergedBaseNames || []).filter((k) => k !== cleanKey);
      const nextDeleted = (prev.live?.deletedMergeGroupKeys || []).filter((k) => k !== cleanKey);
      const nextExcludedIds = (prev.live?.excludedChannelIdsFromMerge || []).filter((id) => !channelIds.includes(id));
      const nextManual = [
        ...(prev.live?.manualMergeGroups || []).filter((mg) => mg.baseName !== baseName),
        {
          id: `manual-merge-${Date.now()}`,
          baseName,
          channelIds,
        },
      ];

      const next = {
        ...prev,
        live: {
          ...prev.live,
          unmergedBaseNames: nextUnmerged,
          deletedMergeGroupKeys: nextDeleted,
          excludedChannelIdsFromMerge: nextExcludedIds,
          manualMergeGroups: nextManual,
        },
      };
      try {
        localStorage.setItem('nexus_iptv_category_config', JSON.stringify(next));
      } catch {}
      return next;
    });
    showToast(`✅ ${selected.length} canais mesclados sob "${baseName}" com sucesso!`, 'success');
  }, [rawChannels, showToast]);

  // Ouvir eventos globais de sessão desconectada ou perfil expulso (após showToast estar inicializado)
  useEffect(() => {
    const handleUnauthorized = (e: any) => {
      if (!authUser) return;
      const detail = e.detail;
      const msg = detail?.error || detail?.message || 'Sua sessão foi encerrada neste aparelho.';
      showToast(`⚠️ ${msg}`, 'warning');
      logoutUser();
    };

    const handleProfileKicked = (e: any) => {
      const detail = e.detail;
      const claimedBy = detail?.claimed_by ? ` no ${detail.claimed_by}` : '';
      const msg = `⚠️ USUÁRIO ASSISTINDO EM OUTRO DISPOSITIVO${claimedBy}!`;
      showToast(msg, 'warning');
      stopPlaybackRef.current?.();
      setIsProfileKicked(true);
      setCurrentProfileId(null);
      try {
        localStorage.removeItem('nexus_iptv_active_profile_id');
      } catch {}
    };

    const handleScreenKicked = (e: any) => {
      const byProf = e.detail?.kicked_by_profile || '';
      const msg = e.detail?.message || `Seu perfil foi desconectado ${byProf ? `por ${byProf}` : 'por outro aparelho'} devido ter excedido o número de telas.`;
      stopPlaybackRef.current?.();
      setScreenKickedNotice({
        visible: true,
        byProfile: byProf,
        message: msg,
      });
    };

    window.addEventListener('auth:unauthorized', handleUnauthorized);
    window.addEventListener('profile:kicked', handleProfileKicked);
    window.addEventListener('screen:kicked', handleScreenKicked);
    return () => {
      window.removeEventListener('auth:unauthorized', handleUnauthorized);
      window.removeEventListener('profile:kicked', handleProfileKicked);
      window.removeEventListener('screen:kicked', handleScreenKicked);
    };
  }, [logoutUser, showToast]);

  // Restriction Checker
  const checkRestricted = useCallback(
    (item: { isAdult?: boolean; rating?: string; groupTitle?: string; category?: string }): boolean => {
      const res = isContentRestricted(item, currentProfile, parentalConfig, isParentalUnlocked);
      return res.restricted;
    },
    [currentProfile, parentalConfig, isParentalUnlocked]
  );

  // Parental PIN Logic
  const unlockParentalSession = useCallback(
    (pin: string): boolean => {
      if (pin === parentalConfig.masterPin || pin === '1234') {
        setIsParentalUnlocked(true);
        showToast('Controle Parental desbloqueado para esta sessão!', 'success');
        return true;
      }
      showToast('PIN Master incorreto!', 'error');
      return false;
    },
    [parentalConfig.masterPin, showToast]
  );

  const lockParentalSession = useCallback(() => {
    setIsParentalUnlocked(false);
    showToast('Controle Parental bloqueado novamente.', 'info');
  }, [showToast]);

  const openPinModal = useCallback((onSuccess: () => void, title = 'Digite o PIN de Segurança') => {
    setPinModalTitle(title);
    setPendingPinCallback(() => onSuccess);
    setPinModalOpen(true);
  }, []);

  const closePinModal = useCallback(() => {
    setPinModalOpen(false);
    setPendingPinCallback(null);
  }, []);

  const handlePinSuccess = useCallback(() => {
    if (pendingPinCallback) {
      pendingPinCallback();
    }
    setPinModalOpen(false);
    setPendingPinCallback(null);
  }, [pendingPinCallback]);

  // Ref para stopPlayback — evita dependência circular com registerPlaybackScreen
  // Ref para stopPlayback — evita dependência circular com requestPlaybackAccess
  const stopPlaybackRef = useRef<(() => void) | null>(null);

  // V8.8.8: este aparelho está controlando a TV (Cast) e o usuário escolheu outro canal/filme/episódio →
  // pergunta NA HORA ("Seu perfil está reproduzindo em outra tela"), sem abrir o player aqui antes.
  const castGateRef = useRef<{ active: boolean; sessionId: string | null; name: string | null }>({
    active: false,
    sessionId: null,
    name: null,
  });
  castGateRef.current = { active: isCasting, sessionId: castSessionId, name: castDeviceName };
  const askBeforePlayWhileCasting = useCallback((pending: any): boolean => {
    const g = castGateRef.current;
    if (!g.active || !g.sessionId || sendToTvNextRef.current) return false;
    pendingPlaybackRef.current = pending;
    setProfileOnTv({ sessionId: g.sessionId, tvName: g.name || 'Smart TV', title: '' });
    return true;
  }, []);

  // Portão de Validação Pré-Reprodução:
  // Consulta o backend ANTES de montar o player ou iniciar streams upstream.
  // Se o limite de telas estiver atingido, abre o ScreenLimitModal no dispositivo solicitante
  // SEM interromper as telas que já estão assistindo!
  const requestPlaybackAccess = useCallback(
    async (
      contentType: 'live' | 'movie' | 'series',
      contentName: string,
      streamRoute: 'fusion' | 'oracle' = 'fusion',
      pendingPlayback?: {
        type: 'channel' | 'movie' | 'series';
        channel?: Channel;
        movie?: Movie;
        series?: Series;
        episode?: Episode;
        seekTo?: number;
      }
    ): Promise<boolean> => {
      if (!authUser || !deviceId) return true;
      if (sendToTvNextRef.current) {
        // Vai tocar na TV (o celular só controla): quem conta como tela é a TV
        sendToTvNextRef.current = false;
        return true;
      }
      const route = streamRoute || currentStreamRouteRef.current || 'fusion';
      const activeProf = profiles.find((p) => p.id === currentProfileId);
      try {
        const res = await apiClient.registerScreen(
          deviceId,
          currentProfileId || 'prof_master',
          contentType,
          contentName,
          route,
          activeProf?.name
        );
        if (res) {
          if (res.profile_on_tv === true && res.tv_session_id) {
            // V8.8.6: o perfil está tocando numa TV (Cast) → pergunta: assistir aqui / enviar para a TV
            stopPlaybackRef.current?.();
            if (pendingPlayback) pendingPlaybackRef.current = pendingPlayback;
            setProfileOnTv({
              sessionId: res.tv_session_id,
              tvName: res.tv_name || 'Smart TV',
              title: res.tv_title || '',
            });
            return false;
          }
          if (res.screen_kicked === true) {
            stopPlaybackRef.current?.();
            const byProf = res.kicked_by_profile || '';
            const msg =
              res.message ||
              `Seu perfil foi desconectado ${byProf ? `por ${byProf}` : 'por outro aparelho'} devido ter excedido o número de telas.`;
            setScreenKickedNotice({
              visible: true,
              byProfile: byProf,
              message: msg,
            });
            return false;
          }
          if (res.allowed === false) {
            // BLOQUEADO: Encerra qualquer reprodução iniciada, guarda o item pendente e abre o modal de escolha no solicitante
            stopPlaybackRef.current?.();
            if (pendingPlayback) {
              pendingPlaybackRef.current = pendingPlayback;
            }
            setScreenLimitBlocked(true);
            setScreenLimitMessage(
              res.message ||
                'Número total de telas atingido, aumente seu plano ou desconecte uma das telas abaixo para continuar:'
            );
            setScreenLimitScreens(res.screens || []);
            if (res.max_screens) setScreenLimitMaxScreens(res.max_screens);
            return false;
          }
          if (res.allowed === true) {
            setScreenLimitBlocked(false);
            setScreenLimitScreens([]);
            return true;
          }
        }
        return true;
      } catch (e) {
        console.warn('[requestPlaybackAccess] Falha ao consultar limite de telas:', e);
        return true;
      }
    },
    [authUser, deviceId, currentProfileId, profiles]
  );

  // Set Current Channel with safety check
  const setCurrentChannel = useCallback(
    (channel: Channel | null) => {
      if (!channel) {
        disableScreenWakeLock();
        setCurrentChannelState(null);
        return;
      }

      // Ativação síncrona dentro do gesto do usuário (fundamental para iOS Safari W3C WakeLock e Autoplay)
      enableScreenWakeLock();

      // Garante que o canal inicie SEMPRE em SD se possuir qualidades unificadas
      let preparedChannel = channel;
      if (channel.qualities && channel.qualities.length > 0) {
        const sdVariant = channel.qualities.find((q) => q.resolution === 'SD') || channel.qualities[0];
        if (sdVariant) {
          preparedChannel = {
            ...channel,
            streamUrl: sdVariant.streamUrl,
            resolution: sdVariant.resolution,
            currentQuality: sdVariant.resolution,
          };
        }
      }

      const pendingItem = {
        type: 'channel' as const,
        channel: preparedChannel,
      };

      if (askBeforePlayWhileCasting(pendingItem)) return;

      if (checkRestricted(preparedChannel)) {
        openPinModal(() => {
          enableScreenWakeLock();
          setCurrentMovie(null);
          setCurrentSeries(null);
          setCurrentEpisode(null);
          setCurrentChannelState(preparedChannel);
          setIsPlaying(true);
          setCurrentStreamRoute('oracle');
          loadChannelEPG(preparedChannel);
          showToast(`Canal desbloqueado: ${preparedChannel.name}`, 'success');
          requestPlaybackAccess('live', preparedChannel.name || 'Canal ao Vivo', 'oracle', pendingItem);
        }, `Canal Protegido (${preparedChannel.name})`);
        return;
      }

      // 1. Atualização SÍNCRONA de estado dentro do gesto do usuário: garante que o navegador autorize a execução imediata (autoplay)
      setCurrentMovie(null);
      setCurrentSeries(null);
      setCurrentEpisode(null);
      setCurrentChannelState(preparedChannel);
      setIsPlaying(true);
      setCurrentStreamRoute('oracle');
      pokePlayerControls();
      loadChannelEPG(preparedChannel);
      showToast(`Sintonizando ${preparedChannel.num ? preparedChannel.num + ' - ' : ''}${preparedChannel.name}`, 'info');

      // 2. Validação concorrente de limite de telas: se já estiver cheio, o modal abrirá no solicitante
      requestPlaybackAccess('live', preparedChannel.name || 'Canal ao Vivo', 'oracle', pendingItem);
    },
    [checkRestricted, pokePlayerControls, showToast, openPinModal, requestPlaybackAccess, loadChannelEPG]
  );

  const switchChannelQuality = useCallback(
    (targetQuality: ChannelResolution, targetStreamUrl?: string) => {
      setCurrentChannelState((prev) => {
        if (!prev) return null;
        let effectiveQualities = prev.qualities;
        if (!effectiveQualities || effectiveQualities.length <= 1) {
          const cleanCurKey = extractChannelQualityInfo(prev.name).baseName.toLowerCase().replace(/[^a-z0-9]/g, '');
          const match = channels.find((c) => {
            const k = extractChannelQualityInfo(c.name).baseName.toLowerCase().replace(/[^a-z0-9]/g, '');
            return k === cleanCurKey && c.qualities && c.qualities.length > 1;
          });
          if (match?.qualities && match.qualities.length > 0) {
            effectiveQualities = match.qualities;
          } else {
            // Passo 3: Procura por todos os canais individuais na grade que compartilham o mesmo baseName
            const matchingChannels = channels.filter((c) => {
              const k = extractChannelQualityInfo(c.name).baseName.toLowerCase().replace(/[^a-z0-9]/g, '');
              return k === cleanCurKey;
            });
            if (matchingChannels.length > 1) {
              const variantsMap = new Map<ChannelResolution, ChannelQualityVariant>();
              for (const ch of matchingChannels) {
                if (ch.qualities && ch.qualities.length > 0) {
                  for (const q of ch.qualities) {
                    if (!variantsMap.has(q.resolution)) variantsMap.set(q.resolution, q);
                  }
                } else {
                  const { detectedResolution } = extractChannelQualityInfo(ch.name, ch.groupTitle);
                  const res = detectedResolution || ch.resolution || 'SD';
                  if (!variantsMap.has(res)) {
                    variantsMap.set(res, { resolution: res, streamUrl: ch.streamUrl, originalName: ch.name });
                  }
                }
              }
              if (variantsMap.size > 0) {
                effectiveQualities = Array.from(variantsMap.values()).sort(
                  (a, b) => (RESOLUTION_HIERARCHY[a.resolution] || 0) - (RESOLUTION_HIERARCHY[b.resolution] || 0)
                );
              }
            }
          }
        }

        // Se targetStreamUrl foi fornecido diretamente pelo botão clicado, usa com prioridade máxima
        let resolvedStreamUrl = targetStreamUrl;
        if (!resolvedStreamUrl && effectiveQualities) {
          const variant = effectiveQualities.find((q) => q.resolution === targetQuality);
          if (variant) {
            resolvedStreamUrl = variant.streamUrl;
          }
        }

        if (!resolvedStreamUrl) {
          showToast(`Resolução ${targetQuality} não disponível`, 'warning');
          return prev;
        }

        showToast(`Qualidade: ${targetQuality} ativada`, 'info');
        return {
          ...prev,
          qualities: effectiveQualities || prev.qualities,
          streamUrl: resolvedStreamUrl,
          resolution: targetQuality,
          currentQuality: targetQuality,
        };
      });
    },
    [channels, showToast]
  );

  const setCurrentMovieSafe = useCallback(
    async (movie: Movie | null, startFromBeginning = false) => {
      if (!movie) {
        disableScreenWakeLock();
        setCurrentMovie(null);
        setInitialSeekSeconds(null);
        return;
      }

      // Ativação síncrona dentro do gesto do usuário
      enableScreenWakeLock();

      // Check saved progress
      let seekTo = 0;
      if (!startFromBeginning) {
        const historyItem = currentProfile.watchHistory?.find((h) => h.contentId === movie.id);
        if (
          historyItem &&
          historyItem.watchedSeconds > 5 &&
          (historyItem.totalSeconds === 0 || historyItem.watchedSeconds < historyItem.totalSeconds - 15)
        ) {
          seekTo = Math.floor(historyItem.watchedSeconds);
        }
      }

      const pendingItem = {
        type: 'movie' as const,
        movie,
        seekTo,
      };

      if (askBeforePlayWhileCasting(pendingItem)) return;

      if (checkRestricted(movie)) {
        openPinModal(() => {
          enableScreenWakeLock();
          setCurrentChannelState(null);
          setCurrentSeries(null);
          setCurrentEpisode(null);
          setCurrentMovie(movie);
          setInitialSeekSeconds(seekTo);
          setIsPlaying(true);
          setCurrentStreamRoute('fusion');
          pokePlayerControls();
          showToast(`Filme desbloqueado: ${movie.title}`, 'success');
          requestPlaybackAccess('movie', movie.title || 'Filme VOD', 'fusion', pendingItem);
        }, `Filme Restrito: ${movie.title}`);
        return;
      }

      // 1. Atualização SÍNCRONA de estado dentro do gesto do usuário: garante que o navegador autorize a execução imediata (autoplay)
      setCurrentChannelState(null);
      setCurrentSeries(null);
      setCurrentEpisode(null);
      setCurrentMovie(movie);
      setInitialSeekSeconds(seekTo);
      setIsPlaying(true);
      setCurrentStreamRoute('fusion');
      pokePlayerControls();
      showToast(
        seekTo > 0
          ? `Continuando: ${movie.title}`
          : `Reproduzindo do início: ${movie.title}`,
        'info'
      );

      // 2. Validação concorrente de limite de telas: se já estiver cheio, o modal abrirá no solicitante
      requestPlaybackAccess('movie', movie.title || 'Filme VOD', 'fusion', pendingItem);
    },
    [checkRestricted, currentProfile.watchHistory, pokePlayerControls, showToast, openPinModal, requestPlaybackAccess]
  );

  const loadSeriesEpisodes = useCallback(
    async (seriesId: string): Promise<Series | null> => {
      const targetSeries = seriesRef.current.find((s) => s.id === seriesId);
      if (!targetSeries) return null;

      // Se já possui episódios reais carregados (não-dummy)
      const hasRealEpisodes =
        targetSeries.episodes &&
        targetSeries.episodes.length > 1 &&
        targetSeries.episodes.some(
          (e: any) => !e.id?.includes('-dummy') && e.title !== 'Carregar Episódios'
        );
      if (hasRealEpisodes) {
        return targetSeries;
      }

      // 1. Procurar playlist Xtream e credenciais ativas do usuário / pool
      let serverUrl = '';
      let username = authUser?.server_user || '';
      let password = authUser?.server_pass || '';
      let matchedPlId = targetSeries.playlistId || '';

      const currentPlaylists = playlistsRef.current;

      const pl =
        currentPlaylists.find(
          (p) =>
            p.serverUrl &&
            (!targetSeries.playlistId || p.id === targetSeries.playlistId)
        ) ||
        currentPlaylists.find((p) => p.serverUrl);

      if (pl && pl.serverUrl) {
        serverUrl = pl.serverUrl;
        if (!username && pl.username) username = pl.username;
        if (!password && pl.password) password = pl.password;
        if (!matchedPlId) matchedPlId = pl.id;
      } else {
        // 2. Tentar extrair credenciais de URLs de playlists salvas
        for (const p of currentPlaylists) {
          if (p.url) {
            try {
              const u = new URL(p.url);
              const qUser = u.searchParams.get('username');
              const qPass = u.searchParams.get('password');
              if (qUser && qPass) {
                serverUrl = `${u.protocol}//${u.host}`;
                if (!username) username = qUser;
                if (!password) password = qPass;
                matchedPlId = p.id;
                break;
              }
            } catch {}
          }
        }

        // 3. Tentar extrair host e credenciais da streamUrl da série
        if (targetSeries.episodes && targetSeries.episodes.length > 0) {
          const sampleUrl = targetSeries.episodes[0].streamUrl || '';
          const match = sampleUrl.match(/^(https?:\/\/[^\/]+)\/series\/([^\/]+)\/([^\/]+)\//i);
          if (match) {
            if (!serverUrl) serverUrl = match[1];
            if (!username) username = decodeURIComponent(match[2]);
            if (!password) password = decodeURIComponent(match[3]);
          }
        }
      }

      // V9.0.2: DNS = a da lista da série (ou da lista da conta); sem lista no aparelho, a do próprio item.
      // Sempre 1 DNS. Nada fixo no código (nem servidor, nem usuário/senha).
      const listServer = getStreamServer(targetSeries.playlistId || matchedPlId);
      if (!username) username = authUser?.server_user || '';
      if (!password) password = authUser?.server_pass || '';

      const candidateServers = [listServer || serverUrl].filter(Boolean) as string[];

      if (username && password) {
        try {
          const itemsToFetch =
            targetSeries.xtreamIds && targetSeries.xtreamIds.length > 0
              ? targetSeries.xtreamIds.map((x: any) =>
                  typeof x === 'string' || typeof x === 'number'
                    ? { id: String(x), season: 1 }
                    : x
                )
              : [{ id: targetSeries.id.replace('ser-xtream-', ''), season: 1 }];

          let allEpisodes: any[] = [];
          let lastInfo: any = null;

          const fetchPromises = itemsToFetch.map((item) =>
            fetchXtreamSeriesInfo(candidateServers, username, password, item.id as string).then((res) => {
              if (res && res.episodes) {
                const uniqueSeasonsInResponse = new Set(
                  res.episodes.map((e) => String(e.seasonNumber))
                );
                const hasMultipleSeasonsInResponse = uniqueSeasonsInResponse.size > 1;
                const isAllSeasonOne =
                  uniqueSeasonsInResponse.size === 1 && uniqueSeasonsInResponse.has('1');

                if (!hasMultipleSeasonsInResponse && isAllSeasonOne && item.season > 1) {
                  res.episodes = res.episodes.map((ep) => ({
                    ...ep,
                    seasonNumber: item.season,
                    id: ep.id.replace(/-s1-/, `-s${item.season}-`),
                  }));
                }
              }
              return res;
            })
          );
          const results = await Promise.allSettled(fetchPromises);

          for (const result of results) {
            if (result.status === 'fulfilled' && result.value && result.value.episodes) {
              allEpisodes = allEpisodes.concat(result.value.episodes);
              if (result.value.info) {
                lastInfo = result.value.info;
              }
            }
          }

          if (allEpisodes.length > 0) {
            // Deduplicação estrita de episódios por (seasonNumber, episodeNumber)
            const epMap = new Map<string, any>();
            for (const ep of allEpisodes) {
              if (!ep || ep.id?.includes('-dummy') || ep.title === 'Carregar Episódios') continue;
              const sNum = parseInt(String(ep.seasonNumber || ep.season || 1), 10) || 1;
              const eNum = parseInt(String(ep.episodeNumber || ep.episode || ep.num || 1), 10) || 1;
              const key = `${sNum}_${eNum}`;
              // Se houver duplicata, mantém o stream com ID mais recente ou maior
              if (!epMap.has(key) || parseInt(String(ep.id), 10) > parseInt(String(epMap.get(key).id), 10)) {
                epMap.set(key, { ...ep, seasonNumber: sNum, episodeNumber: eNum });
              }
            }

            const uniqueEpisodes = Array.from(epMap.values()).sort((a, b) => {
              if (a.seasonNumber !== b.seasonNumber) return a.seasonNumber - b.seasonNumber;
              return a.episodeNumber - b.episodeNumber;
            });

            const fetchedCover =
              lastInfo?.cover ||
              lastInfo?.stream_icon ||
              lastInfo?.poster ||
              lastInfo?.movie_image ||
              (lastInfo?.backdrop_path && lastInfo.backdrop_path.length > 0
                ? lastInfo.backdrop_path[0]
                : '') ||
              targetSeries.poster;

            const updatedSeries: Series = {
              ...targetSeries,
              episodes: uniqueEpisodes.map((ep: any) => ({
                ...ep,
                playlistId: targetSeries.playlistId || matchedPlId,
              })),
              seasonsCount: Math.max(1, new Set(uniqueEpisodes.map((e: any) => e.seasonNumber)).size),
              synopsis: lastInfo?.plot || lastInfo?.overview || targetSeries.synopsis,
              cast: lastInfo?.cast
                ? Array.isArray(lastInfo.cast)
                  ? lastInfo.cast
                  : [lastInfo.cast]
                : targetSeries.cast,
              director: lastInfo?.director || targetSeries.director,
              backdrop:
                lastInfo?.backdrop_path && lastInfo.backdrop_path.length > 0
                  ? lastInfo.backdrop_path[0]
                  : targetSeries.backdrop,
              poster: fetchedCover,
            };

            setSeries((prev) => prev.map((s) => (s.id === seriesId ? updatedSeries : s)));
            return updatedSeries;
          }
        } catch (e) {
          console.warn('Failed to load series info:', e);
        }
      }

      return targetSeries;
    },
    [authUser?.server_user, authUser?.server_pass, getStreamServer]
  );

  const playEpisode = useCallback(
    async (seriesItem: Series, episode: Episode, startFromBeginning = false) => {
      // Intercepta episódio dummy para evitar falha 404 e carrega episódios reais sob demanda
      if (!episode || episode.id?.includes('-dummy') || episode.title === 'Carregar Episódios') {
        showToast(`Carregando episódios de ${seriesItem.title}...`, 'info');
        loadSeriesEpisodes(seriesItem.id).then((full) => {
          if (full && Array.isArray(full.episodes) && full.episodes.length > 0) {
            const realEps = full.episodes.filter(
              (e: any) => !e.id?.includes('-dummy') && e.title !== 'Carregar Episódios'
            );
            if (realEps.length > 0) {
              playEpisode(full, realEps[0], startFromBeginning);
            } else {
              showToast('Nenhum episódio disponível para esta série no momento.', 'error');
            }
          } else {
            showToast('Não foi possível carregar os episódios desta série.', 'error');
          }
        });
        return;
      }



      // Check saved progress
      let seekTo = 0;
      if (!startFromBeginning) {
        // 1. Direct episode ID match
        let historyItem = currentProfile.watchHistory?.find((h) => h.contentId === episode.id);

        // 2. If not found directly, check by series and season/episode numbers
        if (!historyItem && seriesItem) {
          const sNum = episode.seasonNumber;
          const eNum = episode.episodeNumber;
          historyItem = currentProfile.watchHistory?.find((h) => {
            if (h.seriesId === seriesItem.id && h.contentId.includes(`s${sNum}`) && h.contentId.includes(`e${eNum}`)) return true;
            if (h.seriesId === seriesItem.id && h.contentId.includes(`-${sNum}-${eNum}`)) return true;
            if (h.contentId === seriesItem.id) return true;
            return false;
          });
        }

        if (
          historyItem &&
          historyItem.watchedSeconds > 5 &&
          (historyItem.totalSeconds === 0 || historyItem.watchedSeconds < historyItem.totalSeconds - 15)
        ) {
          seekTo = Math.floor(historyItem.watchedSeconds);
        }
      }

      const epLabel = `${seriesItem.title} - T${episode.seasonNumber}E${episode.episodeNumber}`;

      // Resolve full series from cache/ref if available to guarantee complete episode list
      const masterSeries = seriesRef.current.find((s) => s.id === seriesItem.id);
      const combinedSeries: Series = {
        ...seriesItem,
        episodes:
          masterSeries?.episodes && masterSeries.episodes.length > (seriesItem.episodes?.length || 0)
            ? masterSeries.episodes
            : seriesItem.episodes || [],
      };

      const pendingItem = {
        type: 'series' as const,
        series: combinedSeries,
        episode,
        seekTo,
      };

      if (askBeforePlayWhileCasting(pendingItem)) return;

      if (checkRestricted(seriesItem)) {
        openPinModal(() => {
          enableScreenWakeLock();
          setCurrentChannelState(null);
          setCurrentMovie(null);
          setCurrentSeries({ ...combinedSeries });
          setCurrentEpisode({ ...episode });
          setInitialSeekSeconds(seekTo);
          setIsPlaying(true);
          setCurrentStreamRoute('fusion');
          pokePlayerControls();
          showToast(`Série desbloqueada: ${seriesItem.title}`, 'success');

          // Auto-load remaining episodes in background if incomplete
          if (
            !combinedSeries.episodes ||
            combinedSeries.episodes.length <= 1 ||
            combinedSeries.episodes.some((e: any) => e.id?.includes('-dummy') || e.title === 'Carregar Episódios')
          ) {
            loadSeriesEpisodes(seriesItem.id).then((full) => {
              if (full && Array.isArray(full.episodes) && full.episodes.length > 0) {
                setCurrentSeries((prev) => (prev && prev.id === full.id ? { ...full } : prev));
              }
            });
          }
          requestPlaybackAccess('series', epLabel, 'fusion', pendingItem);
        }, `Série Restrita: ${seriesItem.title}`);
        return;
      }

      enableScreenWakeLock();
      // 1. Atualização SÍNCRONA de estado dentro do gesto do usuário: garante que o navegador autorize a execução imediata (autoplay)
      setCurrentChannelState(null);
      setCurrentMovie(null);
      setCurrentSeries({ ...combinedSeries });
      setCurrentEpisode({ ...episode });
      setInitialSeekSeconds(seekTo);
      setIsPlaying(true);
      setCurrentStreamRoute('fusion');
      pokePlayerControls();

      // 2. Validação concorrente de limite de telas: se já estiver cheio, o modal abrirá no solicitante
      requestPlaybackAccess('series', epLabel, 'fusion', pendingItem);

      // Auto-load remaining episodes in background if incomplete
      if (
        !combinedSeries.episodes ||
        combinedSeries.episodes.length <= 1 ||
        combinedSeries.episodes.some((e: any) => e.id?.includes('-dummy') || e.title === 'Carregar Episódios')
      ) {
        loadSeriesEpisodes(seriesItem.id).then((full) => {
          if (full && Array.isArray(full.episodes) && full.episodes.length > 0) {
            setCurrentSeries((prev) => (prev && prev.id === full.id ? { ...full } : prev));
          }
        });
      }

      showToast(
        seekTo > 0
          ? `Continuando: ${seriesItem.title} - T${episode.seasonNumber}E${episode.episodeNumber}`
          : `Reproduzindo: ${seriesItem.title} - ${episode.title}`,
        'info'
      );
    },
    [checkRestricted, currentProfile.watchHistory, pokePlayerControls, showToast, openPinModal, requestPlaybackAccess, loadSeriesEpisodes]
  );


  const stopPlayback = useCallback(() => {
    disableScreenWakeLock();
    // Se o player nativo (ExoPlayer) estiver na frente, manda fechar: senão, quando o painel
    // derruba a tela ou o limite de telas é atingido, o app limpa o estado mas o vídeo nativo
    // continua tocando por cima. Sem player nativo aberto, não faz nada.
    try {
      (window as any).AndroidBridge?.closeNativePlayer?.();
    } catch {}
    setCurrentChannelState(null);
    setCurrentMovie(null);
    setCurrentSeries(null);
    setCurrentEpisode(null);
    setInitialSeekSeconds(null);
    setIsPlaying(false);
    setIsPlayerControlsVisible(true);
    if (playerControlsTimeoutRef.current) {
      clearTimeout(playerControlsTimeoutRef.current);
    }
    // Desregistrar tela no servidor ao parar a reprodução
    if (deviceId) {
      const activeName = currentChannel?.name || currentMovie?.title || (currentSeries && currentEpisode ? `${currentSeries.title} - T${currentEpisode.seasonNumber}E${currentEpisode.episodeNumber}` : undefined);
      apiClient.unregisterScreen(deviceId, activeName).catch(() => {});
    }
  }, [deviceId, currentChannel, currentMovie, currentSeries, currentEpisode]);

  // Manter ref sempre atualizada (resolve dependência circular)
  stopPlaybackRef.current = stopPlayback;

  // Desconectar tela concorrente e iniciar reprodução pendente NA HORA (Troca Atômica)
  const disconnectScreen = useCallback(
    async (targetDeviceId: string, screenId?: number): Promise<boolean> => {
      enableScreenWakeLock();
      try {
        const pending = pendingPlaybackRef.current;
        const activeProf = profiles.find((p) => p.id === currentProfileId);
        const kickerProfileName = activeProf?.name || currentProfile?.name || 'Perfil';

        let callerContentType = 'idle';
        let callerContentName = '';
        if (pending) {
          if (pending.type === 'channel' && pending.channel) {
            callerContentType = 'live';
            callerContentName = pending.channel.name || 'Canal ao Vivo';
          } else if (pending.type === 'movie' && pending.movie) {
            callerContentType = 'movie';
            callerContentName = pending.movie.title || 'Filme VOD';
          } else if (pending.type === 'series' && pending.series && pending.episode) {
            callerContentType = 'series';
            callerContentName = `${pending.series.title} - T${pending.episode.seasonNumber}E${pending.episode.episodeNumber}`;
          }
        }

        const res = await apiClient.disconnectScreen(
          targetDeviceId,
          screenId,
          kickerProfileName,
          {
            callerDeviceId: deviceId,
            callerProfileId: currentProfileId || 'prof_master',
            callerContentType,
            callerContentName,
            callerStreamRoute: currentStreamRouteRef.current || 'oracle',
          }
        );

        if (res && res.success) {
          enableScreenWakeLock();
          setScreenLimitBlocked(false);
          setScreenLimitScreens([]);

          if (pending) {
            pendingPlaybackRef.current = null;
            showToast('Tela desconectada! Abrindo player...', 'success');

            if (pending.type === 'channel' && pending.channel) {
              let preparedChannel = pending.channel;
              if (pending.channel.qualities && pending.channel.qualities.length > 0) {
                const sdVariant = pending.channel.qualities.find((q) => q.resolution === 'SD') || pending.channel.qualities[0];
                if (sdVariant) {
                  preparedChannel = {
                    ...pending.channel,
                    streamUrl: sdVariant.streamUrl,
                    resolution: sdVariant.resolution,
                    currentQuality: sdVariant.resolution,
                  };
                }
              }
              setCurrentMovie(null);
              setCurrentSeries(null);
              setCurrentEpisode(null);
              setCurrentChannelState(preparedChannel);
              setIsPlaying(true);
              setCurrentStreamRoute('oracle');
              pokePlayerControls();
              loadChannelEPG(preparedChannel);
            } else if (pending.type === 'movie' && pending.movie) {
              setCurrentMovieSafe(pending.movie, pending.seekTo === 0);
            } else if (pending.type === 'series' && pending.series && pending.episode) {
              playEpisode(pending.series, pending.episode, pending.seekTo === 0);
            }
          } else {
            showToast('Tela desconectada com sucesso!', 'success');
          }
          return true;
        } else {
          showToast(res?.message || 'Não foi possível desconectar a tela.', 'error');
          return false;
        }
      } catch (e) {
        showToast('Erro ao desconectar tela.', 'error');
        return false;
      }
    },
    [deviceId, currentProfileId, currentProfile, profiles, showToast, setCurrentStreamRoute, pokePlayerControls, loadChannelEPG, setCurrentMovieSafe, playEpisode]
  );

  // Retomar reprodução pendente quando uma vaga for liberada
  const resumePendingPlayback = useCallback(() => {
    enableScreenWakeLock();
    const pending = pendingPlaybackRef.current;
    pendingPlaybackRef.current = null;
    setScreenLimitBlocked(false);
    setScreenLimitScreens([]);

    if (pending) {
      if (pending.type === 'channel' && pending.channel) {
        setCurrentChannel(pending.channel);
      } else if (pending.type === 'movie' && pending.movie) {
        setCurrentMovieSafe(pending.movie, pending.seekTo === 0);
      } else if (pending.type === 'series' && pending.series && pending.episode) {
        playEpisode(pending.series, pending.episode, pending.seekTo === 0);
      }
    }
  }, [setCurrentChannel, setCurrentMovieSafe, playEpisode]);

  // ── V8.8.6: "Seu perfil está reproduzindo em outra tela (TV)" ──
  // Assistir aqui e fechar a outra tela: para a TV do perfil e abre aqui o que foi escolhido
  const watchHereAndCloseTv = useCallback(async () => {
    try {
      await apiClient.releaseProfileTv(currentProfileId || 'prof_master');
    } catch {}
    setProfileOnTv(null);
    setCastState(false);
    castGateRef.current = { active: false, sessionId: null, name: null }; // já liberou: não pergunta de novo
    resumePendingPlayback();
  }, [currentProfileId, setCastState, resumePendingPlayback]);

  // Enviar para a TV: o que foi escolhido vai para a TV que já está transmitindo; este aparelho vira controle
  const sendPendingToTv = useCallback(() => {
    const tv = profileOnTv;
    setProfileOnTv(null);
    if (!tv) return;
    sendToTvNextRef.current = true;
    setCastState(true, tv.tvName, tv.sessionId);
    setCastSendRequest({ sessionId: tv.sessionId, tvName: tv.tvName, nonce: Date.now() });
    resumePendingPlayback();
  }, [profileOnTv, setCastState, resumePendingPlayback]);

  const cancelProfileOnTv = useCallback(() => {
    pendingPlaybackRef.current = null;
    setProfileOnTv(null);
  }, []);


  // Heartbeat contínuo — SOMENTE quando está realmente reproduzindo conteúdo (local ou via Cast)
  useEffect(() => {
    const isWatching = !!(currentChannel || currentMovie || (currentSeries && currentEpisode) || isCasting);
    if (!deviceId || !isWatching) return;

    const performHeartbeat = async () => {
      let contentType = 'idle';
      let contentName = '';

      if (currentChannel) {
        contentType = 'live';
        contentName = currentChannel.name || 'Canal ao Vivo';
      } else if (currentMovie) {
        contentType = 'movie';
        contentName = currentMovie.title || 'Filme VOD';
      } else if (currentSeries && currentEpisode) {
        contentType = 'series';
        contentName = `${currentSeries.title} - T${currentEpisode.seasonNumber}E${currentEpisode.episodeNumber}`;
      } else if (isCasting) {
        contentType = 'live';
        contentName = 'Transmissão Cast';
      }

      // Transmitindo via Cast: o celular fica como "ocioso" (controle remoto). Quem conta como tela em uso
      // — no Gerenciador, no pool e no limite de telas — é a própria TV, registrada pelo tv_sync.php.
      if (isCasting) {
        contentType = 'idle';
        contentName = `📺 Controlando a TV: ${castDeviceName || 'Smart TV'}`;
      }

      const activeRoute: 'fusion' | 'oracle' = currentStreamRouteRef.current || 'fusion';
      try {
        const res = await apiClient.sendHeartbeat(deviceId, currentProfileId || 'prof_master', contentType, contentName, activeRoute);
        if (res) {
          if (res.screen_kicked === true) {
            stopPlayback();
            const byProf = res.kicked_by_profile || '';
            const msg = res.message || `Seu perfil foi desconectado ${byProf ? `por ${byProf}` : 'por outro aparelho'} devido ter excedido o número de telas.`;
            setScreenKickedNotice({
              visible: true,
              byProfile: byProf,
              message: msg,
            });
          } else if (res.allowed === false) {
            const currentItem = currentChannel
              ? { type: 'channel' as const, channel: currentChannel }
              : currentMovie
              ? { type: 'movie' as const, movie: currentMovie }
              : currentSeries && currentEpisode
              ? { type: 'series' as const, series: currentSeries, episode: currentEpisode }
              : null;
            if (currentItem) {
              pendingPlaybackRef.current = currentItem;
            }
            stopPlayback();
            setScreenLimitBlocked(true);
            setScreenLimitMessage(
              res.message || 'Número total de telas atingido, aumente seu plano ou desconecte uma das telas abaixo para continuar:'
            );
            if (res.screens) setScreenLimitScreens(res.screens);
            if (res.max_screens) setScreenLimitMaxScreens(res.max_screens);
          }
        }
      } catch {}
    };

    // Disparo imediato assim que entra na reprodução ou inicia cast
    performHeartbeat();

    // Mantém pulso regular a cada 12 segundos (desalinhado do check de 8s para evitar picos simultâneos)
    const interval = setInterval(performHeartbeat, 8000);

    return () => clearInterval(interval);
  }, [deviceId, currentProfileId, currentChannel, currentMovie, currentSeries, currentEpisode, isCasting, castDeviceName, stopPlayback]);

  // Desregistrar tela ao fechar a janela / navegador (beforeunload)
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (deviceId) {
        const activeName = currentChannel?.name || currentMovie?.title || (currentSeries && currentEpisode ? `${currentSeries.title} - T${currentEpisode.seasonNumber}E${currentEpisode.episodeNumber}` : (isCasting ? 'Cast' : ''));
        navigator.sendBeacon?.(
          `/api/heartbeat.php?action=unregister`,
          JSON.stringify({ device_id: deviceId, content_name: activeName })
        );
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [deviceId, currentChannel, currentMovie, currentSeries, currentEpisode, isCasting]);

  // Category Helper Functions & Editor
  const toggleHideCategory = useCallback(
    (type: 'live' | 'movies' | 'series', categoryName: string) => {
      setCategoryConfig((prev) => {
        const section = prev[type];
        const isHidden = section.hidden.includes(categoryName);
        const hidden = isHidden
          ? section.hidden.filter((c) => c !== categoryName)
          : [...section.hidden, categoryName];
        return {
          ...prev,
          [type]: { ...section, hidden },
        };
      });
      showToast(`Categoria "${categoryName}" ${categoryConfig[type].hidden.includes(categoryName) ? 'exibida' : 'ocultada'}.`, 'info');
    },
    [categoryConfig, showToast]
  );

  const renameCategory = useCallback(
    (type: 'live' | 'movies' | 'series', oldName: string, newName: string) => {
      const trimmed = newName.trim();
      if (!trimmed || oldName === trimmed) return;
      setCategoryConfig((prev) => {
        const section = prev[type];
        return {
          ...prev,
          [type]: {
            ...section,
            renames: {
              ...section.renames,
              [oldName]: trimmed,
            },
          },
        };
      });
      showToast(`Categoria renomeada para "${trimmed}"`, 'success');
    },
    [showToast]
  );

  const reorderCategory = useCallback(
    (type: 'live' | 'movies' | 'series', categoryName: string, direction: 'up' | 'down') => {
      setCategoryConfig((prev) => {
        const section = prev[type];
        let baseCategories: string[] = [];
        if (type === 'live') {
          baseCategories = Array.from(new Set([...channels.map((c) => c.groupTitle || 'Geral'), ...section.customCategories])).filter(Boolean);
        } else if (type === 'movies') {
          baseCategories = Array.from(new Set([...movies.map((m) => m.category || 'Geral'), ...section.customCategories])).filter(Boolean);
        } else {
          baseCategories = Array.from(new Set([...series.map((s) => s.category || 'Geral'), ...section.customCategories])).filter(Boolean);
        }

        // Monta a ordem atual completa de forma estável e determinística
        let list: string[] = [];
        if (section.customOrder && section.customOrder.length > 0) {
          const set = new Set<string>();
          section.customOrder.forEach((c) => {
            if (baseCategories.includes(c) && !set.has(c)) {
              list.push(c);
              set.add(c);
            }
          });
          baseCategories.forEach((c) => {
            if (!set.has(c)) {
              list.push(c);
              set.add(c);
            }
          });
        } else {
          list = [...baseCategories];
        }

        if (!list.includes(categoryName)) {
          list.push(categoryName);
        }

        const currentIdx = list.indexOf(categoryName);
        const targetIdx = direction === 'up' ? currentIdx - 1 : currentIdx + 1;
        if (targetIdx < 0 || targetIdx >= list.length) return prev;

        const item = list.splice(currentIdx, 1)[0];
        list.splice(targetIdx, 0, item);

        return {
          ...prev,
          [type]: { ...section, customOrder: list },
        };
      });
    },
    [channels, movies, series]
  );

  const setCategoryPosition = useCallback(
    (type: 'live' | 'movies' | 'series', categoryName: string, targetIndex: number) => {
      setCategoryConfig((prev) => {
        const section = prev[type];
        let baseCategories: string[] = [];
        if (type === 'live') {
          baseCategories = Array.from(new Set([...channels.map((c) => c.groupTitle || 'Geral'), ...section.customCategories])).filter(Boolean);
        } else if (type === 'movies') {
          baseCategories = Array.from(new Set([...movies.map((m) => m.category || 'Geral'), ...section.customCategories])).filter(Boolean);
        } else {
          baseCategories = Array.from(new Set([...series.map((s) => s.category || 'Geral'), ...section.customCategories])).filter(Boolean);
        }

        // Monta a ordem atual completa de forma estável e determinística
        let list: string[] = [];
        if (section.customOrder && section.customOrder.length > 0) {
          const set = new Set<string>();
          section.customOrder.forEach((c) => {
            if (baseCategories.includes(c) && !set.has(c)) {
              list.push(c);
              set.add(c);
            }
          });
          baseCategories.forEach((c) => {
            if (!set.has(c)) {
              list.push(c);
              set.add(c);
            }
          });
        } else {
          list = [...baseCategories];
        }

        if (!list.includes(categoryName)) {
          list.push(categoryName);
        }

        const currentIdx = list.indexOf(categoryName);
        const boundedTarget = Math.max(0, Math.min(list.length - 1, targetIndex));

        const item = list.splice(currentIdx, 1)[0];
        list.splice(boundedTarget, 0, item);

        return {
          ...prev,
          [type]: { ...section, customOrder: list },
        };
      });
    },
    [channels, movies, series]
  );

  const moveCategoryToTop = useCallback(
    (type: 'live' | 'movies' | 'series', categoryName: string) => {
      setCategoryPosition(type, categoryName, 0);
    },
    [setCategoryPosition]
  );

  const moveCategoryToBottom = useCallback(
    (type: 'live' | 'movies' | 'series', categoryName: string) => {
      setCategoryPosition(type, categoryName, 99999);
    },
    [setCategoryPosition]
  );

  const createCustomCategory = useCallback(
    (type: 'live' | 'movies' | 'series', categoryName: string) => {
      const trimmed = categoryName.trim();
      if (!trimmed) return;
      setCategoryConfig((prev) => {
        const section = prev[type];
        if (section.customCategories.includes(trimmed)) return prev;
        return {
          ...prev,
          [type]: {
            ...section,
            customCategories: [...section.customCategories, trimmed],
          },
        };
      });
      showToast(`Categoria "${trimmed}" criada!`, 'success');
    },
    [showToast]
  );

  const deleteCustomCategory = useCallback(
    (type: 'live' | 'movies' | 'series', categoryName: string) => {
      const trimmed = categoryName.trim();
      if (!trimmed) return;
      setCategoryConfig((prev) => {
        const section = prev[type];
        const newCustomCategories = section.customCategories.filter((c) => c !== trimmed);
        const newCustomOrder = (section.customOrder || []).filter((c) => c !== trimmed);
        const newRenames = { ...section.renames };
        delete newRenames[trimmed];
        const newHidden = (section.hidden || []).filter((c) => c !== trimmed);
        const updated = {
          ...prev,
          [type]: {
            ...section,
            customCategories: newCustomCategories,
            customOrder: newCustomOrder,
            renames: newRenames,
            hidden: newHidden,
          },
        };
        try {
          localStorage.setItem('nexus_iptv_category_config', JSON.stringify(updated));
        } catch {}
        if (apiClient.isAuthenticated()) {
          apiClient.saveSyncData('global', 'categories', updated).catch(() => {});
          apiClient.saveSyncData('prof_master', 'categories', updated).catch(() => {});
        }
        return updated;
      });
      // Mover canais orphans dessa categoria para 'Geral'
      if (type === 'live') {
        const nextUser = userChannels.map((c) => (c.groupTitle === trimmed ? { ...c, groupTitle: 'Geral' } : c));
        const nextSystem = systemChannels.map((c) => (c.groupTitle === trimmed ? { ...c, groupTitle: 'Geral' } : c));
        setUserChannels(nextUser);
        setSystemChannels(nextSystem);
        try {
          localStorage.setItem('nexus_iptv_channels', JSON.stringify(nextUser));
          localStorage.setItem('nexus_iptv_system_channels', JSON.stringify(nextSystem));
        } catch {}

        let currentOverrides: Record<string, string> = {};
        try {
          const stored = localStorage.getItem('nexus_iptv_channel_category_overrides');
          if (stored) currentOverrides = JSON.parse(stored);
        } catch {}
        let ovChanged = false;
        Object.keys(currentOverrides).forEach((k) => {
          if (currentOverrides[k] === trimmed) {
            delete currentOverrides[k];
            ovChanged = true;
          }
        });
        if (ovChanged) {
          try {
            localStorage.setItem('nexus_iptv_channel_category_overrides', JSON.stringify(currentOverrides));
          } catch {}
          if (apiClient.isAuthenticated()) {
            apiClient.saveSyncData('global', 'channel_overrides', currentOverrides).catch(() => {});
            apiClient.saveSyncData('prof_master', 'channel_overrides', currentOverrides).catch(() => {});
          }
        }

        if (apiClient.isAuthenticated()) {
          const allMerged = [...nextSystem, ...nextUser];
          apiClient.saveGlobalCatalog({
            channels: allMerged,
            // V8.4.6: edição de canal/categoria NÃO manda filmes/séries — o servidor mantém os do banco
            playlists,
          }, playlists?.[0]?.id).catch(() => {});
        }
      }
      showToast(`Categoria "${trimmed}" excluída. Canais movidos para "Geral".`, 'success');
    },
    [showToast, userChannels, systemChannels, rawMovies, rawSeries, playlists]
  );

  const resetCategoryConfig = useCallback(
    (type: 'live' | 'movies' | 'series') => {
      setCategoryConfig((prev) => ({
        ...prev,
        [type]: { hidden: [], renames: {}, customOrder: [], customCategories: [] },
      }));
      showToast('Configuração de categorias restaurada para o padrão!', 'info');
    },
    [showToast]
  );

  const moveItemCategory = useCallback(
    (type: 'live' | 'movies' | 'series', itemId: string, targetCategory: string) => {
      if (type === 'live') {
        setChannels((prev) =>
          prev.map((c) => (c.id === itemId ? { ...c, groupTitle: targetCategory } : c))
        );
      } else if (type === 'movies') {
        setMovies((prev) =>
          prev.map((m) => (m.id === itemId ? { ...m, category: targetCategory } : m))
        );
      } else {
        setSeries((prev) =>
          prev.map((s) => (s.id === itemId ? { ...s, category: targetCategory } : s))
        );
      }
      showToast(`Item movido para "${targetCategory}"!`, 'success');
    },
    [showToast]
  );

  const bulkSetCategoryVisibility = useCallback(
    (type: 'live' | 'movies' | 'series', categoryNames: string[], hide: boolean) => {
      setCategoryConfig((prev) => {
        const section = prev[type];
        let newHidden = [...section.hidden];
        if (hide) {
          categoryNames.forEach((name) => {
            if (!newHidden.includes(name)) newHidden.push(name);
          });
        } else {
          newHidden = newHidden.filter((name) => !categoryNames.includes(name));
        }
        return {
          ...prev,
          [type]: { ...section, hidden: newHidden },
        };
      });
      showToast(
        hide
          ? `${categoryNames.length} categorias ocultadas.`
          : `${categoryNames.length} categorias visíveis.`,
        'info'
      );
    },
    [showToast]
  );

  const saveCategoryGridToCloud = useCallback(async (): Promise<boolean> => {
    try {
      try {
        localStorage.setItem('nexus_iptv_category_config', JSON.stringify(categoryConfig));
      } catch {}

      if (apiClient.isAuthenticated()) {
        const promises: Promise<any>[] = [
          apiClient.saveSyncData('global', 'categories', categoryConfig),
          apiClient.saveSyncData('prof_master', 'categories', categoryConfig),
        ];

        if (currentProfileId && currentProfileId !== 'prof_master') {
          promises.push(apiClient.saveSyncData(currentProfileId, 'categories', categoryConfig));
        }

        for (const p of profiles) {
          if (p.id && p.id !== 'prof_master' && p.id !== currentProfileId) {
            promises.push(apiClient.saveSyncData(p.id, 'categories', categoryConfig));
          }
        }

        await Promise.all(promises);
      }
      return true;
    } catch (e) {
      console.warn('Erro ao salvar grade de categorias na nuvem:', e);
      return false;
    }
  }, [categoryConfig, currentProfileId, profiles]);

  // Raw & Processed Categories
  const rawChannelCategories = useMemo(() => {
    return Array.from(
      new Set([
        ...channels.map((c) => c.groupTitle || 'Geral'),
        ...(categoryConfig?.live?.customCategories || []),
      ])
    ).filter(Boolean);
  }, [channels, categoryConfig?.live?.customCategories]);

  const channelCategories = useMemo(() => {
    const section = categoryConfig?.live || { hidden: [], renames: {}, customOrder: [] };
    const hiddenSet = new Set(section.hidden);
    const visibleCategories = rawChannelCategories.filter((c) => !hiddenSet.has(c));

    let ordered: string[] = [];
    if (section.customOrder && section.customOrder.length > 0) {
      const customSet = new Set<string>();
      section.customOrder.forEach((cat) => {
        if (visibleCategories.includes(cat) && !customSet.has(cat)) {
          ordered.push(cat);
          customSet.add(cat);
        }
      });
      visibleCategories.forEach((cat) => {
        if (!customSet.has(cat)) {
          ordered.push(cat);
          customSet.add(cat);
        }
      });
    } else {
      ordered = [...visibleCategories];
    }

    const mapped = ordered.map((c) => section.renames[c] || c);
    return ['Favoritos', ...mapped];
  }, [rawChannelCategories, categoryConfig.live]);

  const rawMovieCategories = useMemo(() => {
    return Array.from(
      new Set([
        ...movies.map((m) => m.category || 'Geral'),
        ...(categoryConfig?.movies?.customCategories || []),
      ])
    ).filter(Boolean);
  }, [movies, categoryConfig?.movies?.customCategories]);

  const movieCategories = useMemo(() => {
    const section = categoryConfig?.movies || { hidden: [], renames: {}, customOrder: [] };
    const hiddenSet = new Set(section.hidden);
    const visibleCategories = rawMovieCategories.filter((c) => !hiddenSet.has(c));

    let ordered: string[] = [];
    if (section.customOrder && section.customOrder.length > 0) {
      const customSet = new Set<string>();
      section.customOrder.forEach((cat) => {
        if (visibleCategories.includes(cat) && !customSet.has(cat)) {
          ordered.push(cat);
          customSet.add(cat);
        }
      });
      visibleCategories.forEach((cat) => {
        if (!customSet.has(cat)) {
          ordered.push(cat);
          customSet.add(cat);
        }
      });
    } else {
      ordered = [...visibleCategories];
    }

    const mapped = ordered.map((c) => section.renames[c] || c);
    return ['Continuar Assistindo', 'Favoritos', ...mapped];
  }, [rawMovieCategories, categoryConfig.movies]);

  const rawSeriesCategories = useMemo(() => {
    return Array.from(
      new Set([
        ...series.map((s) => s.category || 'Geral'),
        ...(categoryConfig?.series?.customCategories || []),
      ])
    ).filter(Boolean);
  }, [series, categoryConfig?.series?.customCategories]);

  const seriesCategories = useMemo(() => {
    const section = categoryConfig?.series || { hidden: [], renames: {}, customOrder: [] };
    const hiddenSet = new Set(section.hidden);
    const visibleCategories = rawSeriesCategories.filter((c) => !hiddenSet.has(c));

    let ordered: string[] = [];
    if (section.customOrder && section.customOrder.length > 0) {
      const customSet = new Set<string>();
      section.customOrder.forEach((cat) => {
        if (visibleCategories.includes(cat) && !customSet.has(cat)) {
          ordered.push(cat);
          customSet.add(cat);
        }
      });
      visibleCategories.forEach((cat) => {
        if (!customSet.has(cat)) {
          ordered.push(cat);
          customSet.add(cat);
        }
      });
    } else {
      ordered = [...visibleCategories];
    }

    const mapped = ordered.map((c) => section.renames[c] || c);
    return ['Continuar Assistindo', 'Favoritos', ...mapped];
  }, [rawSeriesCategories, categoryConfig.series]);

  // Profile methods
  const selectProfile = useCallback(
    async (profile: UserProfile, pinInput?: string): Promise<boolean> => {
      const hasActivePin = !!(profile.hasPin && profile.pin && profile.pin.trim().length > 0);
      if (hasActivePin) {
        const expectedPin = profile.pin!.trim();
        if (
          pinInput !== expectedPin &&
          pinInput !== parentalConfig.masterPin
        ) {
          showToast('PIN incorreto!', 'error');
          return false;
        }
      }

      // 1. Reivindica o perfil no servidor MySQL em segundo plano sem bloquear a interface da TV
      if (deviceId && authUser) {
        const userAgentStr = typeof navigator !== 'undefined' ? navigator.userAgent : 'App';
        apiClient.claimProfile?.(profile.id, deviceId, userAgentStr).catch((e) => {
          console.warn('Aviso ao reivindicar perfil no servidor:', e);
        });
      }

      // 2. Define o perfil no estado e LocalStorage de forma atômica e instantânea
      setIsProfileKicked(false);
      setCurrentProfileId(profile.id);
      try {
        localStorage.setItem('nexus_iptv_active_profile_id', profile.id);
      } catch {}
      setIsParentalUnlocked(false);

      // 3. Sincronização em Segundo Plano (Background): não bloqueia a entrada imediata no perfil
      scheduleWhenIdle(() => {
        syncFromCloud({ profilesOnly: false }).catch((syncErr) => {
          console.warn('Aviso na sincronização de background:', syncErr);
        });
      });

      showToast(`Perfil: ${profile.name}`, 'success');
      return true;
    },
    [parentalConfig.masterPin, showToast, deviceId, authUser, syncFromCloud]
  );

  const createProfile = useCallback(
    async (newProfileData: Omit<UserProfile, 'id' | 'favorites' | 'watchHistory'>) => {
      const maxAllowed = authUser?.max_profiles || 5;
      if (profiles.length >= maxAllowed) {
        showToast(`Limite máximo de ${maxAllowed} perfil(is) do seu plano atingido. Exclua um perfil para adicionar outro.`, 'warning');
        return;
      }
      const newProf: UserProfile = {
        ...newProfileData,
        id: `prof_${Date.now()}_${Math.random().toString(36).substring(2, 6)}`,
        favorites: [],
        favoriteCategories: {},
        watchHistory: [],
        watchedIds: [],
      };
      
      const updated = [...profiles, newProf];
      setProfiles(updated);

      if (authUser?.id) {
        try {
          localStorage.setItem(`nexus_iptv_profiles_${authUser.id}`, JSON.stringify(updated));
        } catch {}
      }

      if (apiClient.isAuthenticated()) {
        try {
          if (cloudSyncTimeoutRef.current) {
            clearTimeout(cloudSyncTimeoutRef.current);
          }
          lastCloudDataRef.current = '';
          await apiClient.saveProfiles(updated);
        } catch (e) {
          console.warn('Erro ao salvar novo perfil no MySQL:', e);
        }
      }

      showToast(`Perfil "${newProf.name}" criado com sucesso!`, 'success');
    },
    [profiles, authUser?.id, showToast]
  );

  const updateProfile = useCallback(
    async (id: string, updates: Partial<Omit<UserProfile, 'id'>>) => {
      const updated = profiles.map((p) => (p.id === id ? { ...p, ...updates } : p));
      setProfiles(updated);

      if (authUser?.id) {
        try {
          localStorage.setItem(`nexus_iptv_profiles_${authUser.id}`, JSON.stringify(updated));
        } catch {}
      }

      if (apiClient.isAuthenticated()) {
        try {
          if (cloudSyncTimeoutRef.current) {
            clearTimeout(cloudSyncTimeoutRef.current);
          }
          lastCloudDataRef.current = '';
          await apiClient.saveProfiles(updated);
        } catch (e) {
          console.warn('Erro ao salvar atualização do perfil no MySQL:', e);
        }
      }

      showToast('Perfil atualizado com sucesso!', 'success');
    },
    [profiles, authUser?.id, showToast]
  );

  const deleteProfile = useCallback(
    async (id: string) => {
      if (profiles.length <= 1) {
        showToast('Não é possível excluir o único perfil existente.', 'warning');
        return;
      }
      const remaining = profiles.filter((p) => p.id !== id);
      setProfiles(remaining);
      
      if (authUser?.id) {
        try {
          localStorage.setItem(`nexus_iptv_profiles_${authUser.id}`, JSON.stringify(remaining));
        } catch (e) {
          console.warn('LocalStorage save error on deleting profile:', e);
        }
      }

      if (currentProfileId === id) {
        setCurrentProfileId(remaining[0].id);
        try {
          localStorage.setItem('nexus_iptv_active_profile_id', remaining[0].id);
        } catch {}
      }

      if (apiClient.isAuthenticated()) {
        try {
          if (cloudSyncTimeoutRef.current) {
            clearTimeout(cloudSyncTimeoutRef.current);
          }
          lastCloudDataRef.current = '';
          await apiClient.deleteProfile(id);
          await apiClient.saveProfiles(remaining);
          await apiClient.saveSyncData(id, 'history', []);
          await apiClient.saveSyncData(id, 'favorites', [], Date.now());
        } catch (e) {
          console.warn('Error syncing profile deletion to cloud:', e);
        }
      }
      showToast('Perfil e todos os seus favoritos/histórico foram removidos.', 'info');
    },
    [profiles, authUser?.id, currentProfileId, showToast]
  );

  // Lista de perfis SEMPRE atual. Favoritar e salvar histórico partiam da cópia que a função tinha quando
  // foi criada: dois favoritos seguidos faziam o segundo apagar o primeiro, e um salvamento de histórico
  // (ex.: o player nativo a cada 60s) podia gravar por cima com a lista antiga e desfazer um favorito.
  const latestProfilesRef = useRef(profiles);
  latestProfilesRef.current = profiles;

  const toggleFavorite = useCallback(
    (id: string) => {
      const baseProfiles = latestProfilesRef.current;
      const activeProfId = currentProfileId || baseProfiles[0]?.id || 'prof-1';
      const currentProf = baseProfiles.find((p) => p.id === activeProfId) || baseProfiles[0];
      const currentFavs = currentProf?.favorites || [];
      const exists = currentFavs.includes(id);
      const targetFavorites = exists ? currentFavs.filter((f) => f !== id) : [...currentFavs, id];
      const favoritesUpdatedAt = Date.now(); // carimbo: o servidor ignora envios mais velhos que este

      const updatedProfiles = baseProfiles.map((p) => {
        if (p.id === activeProfId) {
          return { ...p, favorites: targetFavorites, favoritesUpdatedAt };
        }
        return p;
      });

      latestProfilesRef.current = updatedProfiles;
      setProfiles(updatedProfiles);
      if (authUser?.id) {
        try {
          localStorage.setItem(`nexus_iptv_profiles_${authUser.id}`, JSON.stringify(updatedProfiles));
        } catch {}
      }

      showToast(exists ? 'Removido dos favoritos' : 'Adicionado aos favoritos ⭐', 'info');

      // Envio imediato e garantido ao MySQL da HostGator
      if (apiClient.isAuthenticated()) {
        apiClient.saveSyncData(activeProfId, 'favorites', targetFavorites, favoritesUpdatedAt).catch(() => {});
      }
    },
    [currentProfileId, authUser?.id, showToast]
  );

  const isFavorite = useCallback(
    (id: string) => {
      return currentProfile.favorites?.includes(id) || false;
    },
    [currentProfile]
  );

  const toggleCategoryFavorite = useCallback(
    (type: 'live' | 'movies' | 'series', categoryName: string) => {
      let updatedProfiles: UserProfile[] = [];
      setProfiles((prev) => {
        updatedProfiles = prev.map((p) => {
          if (p.id === currentProfileId) {
            const currentFavCats = p.favoriteCategories || {};
            const list = currentFavCats[type] || [];
            const exists = list.includes(categoryName);
            const updatedList = exists
              ? list.filter((c) => c !== categoryName)
              : [...list, categoryName];

            return {
              ...p,
              favoriteCategories: {
                ...currentFavCats,
                [type]: updatedList,
              },
            };
          }
          return p;
        });

        try {
          localStorage.setItem('nexus_iptv_profiles', JSON.stringify(updatedProfiles));
        } catch {}

        return updatedProfiles;
      });

      const myP = updatedProfiles.find((p) => p.id === currentProfileId);
      const isNowFav = myP?.favoriteCategories?.[type]?.includes(categoryName);
      showToast(
        !isNowFav
          ? `Categoria "${categoryName}" removida dos favoritos`
          : `Categoria "${categoryName}" favoritada ⭐`,
        'info'
      );

      if (apiClient.isAuthenticated() && myP) {
        apiClient.saveProfiles(updatedProfiles).catch(() => {});
        apiClient.saveSyncData(currentProfileId, 'favorite_categories', myP.favoriteCategories || {}).catch(() => {});
      }
    },
    [currentProfileId, showToast]
  );

  const isCategoryFavorite = useCallback(
    (type: 'live' | 'movies' | 'series', categoryName: string) => {
      return currentProfile.favoriteCategories?.[type]?.includes(categoryName) || false;
    },
    [currentProfile]
  );

  const updateWatchHistory = useCallback(
    (contentId: string, type: ContentType, watchedSeconds: number, totalSeconds: number, seriesId?: string) => {
      const baseProfiles = latestProfilesRef.current;
      const activeProfId = currentProfileId || baseProfiles[0]?.id || 'prof-1';
      const currentProf = baseProfiles.find((p) => p.id === activeProfId) || baseProfiles[0];
      const existingHist = currentProf?.watchHistory || [];

      const existingIndex = existingHist.findIndex((h) => h.contentId === contentId);
      const prevEntry: any = existingIndex >= 0 ? existingHist[existingIndex] : undefined;
      // Episódio: guarda temporada/episódio/título junto — o card do "Continuar Assistindo" usa direto, sem
      // precisar carregar a lista de episódios da série (antes mostrava "T1 E1" até os episódios carregarem).
      const epMeta =
        type === 'series' && currentEpisode && currentEpisode.id === contentId
          ? {
              seasonNumber: currentEpisode.seasonNumber,
              episodeNumber: currentEpisode.episodeNumber,
              episodeTitle: currentEpisode.title,
            }
          : prevEntry && prevEntry.seasonNumber
            ? {
                seasonNumber: prevEntry.seasonNumber,
                episodeNumber: prevEntry.episodeNumber,
                episodeTitle: prevEntry.episodeTitle,
              }
            : {};
      const entry = {
        contentId,
        seriesId: seriesId || (type === 'series' && currentSeries ? currentSeries.id : undefined),
        type,
        watchedSeconds,
        totalSeconds,
        updatedAt: new Date().toISOString(),
        ...epMeta,
      };

      let newHistory = [...existingHist];
      if (existingIndex >= 0) {
        newHistory[existingIndex] = entry;
      } else {
        newHistory = [entry, ...newHistory];
      }
      newHistory = newHistory.slice(0, 100);

      const updatedProfiles = baseProfiles.map((p) => {
        if (p.id === activeProfId) {
          return { ...p, watchHistory: newHistory };
        }
        return p;
      });

      latestProfilesRef.current = updatedProfiles;
      setProfiles(updatedProfiles);
      if (authUser?.id) {
        try {
          localStorage.setItem(`nexus_iptv_profiles_${authUser.id}`, JSON.stringify(updatedProfiles));
        } catch {}
      }

      // Sincronização imediata com o MySQL da HostGator
      if (apiClient.isAuthenticated() && newHistory.length > 0) {
        apiClient.saveSyncData(activeProfId, 'history', newHistory).catch(() => {});
      }
    },
    [currentProfileId, currentSeries, currentEpisode, authUser?.id]
  );

  const continueWatchingItems = useMemo(() => {
    return currentProfile.watchHistory || [];
  }, [currentProfile]);

  const getContentProgress = useCallback(
    (contentId: string) => {
      const item = currentProfile.watchHistory?.find((h) => h.contentId === contentId);
      if (!item || !item.watchedSeconds || item.watchedSeconds < 5) return null;
      const total = item.totalSeconds || 0;
      const percentage = total > 0 ? Math.min(100, Math.round((item.watchedSeconds / total) * 100)) : 0;
      const isCompleted = percentage >= 95;
      const remainingSecs = Math.max(0, total - item.watchedSeconds);

      return {
        watchedSeconds: item.watchedSeconds,
        totalSeconds: total,
        percentage,
        isCompleted,
        updatedAt: item.updatedAt,
        formattedProgress: formatSeconds(item.watchedSeconds),
        formattedTotal: formatSeconds(total),
        formattedRemaining: formatDurationHuman(remainingSecs),
      };
    },
    [currentProfile]
  );

  const getSeriesProgress = useCallback(
    (seriesItem: Series) => {
      const historyList = (currentProfile.watchHistory || [])
        .filter((h) => {
          if (h.seriesId && h.seriesId === seriesItem.id) return true;
          if (h.contentId === seriesItem.id) return true;
          if (seriesItem.episodes && seriesItem.episodes.some((e) => e.id === h.contentId)) return true;
          const cleanId = seriesItem.id.replace('ser-xtream-', '').replace('ser-', '');
          if (cleanId && h.contentId.includes(cleanId)) return true;
          if (seriesItem.xtreamIds && seriesItem.xtreamIds.some((x) => h.contentId.includes(String(x.id)))) return true;
          return false;
        })
        .sort((a, b) => new Date(b.updatedAt || 0).getTime() - new Date(a.updatedAt || 0).getTime());

      if (historyList.length === 0) return null;
      const latestHistory = historyList[0];
      // Temporada/episódio gravados no próprio histórico (não depende de carregar os episódios da série)
      const lh: any = latestHistory;
      const metaEp = lh.seasonNumber
        ? {
            id: latestHistory.contentId,
            seasonNumber: lh.seasonNumber,
            episodeNumber: lh.episodeNumber || 1,
            title: lh.episodeTitle || 'Episódio em Andamento',
          }
        : null;
      const lastEp =
        (seriesItem.episodes && seriesItem.episodes.find((e) => e.id === latestHistory.contentId)) ||
        metaEp ||
        { id: latestHistory.contentId, seasonNumber: 1, episodeNumber: 1, title: 'Episódio em Andamento' };
      const total = latestHistory.totalSeconds || 0;
      const percentage = total > 0 ? Math.min(100, Math.round((latestHistory.watchedSeconds / total) * 100)) : 0;
      const remainingSecs = Math.max(0, total - latestHistory.watchedSeconds);

      return {
        lastWatchedEpisode: lastEp as any,
        lastEpisode: lastEp as any,
        seasonNumber: lastEp.seasonNumber || 1,
        episodeNumber: lastEp.episodeNumber || 1,
        episodeProgress: {
          watchedSeconds: latestHistory.watchedSeconds,
          totalSeconds: total,
          percentage,
          formattedProgress: formatSeconds(latestHistory.watchedSeconds),
          formattedTotal: formatSeconds(total),
          formattedRemaining: formatDurationHuman(remainingSecs),
        },
        watchedSeconds: latestHistory.watchedSeconds,
        totalSeconds: total,
        percentage,
        totalWatchedEpisodes: historyList.length,
        isCompleted: percentage >= 95,
        updatedAt: latestHistory.updatedAt,
        formattedProgress: formatSeconds(latestHistory.watchedSeconds),
      };
    },
    [currentProfile]
  );

  const isContentWatched = useCallback(
    (contentId: string) => {
      if (!currentProfile || !contentId) return false;
      const watched = currentProfile.watchedIds || [];
      if (watched.includes(contentId)) return true;

      // Correspondência estrita exata com ou sem prefixo padrão
      const cleanTarget = contentId.replace(/^(ep-xtream-|ep-|ser-xtream-|ser-|mov-xtream-|mov-)/, '');
      if (cleanTarget) {
        if (
          watched.includes(cleanTarget) ||
          watched.includes(`ep-${cleanTarget}`) ||
          watched.includes(`ep-xtream-${cleanTarget}`) ||
          watched.includes(`ser-${cleanTarget}`) ||
          watched.includes(`ser-xtream-${cleanTarget}`) ||
          watched.includes(`mov-${cleanTarget}`) ||
          watched.includes(`mov-xtream-${cleanTarget}`)
        ) {
          return true;
        }
      }

      const history = currentProfile.watchHistory?.find((h) => {
        if (h.contentId === contentId) return true;
        if (cleanTarget) {
          const cleanH = h.contentId.replace(/^(ep-xtream-|ep-|ser-xtream-|ser-|mov-xtream-|mov-)/, '');
          return cleanH === cleanTarget;
        }
        return false;
      });

      if (history && history.totalSeconds > 0) {
        return (history.watchedSeconds / history.totalSeconds) >= 0.9;
      }
      return false;
    },
    [currentProfile]
  );

  const toggleWatched = useCallback(
    (
      contentIdOrIds: string | string[],
      isSeries?: boolean,
      seriesEpisodes?: Episode[],
      forceState?: boolean,
      options?: { silent?: boolean; seriesId?: string }
    ) => {
      const ids = Array.isArray(contentIdOrIds) ? contentIdOrIds : [contentIdOrIds];
      if (ids.length === 0) return;

      const firstId = ids[0];
      const isCurrentlyWatched = forceState !== undefined ? !forceState : isContentWatched(firstId);
      const shouldMark = forceState !== undefined ? forceState : !isCurrentlyWatched;

      const episodeIds = seriesEpisodes ? seriesEpisodes.map((e) => e.id) : [];
      const allTargetIds = new Set([...ids, ...episodeIds]);
      const strip = (v: string) => String(v || '').replace(/^(ep-xtream-|ep-|ser-xtream-|ser-|mov-xtream-|mov-)/, '');

      // V8.2.3: calcula a partir da lista de perfis MAIS ATUAL e de forma síncrona
      const base = latestProfilesRef.current;
      const activeProfId = currentProfileId || base[0]?.id || 'prof-1';
      const prof = base.find((p) => p.id === activeProfId);
      if (!prof) return;

      const currentWatched = new Set<string>(prof.watchedIds || []);
      let newHistory = [...(prof.watchHistory || [])];

      if (!shouldMark) {
        // Desmarcar como assistido
        const cleanTargets = new Set(Array.from(allTargetIds).map(strip).filter(Boolean));
        allTargetIds.forEach((id) => currentWatched.delete(id));
        Array.from(currentWatched).forEach((w) => {
          if (cleanTargets.has(strip(w))) currentWatched.delete(w);
        });
        newHistory = newHistory.filter((h) => !allTargetIds.has(h.contentId) && !cleanTargets.has(strip(h.contentId)));
      } else {
        // Marcar como assistido — preserva o que o histórico já sabe
        const knownEpisodes = new Map<string, any>();
        const sources = [currentSeries, ...(seriesRef.current || [])].filter(Boolean) as Series[];
        for (const s of sources) {
          for (const ep of s.episodes || []) {
            if (allTargetIds.has(ep.id) && !knownEpisodes.has(ep.id)) knownEpisodes.set(ep.id, { ep, seriesId: s.id });
          }
        }
        const nowIso = new Date().toISOString();
        allTargetIds.forEach((id) => {
          currentWatched.add(id);
          const idx = newHistory.findIndex((h) => h.contentId === id);
          const prev: any = idx >= 0 ? newHistory[idx] : null;
          const known = knownEpisodes.get(id);
          const isEpisode = !!known || prev?.type === 'series' || (!!options?.seriesId && id !== options.seriesId);
          const total = prev?.totalSeconds && prev.totalSeconds > 0 ? prev.totalSeconds : 3600;
          const entry: any = {
            ...(prev || {}),
            contentId: id,
            seriesId: isEpisode
              ? prev?.seriesId || known?.seriesId || options?.seriesId
              : isSeries
                ? firstId
                : prev?.seriesId,
            type: (isEpisode || isSeries ? 'series' : prev?.type || 'movie') as ContentType,
            watchedSeconds: total,
            totalSeconds: total,
            updatedAt: nowIso,
          };
          if (known?.ep && !entry.seasonNumber) {
            entry.seasonNumber = known.ep.seasonNumber;
            entry.episodeNumber = known.ep.episodeNumber;
            entry.episodeTitle = known.ep.title;
          }
          if (idx >= 0) newHistory[idx] = entry;
          else newHistory.unshift(entry);
        });
        newHistory = newHistory.slice(0, 100);
      }

      const updatedWatchedIds = Array.from(currentWatched);
      const nextProfiles = base.map((p) =>
        p.id === activeProfId ? { ...p, watchedIds: updatedWatchedIds, watchHistory: newHistory } : p
      );
      latestProfilesRef.current = nextProfiles;
      setProfiles(nextProfiles);
      try {
        if (authUser?.id) localStorage.setItem(`nexus_iptv_profiles_${authUser.id}`, JSON.stringify(nextProfiles));
        localStorage.setItem('nexus_iptv_profiles', JSON.stringify(nextProfiles));
      } catch (e) {
        console.warn('LocalStorage error saving watched status:', e);
      }

      // Servidor: sempre com as listas já calculadas (nunca vazias por engano)
      if (apiClient.isAuthenticated() && activeProfId) {
        apiClient.saveSyncData(activeProfId, 'watched_ids', updatedWatchedIds).catch(() => {});
        apiClient.saveSyncData(activeProfId, 'history', newHistory).catch(() => {});
        apiClient.saveProfiles(nextProfiles).catch(() => {});
      }

      if (!options?.silent) {
        showToast(
          !shouldMark ? 'Marcado como não assistido' : 'Marcado como já assistido! ✔',
          'success'
        );
      }
    },
    [currentProfileId, isContentWatched, showToast, authUser?.id, currentSeries]
  );

  // Série marcada como assistida que ganhou temporada/episódio novo no provedor (last_modified mais novo que
  // a última vez que o perfil assistiu): retira a marca — ela volta ao "Continuar Assistindo" e deixa de
  // aparecer como assistida. Vale também para séries com episódios semanais.
  const unmarkedUpdatedSeriesRef = useRef<Set<string>>(new Set());
  const hasCheckedUpdatedSeriesRef = useRef<Record<string, boolean>>({});
  useEffect(() => {
    if (!currentProfileId || series.length === 0) return;
    if (hasCheckedUpdatedSeriesRef.current[currentProfileId]) return;
    hasCheckedUpdatedSeriesRef.current[currentProfileId] = true;
    const prof = latestProfilesRef.current.find((p) => p.id === currentProfileId);
    const watched = new Set(prof?.watchedIds || []);
    const history = prof?.watchHistory || [];
    if (watched.size === 0 || history.length === 0) return;
    for (const s of series) {
      if (!watched.has(s.id) || unmarkedUpdatedSeriesRef.current.has(s.id)) continue;
      if (seriesUpdatedSinceLastWatch(s, history)) {
        unmarkedUpdatedSeriesRef.current.add(s.id);
        toggleWatched(s.id, false, undefined, false, { silent: true });
      }
    }
  }, [currentProfileId, series.length, toggleWatched]);

  const clearContentProgress = useCallback(
    async (contentId: string, seriesTitle?: string, episodeIds?: string[]) => {
      const epSet = new Set(episodeIds || []);
      const strip = (v: string) => String(v || '').replace(/^(ep-xtream-|ep-|ser-xtream-|ser-|mov-xtream-|mov-)/, '').trim();
      const cleanTarget = strip(contentId);

      // Parte da lista de perfis MAIS ATUAL (antes: calculava dentro de um setProfiles que o React pode adiar)
      const base = latestProfilesRef.current;
      const activeProfId = currentProfileId || base[0]?.id || 'prof-1';
      const prof = base.find((p) => p.id === activeProfId);
      if (!prof) return;

      const oldWatched = prof.watchedIds || [];
      const oldHistory = prof.watchHistory || [];
      const newWatched = oldWatched.filter((id) => {
        if (id === contentId || epSet.has(id)) return false;
        if (cleanTarget) {
          const cleanId = strip(id);
          if (cleanId === cleanTarget || (cleanTarget.length >= 4 && cleanId.includes(cleanTarget))) return false;
        }
        return true;
      });
      const newHistory = oldHistory.filter((h) => {
        if (!h || !h.contentId) return false;
        if (h.contentId === contentId || epSet.has(h.contentId)) return false;
        if (h.seriesId === contentId) return false;
        if (cleanTarget) {
          const cleanH = strip(h.contentId);
          if (cleanH === cleanTarget || (cleanTarget.length >= 4 && cleanH.includes(cleanTarget))) return false;
          if (h.seriesId) {
            const cleanSer = strip(h.seriesId);
            if (cleanSer === cleanTarget || (cleanTarget.length >= 4 && cleanSer.includes(cleanTarget))) return false;
          }
        }
        if (seriesTitle && (h as any).title && String((h as any).title).toLowerCase().includes(seriesTitle.toLowerCase())) return false;
        if (seriesTitle && (h as any).seriesTitle && String((h as any).seriesTitle).toLowerCase().includes(seriesTitle.toLowerCase())) return false;
        return true;
      });

      // Tudo o que saiu do histórico/assistidos AQUI precisa sair também no servidor — inclusive cada EPISÓDIO.
      // Antes ia só a lápide da SÉRIE: o servidor guarda o histórico por episódio, os episódios ficavam lá e,
      // ao reiniciar o app, a série voltava para o "Continuar Assistindo".
      const removedHistoryIds = oldHistory.filter((h) => h && h.contentId && !newHistory.includes(h)).map((h) => h.contentId);
      const removedWatchedIds = oldWatched.filter((id) => !newWatched.includes(id));

      const updatedProfiles = base.map((p) =>
        p.id === activeProfId ? { ...p, watchedIds: newWatched, watchHistory: newHistory } : p
      );
      latestProfilesRef.current = updatedProfiles;
      setProfiles(updatedProfiles);
      try {
        if (authUser?.id) {
          localStorage.setItem(`nexus_iptv_profiles_${authUser.id}`, JSON.stringify(updatedProfiles));
        }
        localStorage.setItem('nexus_iptv_profiles', JSON.stringify(updatedProfiles));
      } catch {}

      if (apiClient.isAuthenticated()) {
        try {
          const now = new Date().toISOString();
          const tombIds = Array.from(new Set([contentId, ...removedHistoryIds]));
          // B2: uma lápide (removed: true) para CADA item removido — o servidor guarda e não devolve mais
          await apiClient.saveSyncData(
            activeProfId,
            'history',
            tombIds.map((id) => ({ contentId: id, removed: true, updatedAt: now }))
          );
          const idsToDrop = Array.from(new Set([contentId, ...(episodeIds || []), ...removedWatchedIds]));
          await apiClient.removeWatchedIds(activeProfId, idsToDrop);
        } catch (err) {
          console.warn('[CloudSync] Erro ao sincronizar exclusão do histórico:', err);
        }
      }

      showToast('Item removido de Continuar Assistindo', 'info');
    },
    [currentProfileId, authUser?.id, showToast]
  );

  const clearAllWatchHistory = useCallback(async () => {
    const clearedProfiles = profiles.map((p) => ({ ...p, watchHistory: [], watchedIds: [] }));
    setProfiles(clearedProfiles);

    if (authUser?.id) {
      try {
        localStorage.setItem(`nexus_iptv_profiles_${authUser.id}`, JSON.stringify(clearedProfiles));
      } catch (e) {
        console.warn('LocalStorage save error on clearing watch history:', e);
      }
    }

    if (apiClient.isAuthenticated() && clearedProfiles.length > 0) {
      try {
        setCloudSyncStatus('syncing');
        for (const p of clearedProfiles) {
          // B2: Ação explícita de limpeza atômica no MySQL
          await apiClient.clearHistory(p.id, true);
        }
        setCloudSyncStatus('synced');
        setLastCloudSyncTime(new Date().toLocaleTimeString('pt-BR'));
      } catch (e) {
        console.warn('Error syncing history clear to cloud:', e);
        setCloudSyncStatus('offline');
      }
    }

    showToast('Histórico de exibição e continuar assistindo zerados com sucesso!', 'success');
  }, [profiles, authUser?.id, showToast]);

  // B4 [SERVIDOR + STICK + WEB]: Polling leve de get_sync_data ao voltar ao app ou abrir a Home (máx 1x/min)
  const lastSyncDataPollRef = useRef(0);
  const refreshLightweightSyncData = useCallback(async () => {
    if (!apiClient.isAuthenticated()) return;
    const now = Date.now();
    if (now - lastSyncDataPollRef.current < 60000) return;
    lastSyncDataPollRef.current = now;

    const activeProfId = currentProfileId || profiles[0]?.id;
    if (!activeProfId) return;

    try {
      const res = await apiClient.getSyncData(activeProfId);
      if (res && res.success) {
        setProfiles((prev) => prev.map((p) => {
          if (p.id === activeProfId) {
            const remoteHist = Array.isArray(res.history) ? res.history : (p.watchHistory || []);
            const remoteWatched = Array.isArray(res.watchedIds) ? res.watchedIds : (Array.isArray(res.watched_ids) ? res.watched_ids : (p.watchedIds || []));
            return {
              ...p,
              watchHistory: remoteHist,
              watchedIds: remoteWatched,
            };
          }
          return p;
        }));
      }
    } catch (err) {
      console.warn('[SyncData] Poll error:', err);
    }
  }, [currentProfileId, profiles]);

  useEffect(() => {
    const handleVisibilityOrFocus = () => {
      if (typeof document !== 'undefined' && document.visibilityState === 'visible') {
        refreshLightweightSyncData();
      }
    };
    window.addEventListener('focus', handleVisibilityOrFocus);
    document.addEventListener('visibilitychange', handleVisibilityOrFocus);
    return () => {
      window.removeEventListener('focus', handleVisibilityOrFocus);
      document.removeEventListener('visibilitychange', handleVisibilityOrFocus);
    };
  }, [refreshLightweightSyncData]);

  // Playlist Management
  const addM3UPlaylist = useCallback(
    async (name: string, contentOrUrl: string, isUrl: boolean): Promise<boolean> => {
      try {
        let content = contentOrUrl;
        if (isUrl) {
          showToast('Baixando lista M3U...', 'info');
          try {
            const resp = await fetch(contentOrUrl);
            if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
            content = await resp.text();
          } catch {
            try {
              // Retry via built-in proxy
              const proxyResp = await fetch(`/api/proxy?url=${encodeURIComponent(contentOrUrl)}`);
              if (!proxyResp.ok) throw new Error(`Proxy HTTP ${proxyResp.status}`);
              content = await proxyResp.text();
            } catch {
              showToast('Não foi possível ler a lista completa do servidor. Adicionando como link direto.', 'warning');
              content = `#EXTM3U\n#EXTINF:-1 tvg-id="stream-1" group-title="Lista Importada",${name} Stream 1\n${contentOrUrl}`;
            }
          }
        }

        const parsed = await parseM3U(content);
        const newPlId = `pl-${Date.now()}`;
        const channelsWithPl = parsed.channels.map((c) => ({ ...c, playlistId: newPlId }));
        const moviesWithPl = parsed.movies.map((m) => ({ ...m, playlistId: newPlId }));
        const seriesWithPl = parsed.series.map((s) => ({ ...s, playlistId: newPlId }));

        if (channelsWithPl.length === 0 && moviesWithPl.length === 0 && seriesWithPl.length === 0) {
          // If plain single stream link
          if (contentOrUrl.startsWith('http')) {
            const singleChannel: Channel = {
              id: `ch-${Date.now()}`,
              num: channels.length + 1,
              name: name || 'Canal M3U',
              logo: 'https://images.unsplash.com/photo-1585829365295-ab7cd400c167?w=128',
              streamUrl: contentOrUrl,
              groupTitle: 'Importados',
              resolution: 'FHD',
              rating: 'L',
              playlistId: newPlId,
            };
            setChannels((prev) => [...prev, singleChannel]);
          } else {
            showToast('Nenhum canal válido encontrado na lista.', 'error');
            return false;
          }
        } else {
          if (channelsWithPl.length > 0) setChannels((prev) => [...prev, ...channelsWithPl]);
          if (moviesWithPl.length > 0) setMovies((prev) => [...prev, ...moviesWithPl]);
          if (seriesWithPl.length > 0) setSeries((prev) => [...prev, ...seriesWithPl]);
        }

        const newPl: PlaylistSource = {
          id: newPlId,
          name,
          type: isUrl ? 'm3u_url' : 'm3u_file',
          url: isUrl ? contentOrUrl : undefined,
          autoUpdate: true,
          lastUpdated: new Date().toLocaleDateString('pt-BR'),
          channelsCount: channelsWithPl.length || 1,
          moviesCount: moviesWithPl.length,
          seriesCount: seriesWithPl.length,
          status: 'active',
        };

        setPlaylists((prev) => [...prev, newPl]);
        showToast(`Lista "${name}" importada com sucesso! (${channelsWithPl.length || 1} canais)`, 'success');
        return true;
      } catch (err: any) {
        showToast(`Erro ao importar lista: ${err.message || err}`, 'error');
        return false;
      }
    },
    [channels.length, showToast]
  );

  const addXtreamPlaylist = useCallback(
    async (name: string, server: string, user: string, pass: string): Promise<boolean> => {
      try {
        showToast('Conectando ao servidor Xtream Codes via Multi-DNS...', 'info');

        // V9.0.2: só o servidor digitado na lista (a tabela global de DNS não entra mais)
        const candidateDNSs = [server.trim()].filter(Boolean);

        const parsed = await testAndFetchXtreamWithFallback(
          candidateDNSs.length > 0 ? candidateDNSs : [server],
          user,
          pass,
          (msg) => showToast(msg, 'info')
        );

        const activeServerUsed = parsed.usedServer || server;
        const newPlId = `pl-xtream-${Date.now()}`;
        const channelsWithPl = parsed.channels.map((c) => ({ ...c, playlistId: newPlId }));
        const moviesWithPl = parsed.movies.map((m) => ({ ...m, playlistId: newPlId }));
        const seriesWithPl = parsed.series.map((s) => ({ ...s, playlistId: newPlId }));

        if (channelsWithPl.length > 0) setChannels((prev) => [...prev, ...channelsWithPl]);
        if (moviesWithPl.length > 0) setMovies((prev) => [...prev, ...moviesWithPl]);
        if (seriesWithPl.length > 0) setSeries((prev) => [...prev, ...seriesWithPl]);

        const newPl: PlaylistSource = {
          id: newPlId,
          name,
          type: 'xtream',
          serverUrl: activeServerUsed,
          username: user,
          password: pass,
          autoUpdate: true,
          lastUpdated: new Date().toLocaleDateString('pt-BR'),
          channelsCount: channelsWithPl.length,
          moviesCount: moviesWithPl.length,
          seriesCount: seriesWithPl.length,
          status: 'active',
        };

        setPlaylists((prev) => {
          const updatedPls = [...prev, newPl];
          // Salvar Catálogo Isolado por Playlist no MySQL (HostGator)
          apiClient.saveGlobalCatalog({
            channels: channelsWithPl,
            movies: moviesWithPl,
            series: seriesWithPl,
            playlists: [newPl],
            replaceVod: true, // V8.4.6: lista nova importada inteira
          }, newPlId);
          return updatedPls;
        });

        showToast(`Conta Xtream "${name}" conectada com sucesso via ${activeServerUsed}!`, 'success');
        return true;
      } catch (err: any) {
        showToast(`Erro ao conectar Xtream: ${err.message || err}`, 'error');
        return false;
      }
    },
    [showToast]
  );

  const parsePlaylistContent = useCallback(
    async (
      name: string,
      contentOrUrl: string,
      isUrl: boolean,
      type: 'm3u' | 'xtream',
      xtreamCredentials?: { server: string; user: string; pass: string }
    ): Promise<{
      name: string;
      channels: Channel[];
      movies: Movie[];
      series: Series[];
    } | null> => {
      try {
        if (type === 'xtream' && xtreamCredentials) {
          showToast('Analisando catálogo do servidor Xtream...', 'info');
          // V9.0.2: só o servidor da própria lista. Nada fixo no código.
          const candidateServers = [xtreamCredentials.server].filter(Boolean);
          const parsed = await testAndFetchXtreamWithFallback(
            candidateServers,
            xtreamCredentials.user,
            xtreamCredentials.pass
          );
          return {
            name,
            channels: parsed.channels,
            movies: parsed.movies,
            series: parsed.series,
          };
        }

        let content = contentOrUrl;
        if (isUrl) {
          showToast('Baixando e processando lista M3U...', 'info');
          let downloaded = false;
          let lastErr = '';

          // 1. Try proxy first
          try {
            const proxyResp = await fetch(`/api/proxy?url=${encodeURIComponent(contentOrUrl)}`);
            if (proxyResp.ok) {
              content = await proxyResp.text();
              if (content && (content.includes('#EXTM3U') || content.includes('#EXTINF') || content.includes('http'))) {
                downloaded = true;
              }
            } else {
              lastErr = `HTTP ${proxyResp.status}`;
            }
          } catch (err: any) {
            lastErr = err.message || String(err);
          }

          // 2. Try direct fetch
          if (!downloaded) {
            try {
              const resp = await fetch(contentOrUrl);
              if (resp.ok) {
                content = await resp.text();
                if (content && (content.includes('#EXTM3U') || content.includes('#EXTINF') || content.includes('http'))) {
                  downloaded = true;
                }
              }
            } catch (err: any) {
              lastErr = err.message || String(err);
            }
          }

          if (!downloaded) {
            throw new Error(`Não foi possível baixar a lista M3U da URL informada (${lastErr || 'servidor inacessível'}). Verifique se o link está online.`);
          }
        }

        const parsed = await parseM3U(content);
        if (parsed.channels.length === 0 && parsed.movies.length === 0 && parsed.series.length === 0) {
          throw new Error('Nenhum canal, filme ou série válido foi encontrado dentro do arquivo M3U fornecido.');
        }

        return {
          name,
          channels: parsed.channels,
          movies: parsed.movies,
          series: parsed.series,
        };
      } catch (e: any) {
        showToast(`Falha ao ler conteúdo: ${e.message || e}`, 'error');
        return null;
      }
    },
    [showToast]
  );

  const importCustomItems = useCallback(
    (data: {
      name: string;
      type: 'm3u_url' | 'm3u_file' | 'xtream' | 'p2p';
      url?: string;
      serverUrl?: string;
      username?: string;
      password?: string;
      channels: Channel[];
      movies: Movie[];
      series: Series[];
    }) => {
      const newPlId = `pl-${Date.now()}`;
      const channelsWithPl = data.channels.map((c) => ({ ...c, playlistId: newPlId }));
      const moviesWithPl = data.movies.map((m) => ({ ...m, playlistId: newPlId }));
      const seriesWithPl = data.series.map((s) => ({ ...s, playlistId: newPlId }));

      if (channelsWithPl.length > 0) {
        setChannels((prev) => [...prev, ...channelsWithPl]);
      }
      if (moviesWithPl.length > 0) {
        setMovies((prev) => [...prev, ...moviesWithPl]);
      }
      if (seriesWithPl.length > 0) {
        setSeries((prev) => [...prev, ...seriesWithPl]);
      }

      const newPl: PlaylistSource = {
        id: newPlId,
        name: data.name,
        type: data.type,
        url: data.url,
        serverUrl: data.serverUrl,
        username: data.username,
        password: data.password,
        autoUpdate: true,
        lastUpdated: new Date().toLocaleDateString('pt-BR'),
        channelsCount: channelsWithPl.length,
        moviesCount: moviesWithPl.length,
        seriesCount: seriesWithPl.length,
        status: 'active',
      };

      setPlaylists((prev) => {
        const updatedPls = [...prev, newPl];
        apiClient.saveGlobalCatalog({
          channels: channelsWithPl,
          movies: moviesWithPl,
          series: seriesWithPl,
          playlists: [newPl],
          replaceVod: true, // V8.4.6: lista nova importada inteira
        }, newPlId);
        return updatedPls;
      });

      showToast(
        `Importação concluída: ${channelsWithPl.length} canais, ${moviesWithPl.length} filmes, ${seriesWithPl.length} séries!`,
        'success'
      );
    },
    [showToast]
  );

  const deletePlaylist = useCallback(
    (id: string) => {
      const remaining = playlists.filter((p) => p.id !== id);
      setPlaylists(remaining);
      try {
        localStorage.setItem('nexus_iptv_playlists', JSON.stringify(remaining));
      } catch (e) {
        console.warn(e);
      }

      // If no playlists remain, clear entire grid completely
      if (remaining.length === 0) {
        setUserChannels([]);
        setSystemChannels([]);
        setRawMovies([]);
        setRawSeries([]);
        setEpgData({});
        setCurrentChannelState(null);
        setCurrentMovie(null);
        setCurrentSeries(null);
        setCurrentEpisode(null);
        setIsPlaying(false);
        try {
          localStorage.setItem('nexus_iptv_channels', JSON.stringify([]));
          localStorage.setItem('nexus_iptv_system_channels', JSON.stringify([]));
          localStorage.setItem('nexus_iptv_movies', JSON.stringify([]));
          localStorage.setItem('nexus_iptv_series', JSON.stringify([]));
        } catch (e) {
          console.warn(e);
        }
        setIDBItem('catalogs', 'main_catalog', {
          userChannels: [],
          movies: [],
          series: [],
        });
        showToast('Lista e todos os seus canais foram removidos. A grade está vazia.', 'info');
        return;
      }

      // Otherwise cascade delete channels, movies and series belonging to this playlist
      setUserChannels((prev) => {
        const filtered = prev.filter((c) => c.playlistId !== id);
        try {
          saveCatalogLS('nexus_iptv_channels', filtered);
        } catch (e) {
          console.warn(e);
        }
        return filtered;
      });

      setSystemChannels((prev) => {
        const filtered = prev.filter((c) => c.playlistId !== id);
        try {
          saveCatalogLS('nexus_iptv_system_channels', filtered);
        } catch (e) {
          console.warn(e);
        }
        return filtered;
      });

      setRawMovies((prev) => {
        const filtered = prev.filter((m) => m.playlistId !== id);
        try {
          saveCatalogLS('nexus_iptv_movies', filtered);
        } catch (e) {
          console.warn(e);
        }
        return filtered;
      });

      setRawSeries((prev) => {
        const filtered = prev.filter((s) => s.playlistId !== id);
        try {
          saveCatalogLS('nexus_iptv_series', filtered);
        } catch (e) {
          console.warn(e);
        }
        return filtered;
      });

      setCurrentChannelState((prev) => (prev?.playlistId === id ? null : prev));
      setCurrentMovie((prev) => (prev?.playlistId === id ? null : prev));
      setCurrentSeries((prev) => (prev?.playlistId === id ? null : prev));
      setCurrentEpisode((prev) => (currentSeries?.playlistId === id ? null : prev));

      showToast('Lista e seus canais/conteúdos vinculados foram removidos.', 'info');
    },
    [playlists, currentSeries?.playlistId, showToast]
  );

  const clearAllPlaylistsAndGrid = useCallback(() => {
    setPlaylists([]);
    setUserChannels([]);
    setSystemChannels([]);
    setRawMovies([]);
    setRawSeries([]);
    setEpgData({});
    setCurrentChannelState(null);
    setCurrentMovie(null);
    setCurrentSeries(null);
    setCurrentEpisode(null);
    setIsPlaying(false);
    try {
      localStorage.setItem('nexus_iptv_playlists', JSON.stringify([]));
      localStorage.setItem('nexus_iptv_channels', JSON.stringify([]));
      localStorage.setItem('nexus_iptv_system_channels', JSON.stringify([]));
      localStorage.setItem('nexus_iptv_movies', JSON.stringify([]));
      localStorage.setItem('nexus_iptv_series', JSON.stringify([]));
    } catch (e) {
      console.warn(e);
    }
    setIDBItem('catalogs', 'main_catalog', {
      userChannels: [],
      movies: [],
      series: [],
    });
    showToast('Todas as listas e canais foram excluídos da grade com sucesso!', 'info');
  }, [showToast]);

  // Busca as novidades no servidor e calcula o que mudaria — SEM aplicar nada ainda. Usado pelo
  // fluxo de "Atualizar Lista" com prévia, pra o admin conferir antes de confirmar.
  const previewPlaylistUpdate = useCallback(
    async (pl: PlaylistSource): Promise<{ diff: CatalogDiff; fetchedData: { channels: Channel[]; movies: Movie[]; series: Series[] } } | null> => {
      try {
        let parsed: { channels: Channel[]; movies: Movie[]; series: Series[]; errors: string[] } | null = null;

        if (pl.type === 'xtream' && pl.username && pl.password) {
          // V9.0.2: só a DNS da própria lista (sempre 1). O cadastro da lista não é mais trocado sozinho —
          // a DNS da lista é a que o Gerenciador cadastrou.
          const candidateServers = [getServerOfList(pl as any) || pl.serverUrl || ''];
          const result = await testAndFetchXtreamWithFallback(candidateServers, pl.username, pl.password);
          parsed = result;
          if (parsed.errors && parsed.errors.length > 0) {
            parsed.errors.forEach((msg) => showToast(msg, 'warning'));
          }
        } else if (pl.type === 'm3u_url' && pl.url) {
          let content = '';
          try {
            const resp = await fetch(pl.url);
            if (resp.ok) content = await resp.text();
          } catch {
            try {
              const proxyResp = await fetch(`/api/proxy?url=${encodeURIComponent(pl.url)}`);
              if (proxyResp.ok) content = await proxyResp.text();
            } catch (e) {
              console.warn(e);
            }
          }
          if (content) {
            parsed = await parseM3U(content);
          }
        }

        if (!parsed) {
          showToast('Não foi possível buscar dados dessa lista.', 'error');
          return null;
        }

        const newChannels = parsed.channels.map((c) => ({ ...c, playlistId: pl.id }));
        const newMovies = parsed.movies.map((m) => ({ ...m, playlistId: pl.id }));
        const newSeries = parsed.series.map((s) => ({ ...s, playlistId: pl.id }));

        const currentRawAll = [...systemChannels, ...userChannels];
        const oldChannels = currentRawAll.filter((c) => c.playlistId === pl.id);
        const oldMovies = rawMovies.filter((m) => m.playlistId === pl.id);
        const oldSeries = rawSeries.filter((s) => s.playlistId === pl.id);

        const diff = computeCatalogDiff(oldChannels, newChannels, oldMovies, newMovies, oldSeries, newSeries);

        return { diff, fetchedData: { channels: newChannels, movies: newMovies, series: newSeries } };
      } catch (err: any) {
        showToast(`Erro ao buscar atualizações: ${err.message || err}`, 'error');
        return null;
      }
    },
    [systemChannels, userChannels, rawMovies, rawSeries, showToast]
  );

  // Aplica de verdade os dados já buscados por previewPlaylistUpdate (chamado só depois que o
  // admin confirma a prévia). REGRA: NÃO toca nem reorganiza os canais que o admin já tem!
  // Apenas anexa os canais, filmes e séries genuinamente novos ao catálogo existente.
  const applyPlaylistUpdate = useCallback(
    async (pl: PlaylistSource, fetchedData: { channels: Channel[]; movies: Movie[]; series: Series[] }) => {
      // 1. Canais atuais em memória (sistema + usuário)
      const currentRawAll = [...systemChannels, ...userChannels];
      const otherChannels = currentRawAll.filter((c) => c.playlistId !== pl.id);
      const myExistingChannels = currentRawAll.filter((c) => c.playlistId === pl.id);

      // Indexa canais existentes desta playlist por ID, URL e baseName
      const existingById = new Map<string, Channel>();
      const existingByUrl = new Map<string, Channel>();
      const existingByBase = new Map<string, Channel>();

      for (const c of myExistingChannels) {
        if (c.id) existingById.set(c.id, c);
        if (c.streamUrl) existingByUrl.set(c.streamUrl.trim(), c);
        if (c.backupStreams) {
          for (const u of c.backupStreams) if (u) existingByUrl.set(u.trim(), c);
        }
        if (c.qualities) {
          for (const q of c.qualities) if (q.streamUrl) existingByUrl.set(q.streamUrl.trim(), c);
        }
        const bName = extractChannelQualityInfo(c.name).baseName.toLowerCase().trim();
        if (bName) existingByBase.set(bName, c);
      }

      // Otimização O(N): indexa canais novos uma única vez em Map para busca O(1) instantânea
      const freshChannelsList = fetchedData?.channels || [];
      const freshById = new Map<string, Channel>();
      const freshByUrl = new Map<string, Channel>();
      const freshByBase = new Map<string, Channel>();
      const freshVariantsByBase = new Map<string, Channel[]>();

      for (const f of freshChannelsList) {
        if (f.id) freshById.set(f.id, f);
        if (f.streamUrl) freshByUrl.set(f.streamUrl.trim(), f);
        const fBName = extractChannelQualityInfo(f.name || '').baseName.toLowerCase().trim();
        if (fBName) {
          if (!freshByBase.has(fBName)) freshByBase.set(fBName, f);
          if (!freshVariantsByBase.has(fBName)) freshVariantsByBase.set(fBName, []);
          freshVariantsByBase.get(fBName)!.push(f);
        }
      }

      // Canais existentes: PRESERVADOS 100% (ordem, números, nomes customizados e categorias manuais).
      // Busca O(1) no mapa — executa em 1ms sem travar a interface do navegador
      const preservedChannels = myExistingChannels.map((oldCh) => {
        const bName = extractChannelQualityInfo(oldCh.name || '').baseName.toLowerCase().trim();
        const freshMatch =
          (oldCh.id && freshById.get(oldCh.id)) ||
          (oldCh.streamUrl && freshByUrl.get(oldCh.streamUrl.trim())) ||
          (bName && freshByBase.get(bName));

        // Mescla todas as qualidades encontradas no freshChannelsList para este canal
        const matchingFreshList = (bName && freshVariantsByBase.get(bName)) || [];
        const existingQualitiesMap = new Map<ChannelResolution, ChannelQualityVariant>();

        if (oldCh.qualities && Array.isArray(oldCh.qualities)) {
          for (const q of oldCh.qualities) {
            if (q && q.resolution && q.streamUrl) existingQualitiesMap.set(q.resolution, q);
          }
        }

        for (const f of matchingFreshList) {
          const { detectedResolution } = extractChannelQualityInfo(f.name || '', f.groupTitle);
          const res: ChannelResolution = detectedResolution || f.resolution || 'SD';
          if (!existingQualitiesMap.has(res) && f.streamUrl) {
            existingQualitiesMap.set(res, {
              resolution: res,
              streamUrl: f.streamUrl,
              originalName: f.name,
            });
          }
        }

        const mergedQualities = Array.from(existingQualitiesMap.values()).sort(
          (a, b) => (RESOLUTION_HIERARCHY[a.resolution] || 0) - (RESOLUTION_HIERARCHY[b.resolution] || 0)
        );

        if (!freshMatch && mergedQualities.length === 0) return oldCh;

        return {
          ...oldCh,
          streamUrl: freshMatch?.streamUrl || oldCh.streamUrl,
          backupStreams: freshMatch?.backupStreams?.length ? freshMatch.backupStreams : oldCh.backupStreams,
          epgId: freshMatch?.epgId || oldCh.epgId,
          logo: oldCh.logo && !oldCh.logo.includes('placeholder') ? oldCh.logo : (freshMatch?.logo || oldCh.logo),
          qualities: mergedQualities.length > 0 ? mergedQualities : oldCh.qualities,
        };
      });

      // 2. Canais GENUINAMENTE NOVOS: não correspondem a NENHUM canal que o usuário já tem
      const newChannelsToAdd: Channel[] = [];
      let maxNum = currentRawAll.reduce((max, c) => Math.max(max, c.num || 0), 0);

      for (const fresh of freshChannelsList) {
        const url = (fresh.streamUrl || '').trim();
        const exists =
          (fresh.id && existingById.has(fresh.id)) ||
          (url && existingByUrl.has(url));

        if (!exists) {
          maxNum++;
          newChannelsToAdd.push({
            ...fresh,
            id: fresh.id || `ch-xtream-${Date.now()}-${Math.random().toString(36).substring(2, 6)}`,
            num: maxNum,
            playlistId: pl.id,
            logo: resolveCorrectChannelLogo(fresh.name || '', fresh.logo, fresh.epgId),
          });
        }
      }

      // Monta lista de canais: tudo o que o usuário já tinha + os novos canais anexados ao final
      const finalRawChannels = [...otherChannels, ...preservedChannels, ...newChannelsToAdd];

      // Aplica channelCategoryOverrides com prioridade máxima
      let localOverrides: Record<string, string> = {};
      try {
        const stored = localStorage.getItem('nexus_iptv_channel_category_overrides');
        if (stored) localOverrides = JSON.parse(stored);
      } catch {}

      const finalChannels = finalRawChannels.map((c) => {
        const bName = extractChannelQualityInfo(c.name || '').baseName.toLowerCase().trim();
        const ov = localOverrides[c.id] || (bName ? localOverrides[`name:${bName}`] : null);
        if (ov && ov !== c.groupTitle) {
          return { ...c, groupTitle: ov };
        }
        return c;
      });

      // 3. MOVIES: Preserva existentes, anexa apenas novos
      const freshMoviesList = fetchedData?.movies || [];
      const otherMovies = (rawMovies || []).filter((m) => m.playlistId !== pl.id);
      const myExistingMovies = (rawMovies || []).filter((m) => m.playlistId === pl.id);
      const existingMoviesById = new Map<string, Movie>(myExistingMovies.map((m) => [m.id, m]));
      const existingMoviesByTitle = new Map<string, Movie>(
        myExistingMovies.map((m) => [(m.title || '').toLowerCase().trim(), m])
      );

      const newMoviesToAdd: Movie[] = [];
      const seenNewMovieTitles = new Set<string>();

      for (const fresh of freshMoviesList) {
        const cleanTitle = (fresh.title || '').toLowerCase().trim();
        const exists =
          (fresh.id && existingMoviesById.has(fresh.id)) ||
          (cleanTitle && existingMoviesByTitle.has(cleanTitle));

        if (!exists) {
          if (cleanTitle && !seenNewMovieTitles.has(cleanTitle)) {
            seenNewMovieTitles.add(cleanTitle);
            newMoviesToAdd.push({ ...fresh, playlistId: pl.id });
          }
        }
      }

      // V8.4.1 (complemento): filme que JÁ temos e mudou de endereço no provedor → troca só o endereço (mantém título, categoria,
      // capa e o id — histórico e "assistido" continuam). Antes a prévia mostrava "URL atualizada" mas aplicar não
      // mudava nada, e os mesmos filmes voltavam em toda sincronização.
      const freshMovieIdsSet = new Set<string>(freshMoviesList.map((f) => f.id).filter(Boolean) as string[]);
      const freshMoviesByIdMap = new Map<string, Movie>();
      const freshMoviesByTitleMap = new Map<string, Movie>();
      for (const f of freshMoviesList) {
        if (f.id && !freshMoviesByIdMap.has(f.id)) freshMoviesByIdMap.set(f.id, f);
        const t = (f.title || '').toLowerCase().trim();
        if (t && !freshMoviesByTitleMap.has(t)) freshMoviesByTitleMap.set(t, f);
      }
      const updatedExistingMovies = myExistingMovies.map((m) => {
        const fresh = findFreshMovieForExisting(m, freshMovieIdsSet, freshMoviesByIdMap, freshMoviesByTitleMap, existingMoviesById);
        const url = (fresh?.streamUrl || '').trim();
        if (fresh && url && url !== (m.streamUrl || '').trim()) {
          return { ...m, streamUrl: url, ...(fresh.backupStreams ? { backupStreams: fresh.backupStreams } : {}) };
        }
        return m;
      });

      const finalMovies = [...otherMovies, ...updatedExistingMovies, ...newMoviesToAdd];

      // 4. SERIES: Preserva existentes, anexa apenas novas (Otimizado O(N) com Map ao invés de 48 milhões de iterações)
      const freshSeriesList = fetchedData?.series || [];
      const freshSeriesById = new Map<string, Series>();
      const freshSeriesByTitle = new Map<string, Series>();

      for (const f of freshSeriesList) {
        if (f.id) freshSeriesById.set(f.id, f);
        const t = (f.title || '').toLowerCase().trim();
        if (t && !freshSeriesByTitle.has(t)) freshSeriesByTitle.set(t, f);
      }

      const otherSeries = (rawSeries || []).filter((s) => s.playlistId !== pl.id);
      const myExistingSeries = (rawSeries || []).filter((s) => s.playlistId === pl.id);
      const existingSeriesById = new Map<string, Series>(myExistingSeries.map((s) => [s.id, s]));
      const existingSeriesByTitle = new Map<string, Series>(
        myExistingSeries.map((s) => [(s.title || '').toLowerCase().trim(), s])
      );

      const updatedExistingSeries = myExistingSeries.map((oldS) => {
        const oldTitle = (oldS.title || '').toLowerCase().trim();
        const fresh =
          (oldS.id && freshSeriesById.get(oldS.id)) ||
          (oldTitle && freshSeriesByTitle.get(oldTitle));

        if (fresh && fresh.seasonsCount && oldS.seasonsCount && fresh.seasonsCount > oldS.seasonsCount) {
          return { ...oldS, seasonsCount: fresh.seasonsCount };
        }
        return oldS;
      });

      const newSeriesToAdd: Series[] = [];
      const seenNewSeriesTitles = new Set<string>();

      for (const fresh of freshSeriesList) {
        const cleanTitle = (fresh.title || '').toLowerCase().trim();
        const exists =
          (fresh.id && existingSeriesById.has(fresh.id)) ||
          (cleanTitle && existingSeriesByTitle.has(cleanTitle));

        if (!exists) {
          if (cleanTitle && !seenNewSeriesTitles.has(cleanTitle)) {
            seenNewSeriesTitles.add(cleanTitle);
            newSeriesToAdd.push({ ...fresh, playlistId: pl.id });
          }
        }
      }

      const finalSeries = [...otherSeries, ...updatedExistingSeries, ...newSeriesToAdd];

      // 5. Atualiza estados e armazenamento local
      setSystemChannels(finalChannels);
      setUserChannels([]);
      try {
        localStorage.setItem('nexus_iptv_system_channels', JSON.stringify(finalChannels));
        localStorage.setItem('nexus_iptv_channels', JSON.stringify([]));
      } catch {}

      setRawMovies(finalMovies);
      try {
        saveCatalogLS('nexus_iptv_movies', finalMovies);
      } catch {}

      setRawSeries(finalSeries);
      try {
        saveCatalogLS('nexus_iptv_series', finalSeries);
      } catch {}

      // 6. Registra novas categorias na configuração da sidebar (apenas na ordenação, nunca como customCategories)
      const newLiveCats = Array.from(new Set(newChannelsToAdd.map((c) => (c.groupTitle || '').trim()).filter(Boolean)));
      if (newLiveCats.length > 0) {
        setCategoryConfig((prev) => {
          const live = prev.live || { hidden: [], renames: {}, customOrder: [], customCategories: [] };
          const customOrder = live.customOrder || [];
          const orderToAdd = newLiveCats.filter((cat) => !customOrder.includes(cat));
          if (orderToAdd.length === 0) return prev;
          return {
            ...prev,
            live: {
              ...live,
              customOrder: [...customOrder, ...orderToAdd],
            },
          };
        });
      }

      // 7. Persiste catálogo consolidado na nuvem MySQL
      if (apiClient.isAuthenticated()) {
        apiClient.saveGlobalCatalog({
          channels: finalChannels,
          movies: finalMovies,
          series: finalSeries,
          playlists,
          replaceVod: true, // V8.4.6: "Aplicar Alterações" da atualização da lista (pode remover itens)
        }, playlists?.[0]?.id).catch((err) => {
          console.warn('[applyPlaylistUpdate] Falha ao salvar catálogo atualizado na nuvem:', err);
        });
      }

      showToast(`Atualização aplicada! ${newChannelsToAdd.length} novos canais, ${newMoviesToAdd.length} novos filmes, ${newSeriesToAdd.length} novas séries adicionados. Seus canais e edições existentes foram 100% preservados!`, 'success');

      return {
        channelsCount: finalChannels.length,
        moviesCount: finalMovies.length,
        seriesCount: finalSeries.length,
      };
    },
    [systemChannels, userChannels, rawMovies, rawSeries, playlists, showToast]
  );

  const refreshPlaylists = useCallback(async () => {
    showToast('Buscando atualizações de canais, filmes e séries no servidor...', 'info');
    try {
      let finalChannelsCount = channels.length;
      let finalMoviesCount = movies.length;
      let finalSeriesCount = series.length;

      if (playlists && playlists.length > 0) {
        for (const pl of playlists) {
          let parsed: { channels: Channel[]; movies: Movie[]; series: Series[]; errors: string[] } | null = null;
          if (pl.type === 'xtream' && pl.username && pl.password) {
            // V9.0.2: só a DNS da própria lista (sempre 1). O cadastro da lista não é mais trocado sozinho —
            // a DNS da lista é a que o Gerenciador cadastrou.
            const candidateServers = [getServerOfList(pl as any) || pl.serverUrl || ''];
            const result = await testAndFetchXtreamWithFallback(candidateServers, pl.username, pl.password);
            parsed = result;
            if (parsed.errors && parsed.errors.length > 0) {
              parsed.errors.forEach((msg) => showToast(msg, 'warning'));
            }
          } else if (pl.type === 'm3u_url' && pl.url) {
            let m3uContent = '';
            try {
              const resp = await fetch(pl.url);
              if (resp.ok) m3uContent = await resp.text();
            } catch {
              try {
                const proxyResp = await fetch(`/api/proxy?url=${encodeURIComponent(pl.url)}`);
                if (proxyResp.ok) m3uContent = await proxyResp.text();
              } catch (e) {
                console.warn(e);
              }
            }
            if (m3uContent) {
              parsed = await parseM3U(m3uContent);
            }
          }

          if (parsed) {
            const channelsWithPl = parsed.channels.map((c) => ({ ...c, playlistId: pl.id }));
            const moviesWithPl = parsed.movies.map((m) => ({ ...m, playlistId: pl.id }));
            const seriesWithPl = parsed.series.map((s) => ({ ...s, playlistId: pl.id }));

            const res = await applyPlaylistUpdate(pl, {
              channels: channelsWithPl,
              movies: moviesWithPl,
              series: seriesWithPl,
            });

            finalChannelsCount = res.channelsCount;
            finalMoviesCount = res.moviesCount;
            finalSeriesCount = res.seriesCount;
          }
        }
      }

      return {
        channelsCount: finalChannelsCount,
        moviesCount: finalMoviesCount,
        seriesCount: finalSeriesCount,
      };
    } catch (err: any) {
      showToast(`Erro ao atualizar lista: ${err.message || err}`, 'error');
      return null;
    }
  }, [playlists, channels, movies, series, applyPlaylistUpdate, showToast]);

  const addSystemChannel = useCallback((channel: Omit<Channel, 'id' | 'num'>) => {
    const id = `sys-ch-${Date.now()}-${Math.random().toString(36).substring(2, 7)}`;
    const num = systemChannels.length > 0 ? Math.max(...systemChannels.map(c => c.num || 0)) + 1 : 1;
    const newCh: Channel = { ...channel, id, num };
    setSystemChannels(prev => [...prev, newCh]);
    showToast(`Canal oficial do sistema "${newCh.name}" adicionado!`, 'success');
    return newCh;
  }, [systemChannels, showToast]);

  const removeSystemChannel = useCallback((id: string) => {
    setSystemChannels(prev => prev.filter(c => c.id !== id));
    showToast('Canal oficial removido.', 'info');
  }, [showToast]);

  const clearSystemChannels = useCallback(() => {
    setSystemChannels([]);
    showToast('Canais oficiais do sistema limpos.', 'info');
  }, [showToast]);

  const updateSettings = useCallback(
    (newSettings: Partial<AppSettings>) => {
      setSettings((prev) => ({ ...prev, ...newSettings }));
      showToast('Configurações salvas.', 'success');
    },
    [showToast]
  );

  const toggleReminder = useCallback(
    (program: EPGProgram) => {
      setReminders((prev) => {
        const exists = prev.includes(program.id);
        const next = exists ? prev.filter((id) => id !== program.id) : [...prev, program.id];
        showToast(
          exists ? `Lembrete cancelado: ${program.title}` : `Lembrete agendado para: ${program.title} ⏰`,
          'info'
        );
        return next;
      });
    },
    [showToast]
  );

  // Category-locked Zapping (Smart TV remote Next/Prev or Number)
  const zapChannel = useCallback(
    (direction: 'next' | 'prev' | number) => {
      // 1. If currently playing a Live Channel
      if (currentChannel) {
        const visibleChannels = channels.filter((c) => !c.hidden);
        if (visibleChannels.length === 0) {
          showToast('Nenhum canal disponível na grade.', 'warning');
          return;
        }
        if (typeof direction === 'number') {
          const found = visibleChannels.find((c) => c.num === direction) || visibleChannels[direction - 1];
          if (found) {
            setCurrentChannel(found);
            showToast(`Sintonizado: #${found.num ? found.num + ' - ' : ''}${found.name}`, 'info');
          } else {
            showToast(`Canal #${direction} não encontrado na grade.`, 'warning');
          }
          return;
        }

        // Determine candidate pool based strictly on activeChannelCategory
        let pool: Channel[] = [];
        const liveRenames = categoryConfig?.live?.renames || {};

        if (activeChannelCategory === 'Favoritos') {
          const favs = visibleChannels.filter((c) => isFavorite(c.id));
          if (favs.length > 0) {
            pool = favs;
          }
        } else if (activeChannelCategory && activeChannelCategory !== 'Todos') {
          const catChannels = visibleChannels.filter(
            (c) => c.groupTitle === activeChannelCategory || liveRenames[c.groupTitle] === activeChannelCategory
          );
          if (catChannels.length > 0) {
            pool = catChannels;
          }
        }

        // If pool is empty or activeChannelCategory is 'Todos' / null, use full visible channel list
        if (pool.length === 0) {
          pool = visibleChannels;
        }

        let currIdx = pool.findIndex(
          (c) =>
            c.id === currentChannel.id ||
            (c.num !== undefined && c.num === currentChannel.num) ||
            c.name === currentChannel.name
        );

        let nextIdx = 0;
        if (currIdx >= 0) {
          if (direction === 'next') {
            nextIdx = (currIdx + 1) % pool.length;
          } else {
            nextIdx = (currIdx - 1 + pool.length) % pool.length;
          }
        } else {
          // If current channel is not in active pool, locate in full visible channel list
          const globalIdx = visibleChannels.findIndex(
            (c) =>
              c.id === currentChannel.id ||
              (c.num !== undefined && c.num === currentChannel.num) ||
              c.name === currentChannel.name
          );
          const safeGlobalIdx = globalIdx >= 0 ? globalIdx : 0;
          pool = visibleChannels;
          if (direction === 'next') {
            nextIdx = (safeGlobalIdx + 1) % pool.length;
          } else {
            nextIdx = (safeGlobalIdx - 1 + pool.length) % pool.length;
          }
        }

        const target = pool[nextIdx];
        if (target) {
          setCurrentChannel(target);
          const catName = activeChannelCategory || target.groupTitle;
          showToast(
            `#${target.num ? target.num + ' - ' : ''}${target.name}${catName ? ` [${catName}]` : ''}`,
            'info'
          );
        }
        return;
      }

      // 2. If currently playing a VOD Movie
      if (currentMovie && typeof direction !== 'number') {
        if (movies.length === 0) return;
        let pool: Movie[] = [];
        const vodRenames = categoryConfig?.movies?.renames || {};

        if (activeMovieCategory === 'Favoritos') {
          const favs = movies.filter((m) => isFavorite(m.id));
          if (favs.length > 0) pool = favs;
        } else if (activeMovieCategory === 'Continuar Assistindo') {
          const history = movies.filter((m) => currentProfile?.watchHistory?.[m.id]);
          if (history.length > 0) pool = history;
        } else if (activeMovieCategory && activeMovieCategory !== 'Todos') {
          const catMovies = movies.filter(
            (m) => m.category === activeMovieCategory || vodRenames[m.category] === activeMovieCategory
          );
          if (catMovies.length > 0) pool = catMovies;
        }

        if (pool.length === 0) {
          pool = movies;
        }

        const currIdx = pool.findIndex((m) => m.id === currentMovie.id || m.title === currentMovie.title);
        let nextIdx = 0;
        if (currIdx >= 0) {
          if (direction === 'next') {
            nextIdx = (currIdx + 1) % pool.length;
          } else {
            nextIdx = (currIdx - 1 + pool.length) % pool.length;
          }
        } else {
          const globalIdx = movies.findIndex((m) => m.id === currentMovie.id || m.title === currentMovie.title);
          const safeGlobalIdx = globalIdx >= 0 ? globalIdx : 0;
          pool = movies;
          if (direction === 'next') {
            nextIdx = (safeGlobalIdx + 1) % pool.length;
          } else {
            nextIdx = (safeGlobalIdx - 1 + pool.length) % pool.length;
          }
        }

        const target = pool[nextIdx];
        if (target) {
          setCurrentMovieSafe(target);
          showToast(`Filme: ${target.title}`, 'info');
        }
        return;
      }

      // 3. If currently playing a Series Episode
      if (currentSeries && currentEpisode && typeof direction !== 'number') {
        const episodes = currentSeries.episodes || [];
        if (episodes.length > 0) {
          const currIdx = episodes.findIndex((ep) => ep.id === currentEpisode.id);
          const safeIdx = currIdx >= 0 ? currIdx : 0;
          let nextIdx = 0;
          if (direction === 'next') {
            nextIdx = (safeIdx + 1) % episodes.length;
          } else {
            nextIdx = (safeIdx - 1 + episodes.length) % episodes.length;
          }

          const nextEp = episodes[nextIdx];
          if (nextEp) {
            playEpisode(currentSeries, nextEp);
            showToast(
              `[${currentSeries.title}] T${nextEp.seasonNumber}E${nextEp.episodeNumber} - ${nextEp.title}`,
              'info'
            );
            return;
          }
        }
      }

      // 4. Fallback (no stream active, start first or last channel)
      if (channels.length > 0) {
        if (typeof direction === 'number') {
          const found = channels.find((c) => c.num === direction) || channels[direction - 1];
          if (found) setCurrentChannel(found);
        } else if (direction === 'next') {
          setCurrentChannel(channels[0]);
        } else {
          setCurrentChannel(channels[channels.length - 1]);
        }
      }
    },
    [
      currentChannel,
      currentMovie,
      currentSeries,
      currentEpisode,
      channels,
      movies,
      activeChannelCategory,
      activeMovieCategory,
      categoryConfig,
      currentProfile,
      isFavorite,
      setCurrentChannel,
      setCurrentMovieSafe,
      playEpisode,
      showToast,
    ]
  );

  // Keyboard remote control shortcuts (TV simulation)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Don't intercept when user is typing in an input
      if (['INPUT', 'TEXTAREA', 'SELECT'].includes((e.target as HTMLElement)?.tagName)) {
        return;
      }

      switch (e.key) {
        case 'PageUp':
          zapChannel('prev');
          break;
        case 'PageDown':
          zapChannel('next');
          break;
        case 'm':
        case 'M':
          setSettings((prev) => ({ ...prev, volumeBooster: !prev.volumeBooster }));
          break;
        case 'r':
        case 'R':
          setIsRemoteOpen((prev) => !prev);
          break;
        case 'Escape':
          stopPlayback();
          break;
        default:
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [zapChannel, stopPlayback]);

  const addSingleChannel = useCallback(
    (channelData: Partial<Channel>): Channel => {
      const nextNum = channels.length > 0 ? Math.max(...channels.map((c) => c.num || 0)) + 1 : 1;
      const channelName = channelData.name || `Canal ${nextNum}`;
      const resolvedLogo = resolveCorrectChannelLogo(
        channelName,
        channelData.logo,
        channelData.epgId
      );

      const newChannel: Channel = {
        id: channelData.id || `custom-ch-${Date.now()}-${Math.random().toString(36).substring(2, 6)}`,
        num: channelData.num || nextNum,
        name: channelName,
        logo: resolvedLogo,
        streamUrl: channelData.streamUrl || '',
        streamUrl4k: channelData.streamUrl4k,
        backupStreams: channelData.backupStreams || [],
        has4K: channelData.has4K || false,
        groupTitle: channelData.groupTitle || 'Geral',
        epgId: channelData.epgId || `custom.${Date.now()}`,
        rating: channelData.rating || 'L',
        resolution: channelData.resolution || 'HD',
        isAdult: channelData.isAdult || false,
      };

      setChannels((prev) => [...prev, newChannel]);
      showToast(`Canal "${newChannel.name}" adicionado com sucesso!`, 'success');
      return newChannel;
    },
    [channels, showToast]
  );

  const addPresetChannel = useCallback(
    (preset: Channel) => {
      setChannels((prev) => {
        const exists = prev.some((c) => c.streamUrl === preset.streamUrl || c.name === preset.name);
        if (exists) {
          showToast(`Canal "${preset.name}" já está na sua grade!`, 'info');
          return prev;
        }
        const nextNum = prev.length > 0 ? Math.max(...prev.map((c) => c.num || 0)) + 1 : 1;
        const newChan = {
          ...preset,
          id: `ch-${Date.now()}`,
          num: nextNum,
          logo: resolveCorrectChannelLogo(preset.name, preset.logo, preset.epgId),
        };
        showToast(`Canal "${preset.name}" adicionado!`, 'success');
        return [...prev, newChan];
      });
    },
    [showToast]
  );

  const deleteChannel = useCallback(
    (id: string) => {
      let targetName = '';
      const nextSystem = systemChannels.filter((c) => {
        if (c.id === id) { targetName = c.name; return false; }
        return true;
      });
      const nextUser = userChannels.filter((c) => {
        if (c.id === id) { targetName = c.name; return false; }
        return true;
      });
      setSystemChannels(nextSystem);
      setUserChannels(nextUser);
      try {
        localStorage.setItem('nexus_iptv_system_channels', JSON.stringify(nextSystem));
        localStorage.setItem('nexus_iptv_channels', JSON.stringify(nextUser));
      } catch {}

      // Limpar override
      try {
        const stored = localStorage.getItem('nexus_iptv_channel_category_overrides');
        if (stored) {
          const ov = JSON.parse(stored);
          if (ov[id]) {
            delete ov[id];
            localStorage.setItem('nexus_iptv_channel_category_overrides', JSON.stringify(ov));
            if (apiClient.isAuthenticated()) {
              apiClient.saveSyncData('global', 'channel_overrides', ov).catch(() => {});
            }
          }
        }
      } catch {}

      if (apiClient.isAuthenticated()) {
        const allMerged = [...nextSystem, ...nextUser];
        apiClient.saveGlobalCatalog({
          channels: allMerged,
          // V8.4.6: edição de canal/categoria NÃO manda filmes/séries — o servidor mantém os do banco
          playlists,
        }, playlists?.[0]?.id).catch(() => {});
      }

      showToast(`Canal "${targetName || 'selecionado'}" removido.`, 'info');
      if (currentChannel?.id === id) {
        setCurrentChannelState(null);
        setIsPlaying(false);
      }
    },
    [systemChannels, userChannels, rawMovies, rawSeries, playlists, currentChannel, showToast]
  );

  const updateChannel = useCallback(
    (id: string, updatedData: Partial<Channel>) => {
      // Identifica o canal canônico ou bruto e suas variantes de resolução
      const allCurrent = [...systemChannels, ...userChannels];
      const targetCanonical = channels.find((c) => c.id === id);
      const targetRaw = allCurrent.find((c) => c.id === id);
      const target = targetCanonical || targetRaw;

      const associatedStreamUrls = new Set<string>();
      if (target?.streamUrl) associatedStreamUrls.add(target.streamUrl);
      if (target?.qualities && Array.isArray(target.qualities)) {
        target.qualities.forEach((q) => {
          if (q.streamUrl) associatedStreamUrls.add(q.streamUrl);
        });
      }

      const targetBaseName = target ? extractChannelQualityInfo(target.name).baseName.toLowerCase() : '';
      const origCat = (target?.groupTitle || '').trim().toLowerCase();

      const isRelated = (c: Channel) => {
        if (c.id === id) return true;
        if (c.streamUrl && associatedStreamUrls.has(c.streamUrl)) return true;
        if (targetBaseName && origCat) {
          const cBaseName = extractChannelQualityInfo(c.name).baseName.toLowerCase();
          const cCat = (c.groupTitle || '').trim().toLowerCase();
          if (cBaseName === targetBaseName && cCat === origCat) return true;
        }
        return false;
      };

      const nextGroup = updatedData.groupTitle ? updatedData.groupTitle.trim() : undefined;

      // 1. Atualiza userChannels e systemChannels sincronicamente
      const nextUserChannels = userChannels.map((c) => {
        if (c.id === id) {
          const merged = { ...c, ...updatedData };
          if (nextGroup) merged.groupTitle = nextGroup;
          merged.logo = resolveCorrectChannelLogo(merged.name, merged.logo, merged.epgId);
          return merged;
        }
        if (isRelated(c)) {
          const partialUpdate: Partial<Channel> = {};
          if (nextGroup) partialUpdate.groupTitle = nextGroup;
          if (updatedData.num !== undefined) partialUpdate.num = updatedData.num;
          if (updatedData.rating) partialUpdate.rating = updatedData.rating;
          if (updatedData.isAdult !== undefined) partialUpdate.isAdult = updatedData.isAdult;
          return { ...c, ...partialUpdate };
        }
        return c;
      });

      const nextSystemChannels = systemChannels.map((c) => {
        if (c.id === id) {
          const merged = { ...c, ...updatedData };
          if (nextGroup) merged.groupTitle = nextGroup;
          merged.logo = resolveCorrectChannelLogo(merged.name, merged.logo, merged.epgId);
          return merged;
        }
        if (isRelated(c)) {
          const partialUpdate: Partial<Channel> = {};
          if (nextGroup) partialUpdate.groupTitle = nextGroup;
          if (updatedData.num !== undefined) partialUpdate.num = updatedData.num;
          if (updatedData.rating) partialUpdate.rating = updatedData.rating;
          if (updatedData.isAdult !== undefined) partialUpdate.isAdult = updatedData.isAdult;
          return { ...c, ...partialUpdate };
        }
        return c;
      });

      const allMerged = [...nextSystemChannels, ...nextUserChannels];

      setUserChannels(nextUserChannels);
      setSystemChannels(nextSystemChannels);
      try {
        localStorage.setItem('nexus_iptv_channels', JSON.stringify(nextUserChannels));
        localStorage.setItem('nexus_iptv_system_channels', JSON.stringify(nextSystemChannels));
      } catch {}

      // 2. Registra nova categoria em categoryConfig caso não exista
      if (nextGroup && nextGroup !== 'Favoritos' && nextGroup !== 'Geral') {
        setCategoryConfig((prev) => {
          const live = prev.live || { hidden: [], renames: {}, customOrder: [], customCategories: [] };
          const customCategories = live.customCategories || [];
          const customOrder = live.customOrder || [];
          let changed = false;

          let newCustomCategories = [...customCategories];
          const isServerCategory = customOrder.includes(nextGroup) || DEFAULT_CANONICAL_LIVE_CATEGORIES.includes(nextGroup);
          if (!isServerCategory && !customCategories.includes(nextGroup)) {
            newCustomCategories.push(nextGroup);
            changed = true;
          }

          let newCustomOrder = [...customOrder];
          if (!customOrder.includes(nextGroup)) {
            newCustomOrder.push(nextGroup);
            changed = true;
          }

          if (!changed) return prev;

          const updatedCfg = {
            ...prev,
            live: {
              ...live,
              customCategories: newCustomCategories,
              customOrder: newCustomOrder,
            },
          };
          try {
            localStorage.setItem('nexus_iptv_category_config', JSON.stringify(updatedCfg));
          } catch {}
          if (apiClient.isAuthenticated()) {
            apiClient.saveSyncData('global', 'categories', updatedCfg).catch(() => {});
            apiClient.saveSyncData('prof_master', 'categories', updatedCfg).catch(() => {});
          }
          return updatedCfg;
        });

        // Grava override persistente para nunca reverter em sincronizações
        let currentOverrides: Record<string, string> = {};
        try {
          const stored = localStorage.getItem('nexus_iptv_channel_category_overrides');
          if (stored) currentOverrides = JSON.parse(stored);
        } catch {}

        allMerged.forEach((c) => {
          if (isRelated(c)) {
            currentOverrides[c.id] = nextGroup;
            const bName = extractChannelQualityInfo(c.name).baseName.toLowerCase().trim();
            if (bName) currentOverrides[`name:${bName}`] = nextGroup;
          }
        });

        try {
          localStorage.setItem('nexus_iptv_channel_category_overrides', JSON.stringify(currentOverrides));
        } catch {}

        if (apiClient.isAuthenticated()) {
          apiClient.saveSyncData('global', 'channel_overrides', currentOverrides).catch(() => {});
          apiClient.saveSyncData('prof_master', 'channel_overrides', currentOverrides).catch(() => {});
        }
      }

      // 3. Persiste no MySQL (HostGator)
      if (apiClient.isAuthenticated()) {
        const targetPlId = playlists?.[0]?.id || '';
        apiClient.saveGlobalCatalog({
          channels: allMerged,
          // V8.4.6: edição de canal/categoria NÃO manda filmes/séries — o servidor mantém os do banco
          playlists,
        }, targetPlId).catch((err) => {
          console.warn('[Sync] Falha ao persistir canal no MySQL:', err);
        });
      }

      showToast(`Canal atualizado com sucesso!`, 'success');
    },
    [channels, systemChannels, userChannels, rawMovies, rawSeries, playlists, showToast]
  );

  const moveChannelsCategory = useCallback(
    async (channelIds: string[], targetCategory: string): Promise<boolean> => {
      const trimmedCategory = targetCategory.trim();
      if (!trimmedCategory || channelIds.length === 0) return false;

      const idSet = new Set(channelIds);
      const allCurrent = [...systemChannels, ...userChannels];
      
      const targetBaseNames = new Set<string>();
      const associatedStreamUrls = new Set<string>();

      channels.forEach((ch) => {
        if (idSet.has(ch.id)) {
          if (ch.streamUrl) associatedStreamUrls.add(ch.streamUrl);
          if (ch.qualities && Array.isArray(ch.qualities)) {
            ch.qualities.forEach((q) => {
              if (q.streamUrl) associatedStreamUrls.add(q.streamUrl);
            });
          }
          const base = extractChannelQualityInfo(ch.name).baseName.toLowerCase();
          if (base) targetBaseNames.add(base);
        }
      });

      allCurrent.forEach((ch) => {
        if (idSet.has(ch.id)) {
          if (ch.streamUrl) associatedStreamUrls.add(ch.streamUrl);
          const base = extractChannelQualityInfo(ch.name).baseName.toLowerCase();
          if (base) targetBaseNames.add(base);
        }
      });

      const isRelated = (c: Channel) => {
        if (idSet.has(c.id)) return true;
        if (c.streamUrl && associatedStreamUrls.has(c.streamUrl)) return true;
        const cBase = extractChannelQualityInfo(c.name).baseName.toLowerCase();
        if (targetBaseNames.has(cBase)) return true;
        return false;
      };

      const nextUserChannels = userChannels.map((c) => (isRelated(c) ? { ...c, groupTitle: trimmedCategory } : c));
      const nextSystemChannels = systemChannels.map((c) => (isRelated(c) ? { ...c, groupTitle: trimmedCategory } : c));
      const allMerged = [...nextSystemChannels, ...nextUserChannels];

      setUserChannels(nextUserChannels);
      setSystemChannels(nextSystemChannels);
      try {
        localStorage.setItem('nexus_iptv_channels', JSON.stringify(nextUserChannels));
        localStorage.setItem('nexus_iptv_system_channels', JSON.stringify(nextSystemChannels));
      } catch {}

      // Grava overrides persistentes contra reversão
      let currentOverrides: Record<string, string> = {};
      try {
        const stored = localStorage.getItem('nexus_iptv_channel_category_overrides');
        if (stored) currentOverrides = JSON.parse(stored);
      } catch {}

      allMerged.forEach((c) => {
        if (isRelated(c)) {
          currentOverrides[c.id] = trimmedCategory;
          const bName = extractChannelQualityInfo(c.name).baseName.toLowerCase().trim();
          if (bName) currentOverrides[`name:${bName}`] = trimmedCategory;
        }
      });

      try {
        localStorage.setItem('nexus_iptv_channel_category_overrides', JSON.stringify(currentOverrides));
      } catch {}

      if (apiClient.isAuthenticated()) {
        apiClient.saveSyncData('global', 'channel_overrides', currentOverrides).catch(() => {});
        apiClient.saveSyncData('prof_master', 'channel_overrides', currentOverrides).catch(() => {});
      }

      if (trimmedCategory !== 'Favoritos' && trimmedCategory !== 'Geral') {
        setCategoryConfig((prev) => {
          const live = prev.live || { hidden: [], renames: {}, customOrder: [], customCategories: [] };
          const customCategories = live.customCategories || [];
          const customOrder = live.customOrder || [];
          let changed = false;

          let newCustomCategories = [...customCategories];
          const isServerCategory = customOrder.includes(trimmedCategory) || DEFAULT_CANONICAL_LIVE_CATEGORIES.includes(trimmedCategory);
          if (!isServerCategory && !customCategories.includes(trimmedCategory)) {
            newCustomCategories.push(trimmedCategory);
            changed = true;
          }

          let newCustomOrder = [...customOrder];
          if (!customOrder.includes(trimmedCategory)) {
            newCustomOrder.push(trimmedCategory);
            changed = true;
          }

          if (!changed) return prev;

          const updatedCfg = {
            ...prev,
            live: {
              ...live,
              customCategories: newCustomCategories,
              customOrder: newCustomOrder,
            },
          };
          try {
            localStorage.setItem('nexus_iptv_category_config', JSON.stringify(updatedCfg));
          } catch {}
          if (apiClient.isAuthenticated()) {
            apiClient.saveSyncData('global', 'categories', updatedCfg).catch(() => {});
            apiClient.saveSyncData('prof_master', 'categories', updatedCfg).catch(() => {});
          }
          return updatedCfg;
        });
      }

      if (apiClient.isAuthenticated()) {
        try {
          const activePlId = playlists?.[0]?.id || '';
          await apiClient.saveGlobalCatalog({
            channels: allMerged,
            // V8.4.6: edição de canal/categoria NÃO manda filmes/séries — o servidor mantém os do banco
            playlists,
          }, activePlId);
        } catch (err) {
          console.warn('[Sync] Falha ao persistir no MySQL:', err);
        }
      }

      showToast(`${channelIds.length} canal(is) movido(s) para "${trimmedCategory}"!`, 'success');
      return true;
    },
    [channels, systemChannels, userChannels, rawMovies, rawSeries, playlists, showToast]
  );

  const toggleHideChannel = useCallback(
    (id: string) => {
      let nextHidden = false;
      let channelName = '';

      setChannels((prev) =>
        prev.map((c) => {
          if (c.id === id || String(c.num) === id) {
            nextHidden = !c.hidden;
            channelName = c.name;
            return { ...c, hidden: nextHidden };
          }
          return c;
        })
      );

      setCategoryConfig((prev) => {
        const liveSection = prev.live;
        const currentHidden = liveSection.hiddenChannels || [];
        const isAlreadyHidden = currentHidden.includes(id);
        const updated = isAlreadyHidden
          ? currentHidden.filter((hId) => hId !== id)
          : [...currentHidden, id];
        return {
          ...prev,
          live: {
            ...liveSection,
            hiddenChannels: updated,
          },
        };
      });

      if (channelName) {
        showToast(`Canal "${channelName}" ${nextHidden ? 'ocultado' : 'exibido'}.`, 'info');
      }
    },
    [showToast]
  );

  const clearAllChannels = useCallback(() => {
    setChannels([]);
    setCurrentChannelState(null);
    setIsPlaying(false);
    showToast('Todos os canais foram removidos.', 'info');
  }, [showToast]);

  // Movie VOD Handlers
  const addSingleMovie = useCallback(
    (movieData: Partial<Movie>): Movie => {
      const newMovie: Movie = {
        id: movieData.id || `custom-mov-${Date.now()}-${Math.random().toString(36).substring(2, 6)}`,
        title: movieData.title || 'Filme Sem Título',
        poster:
          movieData.poster ||
          'https://images.unsplash.com/photo-1536440136628-849c177e76a1?w=500&auto=format&fit=crop&q=80',
        backdrop:
          movieData.backdrop ||
          'https://images.unsplash.com/photo-1509198397868-475647b2a1e5?w=1200&auto=format&fit=crop&q=80',
        streamUrl: movieData.streamUrl || '',
        category: movieData.category || 'Geral',
        year: movieData.year || new Date().getFullYear(),
        duration: movieData.duration || 110,
        rating: movieData.rating || '12',
        score: movieData.score || 8.0,
        synopsis: movieData.synopsis || 'Sinopse não informada.',
        cast: movieData.cast || ['Elenco Principal'],
        director: movieData.director || 'Diretor',
        quality: movieData.quality || '1080p',
        isAdult: movieData.isAdult || false,
      };

      setMovies((prev) => [...prev, newMovie]);
      showToast(`Filme "${newMovie.title}" adicionado ao Catálogo VOD!`, 'success');
      return newMovie;
    },
    [showToast]
  );

  const addPresetMovie = useCallback(
    (preset: Movie) => {
      setMovies((prev) => {
        const exists = prev.some((m) => m.streamUrl === preset.streamUrl || m.title === preset.title);
        if (exists) {
          showToast(`Filme "${preset.title}" já está no catálogo de filmes!`, 'info');
          return prev;
        }
        const newMov = { ...preset, id: `mov-${Date.now()}` };
        showToast(`Filme "${preset.title}" adicionado aos Filmes!`, 'success');
        return [...prev, newMov];
      });
    },
    [showToast]
  );

  const deleteMovie = useCallback(
    (id: string) => {
      setMovies((prev) => {
        const target = prev.find((m) => m.id === id);
        const filtered = prev.filter((m) => m.id !== id);
        showToast(`Filme "${target?.title || 'selecionado'}" removido.`, 'info');
        return filtered;
      });
      if (currentMovie?.id === id) {
        setCurrentMovie(null);
        setIsPlaying(false);
      }
    },
    [currentMovie, showToast]
  );

  const clearAllMovies = useCallback(() => {
    setMovies([]);
    setCurrentMovie(null);
    setIsPlaying(false);
    showToast('Todos os filmes foram removidos do catálogo.', 'info');
  }, [showToast]);

  // Series VOD Handlers
  const addSingleSeries = useCallback(
    (seriesData: Partial<Series>): Series => {
      const newSer: Series = {
        id: seriesData.id || `custom-ser-${Date.now()}-${Math.random().toString(36).substring(2, 6)}`,
        title: seriesData.title || 'Série Sem Título',
        poster:
          seriesData.poster ||
          'https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=500&auto=format&fit=crop&q=80',
        backdrop:
          seriesData.backdrop ||
          'https://images.unsplash.com/photo-1446776811953-b23d57bd21aa?w=1200&auto=format&fit=crop&q=80',
        category: seriesData.category || 'Geral',
        year: seriesData.year || new Date().getFullYear(),
        seasonsCount: seriesData.seasonsCount || 1,
        rating: seriesData.rating || '14',
        score: seriesData.score || 8.5,
        synopsis: seriesData.synopsis || 'Sinopse não informada.',
        cast: seriesData.cast || ['Elenco Principal'],
        isAdult: seriesData.isAdult || false,
        episodes: seriesData.episodes || [
          {
            id: `ep-${Date.now()}-1`,
            seasonNumber: 1,
            episodeNumber: 1,
            title: 'Episódio 1',
            synopsis: 'Primeiro episódio',
            duration: 45,
            thumbnail:
              seriesData.poster ||
              'https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=400&auto=format&fit=crop&q=80',
            streamUrl: '',
          },
        ],
      };

      setSeries((prev) => [...prev, newSer]);
      showToast(`Série "${newSer.title}" adicionada ao Catálogo VOD!`, 'success');
      return newSer;
    },
    [showToast]
  );

  const addPresetSeries = useCallback(
    (preset: Series) => {
      setSeries((prev) => {
        const exists = prev.some((s) => s.title === preset.title);
        if (exists) {
          showToast(`Série "${preset.title}" já está no catálogo!`, 'info');
          return prev;
        }
        const newSer = { ...preset, id: `ser-${Date.now()}` };
        showToast(`Série "${preset.title}" adicionada!`, 'success');
        return [...prev, newSer];
      });
    },
    [showToast]
  );

  const deleteSeries = useCallback(
    (id: string) => {
      setSeries((prev) => {
        const target = prev.find((s) => s.id === id);
        const filtered = prev.filter((s) => s.id !== id);
        showToast(`Série "${target?.title || 'selecionada'}" removida.`, 'info');
        return filtered;
      });
      if (currentSeries?.id === id) {
        setCurrentSeries(null);
        setCurrentEpisode(null);
        setIsPlaying(false);
      }
    },
    [currentSeries, showToast]
  );

  const clearAllSeries = useCallback(() => {
    setSeries([]);
    setCurrentSeries(null);
    setCurrentEpisode(null);
    setIsPlaying(false);
    showToast('Todas as séries foram removidas.', 'info');
  }, [showToast]);

  const resetDefaultChannels = useCallback(() => {
    setPlaylists([]);
    setChannels([]);
    setSystemChannels([]);
    setMovies([]);
    setSeries([]);
    setEpgData({});
    localStorage.setItem('nexus_iptv_playlists', JSON.stringify([]));
    localStorage.setItem('nexus_iptv_channels', JSON.stringify([]));
    localStorage.setItem('nexus_iptv_system_channels', JSON.stringify([]));
    localStorage.setItem('nexus_iptv_movies', JSON.stringify([]));
    localStorage.setItem('nexus_iptv_series', JSON.stringify([]));
    localStorage.setItem('nexus_iptv_version', IPTV_DATA_VERSION);
    setCurrentChannelState(null);
    setCurrentMovie(null);
    setCurrentSeries(null);
    setCurrentEpisode(null);
    showToast('Grade e listas limpas com sucesso!', 'success');
  }, [showToast]);

  return (
    <IPTVContext.Provider
      value={{
        activeTab,
        setActiveTab,
        searchQuery,
        setSearchQuery,
        activeChannelCategory,
        setActiveChannelCategory,
        activeMovieCategory,
        setActiveMovieCategory,
        activeSeriesCategory,
        setActiveSeriesCategory,
        activeSeriesDetail,
        setActiveSeriesDetail,
        categoryConfig,
        toggleHideCategory,
        renameCategory,
        reorderCategory,
        setCategoryPosition,
        moveCategoryToTop,
        moveCategoryToBottom,
        createCustomCategory,
        deleteCustomCategory,
        resetCategoryConfig,
        setCategoryConfig,
        moveItemCategory,
        bulkSetCategoryVisibility,
        saveCategoryGridToCloud,
        currentChannel,
        setCurrentChannel,
        playChannel: setCurrentChannel,
        switchChannelQuality,
        currentMovie,
        setCurrentMovie: setCurrentMovieSafe,
        playMovie: setCurrentMovieSafe,
        currentSeries,
        currentEpisode,
        playEpisode,
        playSeriesEpisode: playEpisode,
        initialSeekSeconds,
        setInitialSeekSeconds,
        getContentProgress,
        getSeriesProgress,
        clearContentProgress,
        isContentWatched,
        toggleWatched,
        stopPlayback,
        isPlaying,
        setIsPlaying,
        channels,
        rawChannels,
        systemChannels,
        toggleMergeChannel,
        setMergeQualitiesGlobal,
        excludeQualityFromMerge,
        restoreQualityToMerge,
        excludeChannelFromMerge,
        restoreChannelToMerge,
        deleteChannelMerge,
        restoreDeletedMerge,
        mergeSelectedChannelsManually,
        addSystemChannel,
        removeSystemChannel,
        clearSystemChannels,
        movies,
        series,
        loadSeriesEpisodes,
        epgData,
        loadChannelEPG,
        loadBatchEPG,
        isEpgLoading,
        channelCategories,
        movieCategories,
        seriesCategories,
        rawChannelCategories,
        rawMovieCategories,
        rawSeriesCategories,
        profiles,
        currentProfile,
        currentProfileId,
        isProfileKicked,
        selectProfile,
        createProfile,
        updateProfile,
        deleteProfile,
        toggleFavorite,
        isFavorite,
        toggleCategoryFavorite,
        isCategoryFavorite,
        updateWatchHistory,
        clearAllWatchHistory,
        continueWatchingItems,
        parentalConfig,
        setParentalConfig,
        isParentalUnlocked,
        unlockParentalSession,
        lockParentalSession,
        pinModalOpen,
        openPinModal,
        closePinModal,
        handlePinSuccess,
        pinModalTitle,
        checkRestricted,
        playlists,
        addM3UPlaylist,
        addXtreamPlaylist,
        parsePlaylistContent,
        importCustomItems,
        deletePlaylist,
        clearAllPlaylistsAndGrid,
        refreshPlaylists,
        previewPlaylistUpdate,
        applyPlaylistUpdate,
        settings,
        updateSettings,
        reminders,
        toggleReminder,
        isRemoteOpen,
        setIsRemoteOpen,
        toasts,
        showToast,
        removeToast,
        zapChannel,
        resetDefaultChannels,
        addSingleChannel,
        addPresetChannel,
        deleteChannel,
        updateChannel,
        moveChannelsCategory,
        toggleHideChannel,
        clearAllChannels,
        addSingleMovie,
        addPresetMovie,
        deleteMovie,
        clearAllMovies,
        addSingleSeries,
        addPresetSeries,
        deleteSeries,
        clearAllSeries,
        isPlayerControlsVisible,
        setIsPlayerControlsVisible,
        hidePlayerControlsNow,
        clearPlayerControlsTimer,
        setControlsAutoHideSuppressed,
        pokePlayerControls,
        authUser,
        isAdmin,
        loginUser,
        registerUser,
        logoutUser,
        logout: logoutUser,
        login: loginUser,
        isAuthModalOpen,
        setIsAuthModalOpen,
        isDevicesModalOpen,
        setIsDevicesModalOpen,
        openDevicesModal,
        closeDevicesModal,
        deviceId,
        currentStreamRoute,
        setCurrentStreamRoute,
        screenLimitBlocked,
        screenLimitMessage,
        screenLimitScreens,
        screenLimitMaxScreens,
        disconnectScreen,
        resumePendingPlayback,
        closeScreenLimitModal,
        screenKickedNotice,
        dismissScreenKickedNotice,
        cloudSyncStatus,
        lastCloudSyncTime,
        forceCloudSync,
        isCloudLoaded,
        isCatalogFromCloud,
        catalogLoadFailed,
        systemDNSList,
        updateSystemDNSList,
        loadSystemDNS,
        getStreamServer,
        getStreamAccount,
        channelProfiles,
        getChannelStreamProfile,
        saveChannelStreamProfile,
        refreshLightweightSyncData,
        isCasting,
        castDeviceName,
        castSessionId,
        castTvCode,
        setCastState,
        profileOnTv,
        watchHereAndCloseTv,
        sendPendingToTv,
        cancelProfileOnTv,
        castSendRequest,
        clearCastSendRequest,
      }}
    >
      {children}
    </IPTVContext.Provider>
  );
};

export const useIPTV = () => {
  const context = useContext(IPTVContext);
  if (!context) throw new Error('useIPTV must be used within an IPTVProvider');
  return context;
};
